import React, {
  useState, useEffect, useCallback, useMemo,
  useRef, useLayoutEffect,
} from 'react';
import ReactDOM from 'react-dom';
// AFTER  — added AlertTriangle
import {
  Plus, Trash2, FileText, X, Search, Loader2,
  Printer, Building2, ChevronDown, Check,
  AlertCircle, AlertTriangle, RefreshCw, ChevronsLeft, ChevronsRight,
  ChevronLeft, ChevronRight, ChevronUp, Edit2,
  Filter, List, User, ArrowRight, ArrowLeft,
  FileCheck, Settings, Users, Hash, Calendar,
  Eye, Download, LayoutGrid, CheckSquare, Square,
  CheckCircle2, Link2, MapPin, Info,
} from 'lucide-react';
import { apiService } from '../api/api';
import { useResizableColumns } from '../hooks/useResizableColumns';
import AddExternalHoardingModal from '../components/AddExternalHoardingModal';
import "./Common1.css";
import paragSign from '../Assets/paragSign.png';

/* ═══════════════════════════════════════════
   CONSTANTS
═══════════════════════════════════════════ */
const COMPANY = {
  name: 'JALARAM AD',
  line1: '103/4/5/6, Drashti Arcade, Opp. Anand ITI',
  line2: 'Nr. Grid Crossing, Anand - 388001, GUJ. INDIA. Parag Patel # 9428151123',
  gstin: '24AAMFJ0339H2ZG',
  pan: 'AAMFJ0339H',
  bank: 'AXIS BANK',
  branch: 'GRID CHOKDI, ANAND',
  account: '920020035728954',
  ifsc: 'UTIB0003220',
  signatory: '',
};

const ROWS_PER_PRINT_PAGE = 13;
const PAGE_SIZE_OPTIONS = [5, 10, 15, 20];
function parseOccupancyError(err) {
  const status = err?.response?.status;
  // Accept any 4xx error (400, 409 Conflict, 422 Unprocessable, etc.)
  if (!status || status < 400 || status >= 500) return null;

  const raw =
    err?.response?.data?.message ||
    err?.response?.data?.title ||
    err?.response?.data?.errors ||
    err?.response?.data ||
    err?.message ||
    '';
  const str = typeof raw === 'string' ? raw : JSON.stringify(raw);
  const lower = str.toLowerCase();

  if (
    lower.includes('occupied') ||
    lower.includes('already book') ||
    lower.includes('conflict') ||
    lower.includes('overlaps')
  ) {
    return str.trim();
  }
  return null;
}

// ── component ── (needs AlertTriangle, X from lucide — already imported)
function OccupancyWarningBanner({ messages, onDismiss }) {
  if (!messages || messages.length === 0) return null;
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '13px 16px', borderRadius: 12, marginBottom: 14,
      background: '#fffbeb', border: '1.5px solid #fbbf24',
      boxShadow: '0 2px 10px rgba(251,191,36,0.15)',
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10, flexShrink: 0,
        background: 'rgba(251,191,36,0.15)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <AlertTriangle size={18} color="#d97706" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 900, color: '#92400e', marginBottom: messages.length > 1 ? 6 : 2 }}>
          {messages.length === 1 ? 'Hoarding Already Occupied' : `${messages.length} Hoardings Already Occupied`}
        </div>
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, fontFamily: 'Nunito,sans-serif', fontSize: 12.5, fontWeight: 700, color: '#b45309', lineHeight: 1.5, marginTop: i > 0 ? 4 : 0 }}>
            {messages.length > 1 && <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#d97706', marginTop: 7, flexShrink: 0 }} />}
            {msg}
          </div>
        ))}
        <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, fontWeight: 600, color: '#a16207', marginTop: 6 }}>
          Please choose a different date range or remove these hoardings.
        </div>
      </div>
      <button onClick={onDismiss} style={{ width: 26, height: 26, borderRadius: 7, flexShrink: 0, background: 'rgba(251,191,36,0.18)', border: '1px solid rgba(251,191,36,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#d97706' }}>
        <X size={13} />
      </button>
    </div>
  );
}
const STEPS = [
  { n: 1, label: 'Customer & Type', Icon: Users },
  { n: 2, label: 'Add Hoardings', Icon: Building2 },
  { n: 3, label: 'GST & Generate', Icon: FileCheck },
];

const SITE_PASTEL_PALETTE = [
  { bg: '#FFF0EE', border: '#FFBCB3', dot: '#FF6B55' },
  { bg: '#EEF4FF', border: '#B3CCFF', dot: '#3B7FFF' },
  { bg: '#EDFFF2', border: '#AAFFBB', dot: '#1FBF4A' },
  { bg: '#FFFBEE', border: '#FFE5AA', dot: '#F5A623' },
  { bg: '#F7EEFF', border: '#D9AAFF', dot: '#9B3FFF' },
  { bg: '#EEFFFA', border: '#AAFFD6', dot: '#0DC88E' },
  { bg: '#FFFEEE', border: '#FFEFAA', dot: '#D4B800' },
  { bg: '#FFECFD', border: '#FFAAEE', dot: '#E020CC' },
  { bg: '#EEF9FF', border: '#AADEEF', dot: '#009DBF' },
  { bg: '#F2FFEE', border: '#C3FFAA', dot: '#46A800' },
];

const PAYMENT_FREQ_FALLBACK = [
  { value: 1, label: 'Monthly' },
  { value: 2, label: 'Quarterly' },
  { value: 3, label: 'Half-Yearly' },
  { value: 4, label: 'Yearly' },
];
const PRINTING_TYPES = [
  'Flex Banner Printing',
  'Black Back Printing',
  'Star Black Back Printing',
  'Backlit Printing',
  'Retro Flex Printing',
];
/* ═══════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════ */
const uid = () => Math.random().toString(36).substr(2, 9);
const todayISO = () => {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const fmtCurrency = (n) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDateFull = (d) => {
  if (!d) return '';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const fmtDateShort = (d) => {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return `${String(dt.getDate()).padStart(2, '0')}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getFullYear()).slice(-2)}`;
};

const fmtDateDisplay = (d) => {
  if (!d) return '—';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const addMonths = (dateStr, months) => {
  if (!dateStr) return '';
  const d = new Date(dateStr + 'T00:00:00');
  d.setMonth(d.getMonth() + Math.max(0, Number(months) - 1));
  d.setMonth(d.getMonth() + 1, 0);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};
function calcAmountByDates(startDate, endDate, monthlyRent) {
  if (!startDate || !endDate || !monthlyRent) return Number(monthlyRent) || 0;
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  if (end < start) return Number(monthlyRent) || 0;

  const startDay = start.getDate();
  const endDay = end.getDate();
  const endLastDay = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();

  if (startDay === 1 && endDay === endLastDay) {
    // Full calendar months
    const months =
      (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth()) + 1;
    return Math.round(months * Number(monthlyRent) * 100) / 100;
  }

  // Partial span — daily rate
  const days = Math.round((end - start) / 86400000) + 1; // inclusive
  const dailyRate = Number(monthlyRent) / 30;
  return Math.round(days * dailyRate * 100) / 100;
}

/**
 * calcNOSFromDates
 * Returns whole months for full-month spans, decimal months otherwise.
 */
// ✅ MODIFIED: Round to exactly 12 months for 365/366 day cycles (and multiples of 12 for multi-year)
function calcNOSFromDates(startDate, endDate) {
  if (!startDate || !endDate) return 1;
  const start = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  if (end < start) return 1;

  const startDay = start.getDate();
  const endDay = end.getDate();
  const endLastDay = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();

  if (startDay === 1 && endDay === endLastDay) {
    return (end.getFullYear() - start.getFullYear()) * 12 +
      (end.getMonth() - start.getMonth()) + 1;
  }

  const days = Math.round((end - start) / 86400000) + 1;

  // Check if days corresponds to a full year count (e.g. 365 or 366 days)
  for (let years = 1; years <= 10; years++) {
    const minDays = years * 365;
    const maxDays = years * 365 + Math.floor(years / 4) + 1;
    if (days >= minDays - 1 && days <= maxDays) {
      return years * 12;
    }
  }

  return Math.round((days / 30) * 100) / 100;
}
function calculateEndDate(startDateStr, days) {
  if (!startDateStr || !days) return '';
  const d = new Date(startDateStr + 'T00:00:00');
  d.setDate(d.getDate() + Number(days) - 1);
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function calculateDays(startDateStr, endDateStr) {
  if (!startDateStr || !endDateStr) return 0;
  const start = new Date(startDateStr + 'T00:00:00');
  const end = new Date(endDateStr + 'T00:00:00');
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // inclusive
  return diffDays;
}

// ✅ MODIFIED: Round to exactly 12 months for 365/366 day cycles (and multiples of 12 for multi-year)
function calcNOSFromDays(days) {
  const d = Number(days);
  if (isNaN(d) || d <= 0) return 0;
  if (d >= 28 && d <= 31) return 1;

  // If it's a multiple of a full year (approx 365 or 366 days per year)
  for (let years = 1; years <= 10; years++) {
    const minDays = years * 365;
    const maxDays = years * 365 + Math.floor(years / 4) + 1; // account for leap years
    if (d >= minDays - 1 && d <= maxDays) {
      return years * 12;
    }
  }

  return Math.round((d / 30) * 100) / 100;
}
function parsePurposeMeta(purpose, defaultRate = 0) {
  const parts = (purpose || '').split('|');
  const location = parts[0] || '';
  const meta = parts[1] || '';
  let ratePerMonth = defaultRate;
  let printingCost = 0;
  let printType = '';
  let printRate = 0;
  if (meta) {
    const matchRate = meta.match(/rate:(\d+(\.\d+)?)/);
    const matchPrint = meta.match(/print:(\d+(\.\d+)?)/);
    const matchPrintType = meta.match(/printType:([^,]*)/);
    const matchPrintRate = meta.match(/printRate:(\d+(\.\d+)?)/);
    if (matchRate) ratePerMonth = Number(matchRate[1]);
    if (matchPrint) printingCost = Number(matchPrint[1]);
    if (matchPrintType) printType = matchPrintType[1] || '';
    if (matchPrintRate) printRate = Number(matchPrintRate[1]);
  }
  return { location, ratePerMonth, printingCost, printType, printRate };
}
function checkHoardingDateConflicts(rows, allContracts, allContractMaps, customers) {
  const conflicts = [];
  const seen = new Set();

  for (const row of rows) {
    if (row.rowType === 'merged' || row.rowType === 'printing') continue;
    if (!row.hoardingID || !row.startDate || !row.endDate) continue;

    const rowStart = new Date(row.startDate);
    const rowEnd = new Date(row.endDate);

    const mappings = allContractMaps.filter(m =>
      Number(m.hoardingID ?? m.HoardingID) === Number(row.hoardingID)
    );

    for (const mapping of mappings) {
      const contractID = Number(
        mapping.customerContractID ?? mapping.CustomerContractID ?? 0
      );
      if (!contractID) continue;

      const contract = allContracts.find(c =>
        Number(c.customerContractID) === contractID
      );
      if (!contract) continue;
      if (contract.status === 'Expired' || contract.status === 'Terminated') continue;

      const cStart = new Date(contract.startDate);
      const cEnd = new Date(contract.endDate);

      // Overlap: rowStart ≤ cEnd  AND  rowEnd ≥ cStart
      if (rowStart <= cEnd && rowEnd >= cStart) {
        const key = `${row.hoardingID}-${contractID}`;
        if (seen.has(key)) continue;
        seen.add(key);

        const customer = customers.find(c =>
          Number(c.customerID) === Number(contract.customerID)
        );
        conflicts.push({
          hoardingID: row.hoardingID,
          hoardingCode: row.hoardingCode || `#${row.hoardingID}`,
          rowStart: row.startDate,
          rowEnd: row.endDate,
          contractID,
          contractStart: contract.startDate,
          contractEnd: contract.endDate,
          customerName: customer?.customerName || `Customer #${contract.customerID}`,
          status: contract.status,
        });
      }
    }
  }
  return conflicts;
}
function numberToWords(n) {
  n = Math.round(Math.abs(n));
  if (!n) return 'Zero Only';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
  const two = (x) => x < 20 ? ones[x] : tens[Math.floor(x / 10)] + (x % 10 ? ' ' + ones[x % 10] : '');
  const three = (x) => x < 100 ? two(x) : ones[Math.floor(x / 100)] + ' Hundred' + (x % 100 ? ' ' + two(x % 100) : '');
  let w = '';
  if (Math.floor(n / 10000000)) w += two(Math.floor(n / 10000000)) + ' Crore ';
  if (Math.floor((n % 10000000) / 100000)) w += two(Math.floor((n % 10000000) / 100000)) + ' Lakh ';
  if (Math.floor((n % 100000) / 1000)) w += two(Math.floor((n % 100000) / 1000)) + ' Thousand ';
  if (n % 1000) w += three(n % 1000);
  return w.trim() + ' Only';
}

function normalizeList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.$values)) return res.$values;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.items)) return res.items;
  return [];
}

function normalizeCustomer(raw) {
  if (!raw) return null;
  return {
    customerID: raw.customerID ?? raw.CustomerID ?? raw.id ?? raw.Id ?? 0,
    customerName: raw.customerName ?? raw.CustomerName ?? '',
    addressLine1: raw.addressLine1 ?? raw.AddressLine1 ?? '',
    addressLine2: raw.addressLine2 ?? raw.AddressLine2 ?? '',
    addressLine3: raw.addressLine3 ?? raw.AddressLine3 ?? '',
    city: raw.city ?? raw.City ?? '',
    district: raw.district ?? raw.District ?? '',
    country: 'India',
    phone1: raw.phone1 ?? raw.Phone1 ?? '',
    phone2: raw.phone2 ?? raw.Phone2 ?? '',
    gstNumber: raw.gstNumber ?? raw.GstNumber ?? raw.gSTNumber ?? '',
    authorizedName: raw.authorizedName ?? raw.AuthorizedName ?? '',
  };
}

function normalizeCompany(raw) {
  return {
    companyID: raw.company_ID ?? raw.companyID ?? raw.CompanyID ?? 0,
    companyName: raw.company_Name ?? raw.companyName ?? raw.CompanyName ?? '',
    isActive: raw.is_Active ?? raw.isActive ?? raw.IsActive ?? false,
  };
}

function normalizeQuotation(raw) {
  return {
    quotationID: Number(raw.quotationID ?? raw.QuotationID ?? 0),
    quotationRevisionNumber: Number(raw.quotationRevisionNumber ?? raw.QuotationRevisionNumber ?? 0),
    customerID: Number(raw.customerID ?? raw.CustomerID ?? 0),
    quotationNumber: raw.quotationNumber ?? raw.QuotationNumber ?? '',
    quotationDate: (raw.quotationDate ?? raw.QuotationDate ?? '').split('T')[0],
    cGSTPercent: Number(raw.cGSTPercent ?? raw.CGSTPercent ?? 9),
    cGSTAmount: Number(raw.cGSTAmount ?? raw.CGSTAmount ?? 0),
    sGSTPercent: Number(raw.sGSTPercent ?? raw.SGSTPercent ?? 9),
    sGSTAmount: Number(raw.sGSTAmount ?? raw.SGSTAmount ?? 0),
    totalAmount: Number(raw.totalAmount ?? raw.TotalAmount ?? 0),
  };
}

function normalizeQuotLine(raw) {
  return {
    quotationLineNumber: Number(raw.quotationLineNumber ?? raw.QuotationLineNumber ?? 0),
    quotationID: Number(raw.quotationID ?? raw.QuotationID ?? 0),
    quotationRevisionNumber: Number(raw.quotationRevisionNumber ?? raw.QuotationRevisionNumber ?? 0),
    hoardingID: Number(raw.hoardingID ?? raw.HoardingID ?? 0),
    purpose: raw.purpose ?? raw.Purpose ?? '',
    periodBeginDate: (raw.periodBeginDate ?? raw.PeriodBeginDate ?? '').split('T')[0],
    periodEndDate: (raw.periodEndDate ?? raw.PeriodEndDate ?? '').split('T')[0],
    rentAmount: Number(raw.rentAmount ?? raw.RentAmount ?? 0),
    mergeFlag: (raw.mergeFlag ?? raw.MergeFlag ?? false) === true || String(raw.mergeFlag ?? raw.MergeFlag ?? '').toLowerCase() === 'true',
  };
}

function normalizeHoarding(raw) {
  if (!raw) return null;
  const isExt = raw.isExternal === true || String(raw.isExternal).toLowerCase() === 'true' ||
    raw.is_External === true || String(raw.is_External).toLowerCase() === 'true' ||
    raw.IsExternal === true || String(raw.IsExternal).toLowerCase() === 'true' ||
    raw.Is_External === true || String(raw.Is_External).toLowerCase() === 'true';
  return {
    hoardingID: Number(raw.hoardingID ?? raw.HoardingID ?? 0),
    hoardingCode: raw.hoardingCode ?? raw.HoardingCode ?? '',
    monthlyRent: Number(raw.monthlyRent ?? raw.MonthlyRent ?? 0),
    width: Number(raw.width ?? raw.Width ?? 0),
    height: Number(raw.height ?? raw.Height ?? 0),
    siteID: raw.siteID ?? raw.SiteID ?? raw.outsideSiteID ?? raw.OutsideSiteID ?? null,
    status: raw.status ?? raw.Status ?? '',
    material: raw.material ?? raw.Material ?? '',
    hoardingType: raw.hoardingType ?? raw.HoardingType ?? '',
    site: raw.site ? (isExt ? normalizeOutsideSite(raw.site) : normalizeSite(raw.site)) : (raw.outsideSite ? normalizeOutsideSite(raw.outsideSite) : null),
    isExternal: isExt,
  };
}
// Normalize siteID to number for consistent Map keys
const toSID = (val) => {
  const n = Number(val);
  return (!isNaN(n) && n > 0) ? n : null;
};
const isAvailable = (h) => {
  if (typeof h.status === 'boolean') return h.status;
  if (typeof h.status === 'string') return ['available', 'active'].includes(h.status.toLowerCase());
  return false;
};

function parseSize(sizeStr) {
  const parts = (sizeStr || '').split(/[Xx×\s]+/).map(p => parseFloat(p.trim())).filter(n => !isNaN(n) && n > 0);
  return { w: parts[0] || 0, h: parts[1] || 0 };
}



// AFTER — collect UNIQUE siteIDs from ALL sources, deduplicated
function buildSiteColorMap(hoardings, sites = [], outsideSites = []) {
  const map = new Map();
  let idx = 0;

  // Seed from hoardings FIRST — these siteIDs are confirmed correct
  for (const h of hoardings) {
    const sid = toSID(h.siteID);
    if (sid != null && !map.has(sid)) {
      map.set(sid, SITE_PASTEL_PALETTE[idx % SITE_PASTEL_PALETTE.length]);
      idx++;
    }
  }

  // Fill any remaining internal site IDs not already covered
  for (const s of sites) {
    const sid = toSID(s.siteID ?? s.SiteID);
    if (sid != null && !map.has(sid)) {
      map.set(sid, SITE_PASTEL_PALETTE[idx % SITE_PASTEL_PALETTE.length]);
      idx++;
    }
  }

  // Fill any external outside site IDs not already covered
  for (const os of outsideSites) {
    const sid = toSID(os.outsideSiteID ?? os.siteID ?? os.SiteID);
    if (sid != null && !map.has(sid)) {
      map.set(sid, SITE_PASTEL_PALETTE[idx % SITE_PASTEL_PALETTE.length]);
      idx++;
    }
  }

  return map;
}
function normalizeSite(raw) {
  if (!raw) return null;
  return {
    siteID: raw.siteID ?? raw.SiteID ?? 0,
    addressLine1: raw.addressLine1 ?? raw.AddressLine1 ?? '',
    addressLine2: raw.addressLine2 ?? raw.AddressLine2 ?? '',
    addressLine3: raw.addressLine3 ?? raw.AddressLine3 ?? '',
    landmark: raw.landmark ?? raw.Landmark ?? '',
    city: raw.city ?? raw.City ?? '',
    district: raw.district ?? raw.District ?? '',
    siteType: raw.siteType ?? raw.SiteType ?? '',
    country: raw.country ?? raw.Country ?? '',
    ownerID: raw.ownerID ?? raw.OwnerID ?? 0,
  };
}

function normalizeOutsideSite(raw) {
  if (!raw) return null;
  return {
    outsideSiteID: Number(raw.outsideSiteID ?? raw.OutsideSiteID ?? raw.siteID ?? raw.SiteID ?? 0),
    siteID: Number(raw.outsideSiteID ?? raw.OutsideSiteID ?? raw.siteID ?? raw.SiteID ?? 0),
    addressLine1: raw.addressLine1 ?? raw.AddressLine1 ?? '',
    addressLine2: raw.addressLine2 ?? raw.AddressLine2 ?? '',
    addressLine3: raw.addressLine3 ?? raw.AddressLine3 ?? '',
    landmark: raw.landmark ?? raw.Landmark ?? '',
    city: raw.city ?? raw.City ?? '',
    district: raw.district ?? raw.District ?? '',
    state: raw.state ?? raw.State ?? '',
    pincode: raw.pincode ?? raw.Pincode ?? '',
    siteType: raw.siteType ?? raw.SiteType ?? '',
    country: raw.country ?? raw.Country ?? '',
    vendorID: Number(raw.vendorID ?? raw.VendorID ?? 0),
  };
}

function getHoardingSite(h, siteMap, outsideSiteMap) {
  if (!h) return null;
  const isExt = h.isExternal === true || String(h.isExternal).toLowerCase() === 'true' ||
    h.is_External === true || String(h.is_External).toLowerCase() === 'true' ||
    h.IsExternal === true || String(h.IsExternal).toLowerCase() === 'true' ||
    h.Is_External === true || String(h.Is_External).toLowerCase() === 'true';
  const rawSid = toSID(h.siteID ?? h.site?.siteID ?? h.site?.SiteID ?? h.site?.outsideSiteID ?? h.outsideSiteID);

  if (isExt) {
    if (rawSid != null && outsideSiteMap?.has(rawSid)) return outsideSiteMap.get(rawSid);
    if (h.outsideSite) return normalizeOutsideSite(h.outsideSite);
    if (h.site) return normalizeOutsideSite(h.site);
    if (rawSid != null && siteMap?.has(rawSid)) return siteMap.get(rawSid);
    return null;
  } else {
    if (rawSid != null && siteMap?.has(rawSid)) return siteMap.get(rawSid);
    if (h.site) return normalizeSite(h.site);
    return null;
  }
}

function buildSiteAddress(site, fallback = '') {
  if (!site) return fallback;
  const addrParts = [site.addressLine1, site.addressLine2, site.addressLine3].filter(Boolean);
  const cityPart = [site.city, site.district].filter(Boolean).join(', ');
  const full = [...addrParts, cityPart].filter(Boolean).join(', ');
  return full || fallback;
}

function getSiteDisplayLines(site, fallback = '') {
  if (!site) return { line1: fallback, line2: '' };

  const addrParts = [site.addressLine1, site.addressLine2, site.addressLine3]
    .filter(Boolean);
  const cityDistrict = [site.city, site.district].filter(Boolean).join(', ');

  // Primary address: use explicit address lines if present, else city/district
  const line1 = addrParts.length > 0
    ? addrParts.join(', ')
    : cityDistrict || fallback;

  // Secondary line: landmark · city/district (only when line1 didn't already use them)
  const line2Parts = [];
  if (site.landmark) line2Parts.push(`Nr. ${site.landmark}`);
  // Include city/district in line2 only when we had actual address lines in line1
  if (addrParts.length > 0 && cityDistrict) line2Parts.push(cityDistrict);
  if (site.siteType) line2Parts.push(site.siteType);

  return { line1, line2: line2Parts.join(' · ') };
}

function getSiteAddress(h) {
  return buildSiteAddress(h?.site, h?.hoardingCode || '');
}

const newHoardingRow = (h = null, globalStart = '', globalEnd = '', siteMap = null, outsideSiteMap = null) => {
  const isExt = h?.isExternal === true || String(h?.isExternal).toLowerCase() === 'true' ||
    h?.is_External === true || String(h?.is_External).toLowerCase() === 'true' ||
    h?.IsExternal === true || String(h?.IsExternal).toLowerCase() === 'true' ||
    h?.Is_External === true || String(h?.Is_External).toLowerCase() === 'true';

  const rawSiteID = toSID(h?.siteID ?? h?.site?.siteID ?? h?.site?.SiteID ?? h?.site?.outsideSiteID ?? h?.outsideSiteID);
  const site = getHoardingSite(h, siteMap, outsideSiteMap);
  const siteID = toSID(site?.outsideSiteID ?? site?.siteID) ?? rawSiteID ?? null;

  const start = globalStart || '';
  const end = globalEnd || '';
  const days = (start && end) ? calculateDays(start, end) : 30;
  const computedEnd = end || (start ? calculateEndDate(start, days) : '');
  const nos = calcNOSFromDays(days);
  const baseRent = nos * (h?.monthlyRent || 0);

  return {
    _id: uid(),
    rowType: 'hoarding',
    hoardingID: h?.hoardingID || 0,
    siteID,
    siteObj: site,
    hoardingCode: h?.hoardingCode || '',
    isExternal: isExt,
    location: buildSiteAddress(site, h?.hoardingCode || ''),
    size: h ? `${h.width} X ${h.height}` : '',
    sqFt: h ? (h.width * h.height) : 0,
    nos: nos,
    startDate: start,
    endDate: computedEnd,
    days: days,
    ratePerMonth: h?.monthlyRent || 0,
    amount: baseRent,
    printType: '',
    printRate: 0,
    printingCost: 0,
    quotationLineNumber: 0,
    saved: false,
  };
};

const newPrintingRow = (label = 'Flex Banner Printing', globalStart = '', globalEnd = '') => ({
  _id: uid(),
  rowType: 'printing',
  hoardingID: 0,
  siteID: null,
  location: label,
  hoardingCode: '',
  size: '',
  sqFt: 0,
  nos: 1,
  startDate: globalStart,
  endDate: globalEnd,
  ratePerMonth: 8,
  amount: 0,
  printingCost: 0,
  quotationLineNumber: 0,
  saved: false,
});
const newExtraChargeRow = () => ({
  _id: uid(),
  rowType: 'extra',
  hoardingID: 0,
  siteID: null,
  location: 'Extra Charge',
  hoardingCode: '',
  size: '',
  sqFt: 0,
  nos: 1,
  startDate: '',
  endDate: '',
  ratePerMonth: 0,
  amount: 0,
  printingCost: 0,
  quotationLineNumber: 0,
  saved: false,
});
function newMergedRow(rowsArr, direction) {
  const sizes = rowsArr.map(r => parseSize(r.size));
  const gaps = Math.max(rowsArr.length - 1, 1); // at least 1 gap

  let mw, mh;
  if (direction === 'S') {
    const singleW = sizes[0]?.w || 0;
    const singleH = sizes[0]?.h || 0;
    mw = (singleW * 2) + 1;
    mh = (singleH * 2) + 1;
  } else if (direction === 'H') {
    mw = sizes.reduce((s, sz) => s + sz.w, 0) + gaps;   // sum widths + gaps
    mh = Math.max(...sizes.map(s => s.h));               // tallest height
  } else {
    mw = Math.max(...sizes.map(s => s.w));               // widest width
    mh = sizes.reduce((s, sz) => s + sz.h, 0) + gaps;   // sum heights + gaps
  }

  const sqFt = mw * mh;
  const combinedRate = rowsArr.reduce((s, r) => s + Number(r.ratePerMonth || 0), 0);
  const start = rowsArr[0]?.startDate || '';
  const end = rowsArr[0]?.endDate || '';
  const days = (start && end) ? calculateDays(start, end) : 30;
  const nos = calcNOSFromDays(days);
  const combinedAmt = nos * combinedRate;

  const isExt = rowsArr.some(r => r?.isExternal === true || String(r?.isExternal).toLowerCase() === 'true' || r?.is_External === true || String(r?.is_External).toLowerCase() === 'true' || r?.IsExternal === true || String(r?.IsExternal).toLowerCase() === 'true');

  return {
    _id: uid(),
    rowType: 'merged',
    isMerged: true,
    isExternal: isExt,
    mergeDirection: direction,
    mergedFromIds: rowsArr.map(r => r._id),
    mergedHoardingIDs: rowsArr.map(r => Number(r.hoardingID) || 0).filter(id => id > 0),
    hoardingID: 0,
    siteID: null,
    location: [...new Set(rowsArr.map(r => {
      if (r.rowType === 'hoarding' && r.siteObj) {
        return `${r.siteObj.addressLine1 || ''}${r.siteObj.city ? `, ${r.siteObj.city}` : ''}`;
      }
      return r.location || '';
    }).filter(Boolean))].join(' + '),
    hoardingCode: rowsArr.map(r => r.hoardingCode || '').join(' + '),
    size: `${mw} X ${mh}`,
    sqFt,
    nos: nos,
    startDate: start,
    endDate: end,
    days: days,
    ratePerMonth: combinedRate,
    amount: combinedAmt,
    printingCost: 0,
    quotationLineNumber: 0,
    saved: false,
  };
}



/* ═══════════════════════════════════════════
   PORTAL DROPDOWN
═══════════════════════════════════════════ */
function PortalDropdown({ open, triggerRef, panelRef, children }) {
  const [style, setStyle] = useState({ position: 'fixed', top: 0, left: 0, width: 0, zIndex: 99999 });
  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    const upd = () => {
      const r = triggerRef.current?.getBoundingClientRect();
      if (!r) return;
      const ph = panelRef.current?.offsetHeight || 260;
      const flipUp = (window.innerHeight - r.bottom) < ph + 8 && r.top > ph + 8;
      setStyle({ position: 'fixed', top: flipUp ? r.top - ph - 4 : r.bottom + 4, left: r.left, width: r.width, zIndex: 99999 });
    };
    upd();
    window.addEventListener('scroll', upd, true);
    window.addEventListener('resize', upd);
    return () => { window.removeEventListener('scroll', upd, true); window.removeEventListener('resize', upd); };
  }, [open, triggerRef, panelRef]);
  if (!open) return null;
  return ReactDOM.createPortal(<div ref={panelRef} style={style}>{children}</div>, document.body);
}

function useOutsideClick(wrapRef, panelRef, open, onClose) {
  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (!wrapRef.current?.contains(e.target) && !panelRef.current?.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open, wrapRef, panelRef, onClose]);
}

/* ═══════════════════════════════════════════
   TOAST
═══════════════════════════════════════════ */
function Toast({ msg, type, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3200); return () => clearTimeout(t); }, [onDone]);
  return ReactDOM.createPortal(
    <div className={`qt-toast qt-toast--${type}`} style={{ zIndex: 9999999 }}>
      {type === 'success' ? <CheckCircle2 size={15} /> : <AlertCircle size={15} />}
      {msg}
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════
   CUSTOMER COMBO
═══════════════════════════════════════════ */
function CustomerCombo({ value, onChange, customers }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null); const triggerRef = useRef(null);
  const panelRef = useRef(null); const inputRef = useRef(null); const listRef = useRef(null);
  const close = useCallback(() => { setOpen(false); setQuery(''); }, []);
  useOutsideClick(wrapRef, panelRef, open, close);

  const selected = customers.find(c => String(c.customerID) === String(value));
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return q ? customers.filter(c =>
      (c.customerName || '').toLowerCase().includes(q) ||
      (c.city || '').toLowerCase().includes(q) ||
      (c.district || '').toLowerCase().includes(q)
    ) : customers;
  }, [customers, query]);

  const openDD = () => { setOpen(true); setQuery(''); setTimeout(() => inputRef.current?.focus(), 0); };
  const select = (c) => { onChange(c); setOpen(false); setQuery(''); };
  const clear = (e) => { e.stopPropagation(); onChange(null); setOpen(false); setQuery(''); };
  const nav = (e) => {
    const items = listRef.current?.querySelectorAll('.pg-combo-option');
    const idx = Array.from(items || []).indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); (items[idx + 1] || items[0])?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); (items[idx - 1] || items[items.length - 1])?.focus(); }
    else if (e.key === 'Escape') close();
  };

  return (
    <div className="pg-combo-wrap" ref={wrapRef}>
      <div
        ref={triggerRef}
        className="pg-field-wrap pg-combo-trigger pg-field-wrap--normal"
        onClick={openDD} tabIndex={0}
        onKeyDown={e => { if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDD(); } } else nav(e); }}
      >
        <User size={14} color="#c0c0d8" style={{ flexShrink: 0 }} />
        <span className={`pg-combo-display${!selected ? ' pg-combo-display--placeholder' : ''}`}>
          {selected ? selected.customerName : customers.length === 0 ? 'Loading customers…' : 'Select customer…'}
        </span>
        {selected ? <X size={13} className="pg-combo-clear" onClick={clear} /> : <ChevronDown size={13} color="#c0c0d8" style={{ flexShrink: 0 }} />}
      </div>
      <PortalDropdown open={open} triggerRef={triggerRef} panelRef={panelRef}>
        <div className="pg-combo-panel" style={{ position: 'static' }}>
          <div className="pg-combo-search">
            <Search size={12} color="#c0c0d8" style={{ flexShrink: 0 }} />
            <input ref={inputRef} className="pg-combo-search__input" placeholder="Search by name or city…" value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'ArrowDown') { e.preventDefault(); listRef.current?.querySelectorAll('.pg-combo-option')?.[0]?.focus(); } else if (e.key === 'Escape') close(); }}
            />
            {query && <X size={11} className="pg-combo-clear" onClick={() => setQuery('')} />}
          </div>
          <div className="pg-combo-list" ref={listRef}>
            {filtered.length === 0
              ? <div className="pg-combo-empty">No customers found</div>
              : filtered.map(c => (
                <div key={c.customerID}
                  className={`pg-combo-option${String(c.customerID) === String(value) ? ' pg-combo-option--active' : ''}`}
                  onClick={() => select(c)} tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(c); } else nav(e); }}
                >
                  <div style={{ flex: 1 }}>
                    <span className="pg-combo-option__name">{c.customerName}</span>
                    <span className="pg-combo-option__id">{[c.city, c.district].filter(Boolean).join(', ')}</span>
                  </div>
                  {String(c.customerID) === String(value) && <Check size={12} color="#049edf" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
                </div>
              ))}
          </div>
        </div>
      </PortalDropdown>
    </div>
  );
}

/* ═══════════════════════════════════════════
   COMPANY COMBO
═══════════════════════════════════════════ */
function CompanyCombo({ value, onChange, companies }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null); const triggerRef = useRef(null);
  const panelRef = useRef(null); const inputRef = useRef(null); const listRef = useRef(null);
  const close = useCallback(() => { setOpen(false); setQuery(''); }, []);
  useOutsideClick(wrapRef, panelRef, open, close);

  const selected = companies.find(c => String(c.companyID) === String(value));
  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return q ? companies.filter(c =>
      (c.companyName || '').toLowerCase().includes(q)
    ) : companies;
  }, [companies, query]);

  const openDD = () => { setOpen(true); setQuery(''); setTimeout(() => inputRef.current?.focus(), 0); };
  const select = (c) => { onChange(c); setOpen(false); setQuery(''); };
  const clear = (e) => { e.stopPropagation(); onChange(null); setOpen(false); setQuery(''); };
  const nav = (e) => {
    const items = listRef.current?.querySelectorAll('.pg-combo-option');
    const idx = Array.from(items || []).indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); (items[idx + 1] || items[0])?.focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); (items[idx - 1] || items[items.length - 1])?.focus(); }
    else if (e.key === 'Escape') close();
  };

  return (
    <div className="pg-combo-wrap" ref={wrapRef}>
      <div
        ref={triggerRef}
        className="pg-field-wrap pg-combo-trigger pg-field-wrap--normal"
        onClick={openDD} tabIndex={0}
        onKeyDown={e => { if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openDD(); } } else nav(e); }}
        style={{ background: '#fff' }}
      >
        <Building2 size={14} color="#c0c0d8" style={{ flexShrink: 0 }} />
        <span className={`pg-combo-display${!selected ? ' pg-combo-display--placeholder' : ''}`}>
          {selected ? selected.companyName : companies.length === 0 ? 'Loading companies…' : 'Select company…'}
        </span>
        {selected ? <X size={13} className="pg-combo-clear" onClick={clear} /> : <ChevronDown size={13} color="#c0c0d8" style={{ flexShrink: 0 }} />}
      </div>
      <PortalDropdown open={open} triggerRef={triggerRef} panelRef={panelRef}>
        <div className="pg-combo-panel" style={{ position: 'static' }}>
          <div className="pg-combo-search">
            <Search size={12} color="#c0c0d8" style={{ flexShrink: 0 }} />
            <input ref={inputRef} className="pg-combo-search__input" placeholder="Search company by name…" value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => { if (e.key === 'ArrowDown') { e.preventDefault(); listRef.current?.querySelectorAll('.pg-combo-option')?.[0]?.focus(); } else if (e.key === 'Escape') close(); }}
            />
            {query && <X size={11} className="pg-combo-clear" onClick={() => setQuery('')} />}
          </div>
          <div className="pg-combo-list" ref={listRef}>
            {filtered.length === 0
              ? <div className="pg-combo-empty">No companies found</div>
              : filtered.map(c => (
                <div key={c.companyID}
                  className={`pg-combo-option${String(c.companyID) === String(value) ? ' pg-combo-option--active' : ''}`}
                  onClick={() => select(c)} tabIndex={0}
                  onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); select(c); } else nav(e); }}
                >
                  <div style={{ flex: 1 }}>
                    <span className="pg-combo-option__name">{c.companyName}</span>
                  </div>
                  {String(c.companyID) === String(value) && <Check size={12} color="#049edf" style={{ marginLeft: 'auto', flexShrink: 0 }} />}
                </div>
              ))}
          </div>
        </div>
      </PortalDropdown>
    </div>
  );
}

/* ═══════════════════════════════════════════
   SORT ICON
═══════════════════════════════════════════ */
function SortIcon({ col, sortKey, sortDir }) {
  const active = sortKey === col;
  return (
    <span className="pg-sort-icon">
      <ChevronUp size={10} color={active && sortDir === 'asc' ? '#049edf' : '#c0c0d8'} className="pg-sort-icon__up" />
      <ChevronDown size={10} color={active && sortDir === 'desc' ? '#049edf' : '#c0c0d8'} className="pg-sort-icon__down" />
    </span>
  );
}

/* ═══════════════════════════════════════════
   PDF BUILDER
═══════════════════════════════════════════ */
async function fetchCompanyDetailsForQuotation(quotationId, revisionNumber) {
  try {
    const qcRes = await apiService.getQuotationCompanyByQuotation(quotationId, revisionNumber);
    const qcList = Array.isArray(qcRes) ? qcRes : Array.isArray(qcRes?.data) ? qcRes.data : [];
    if (!qcList || qcList.length === 0) return null;
    const companyId = qcList[0].company_ID ?? qcList[0].companyID ?? qcList[0].CompanyID;
    if (!companyId) return null;
    const cdRes = await apiService.getCompanyDetailsById(companyId);
    const cd = cdRes?.data ?? cdRes;
    if (!cd) return null;
    return {
      name: cd.company_Name ?? cd.companyName ?? COMPANY.name,
      line1: cd.address_Line1 ?? cd.addressLine1 ?? COMPANY.line1,
      line2: [cd.address_Line2 ?? cd.addressLine2, cd.city, cd.state, cd.pincode]
        .filter(Boolean).join(', ') || COMPANY.line2,
      contactPerson: cd.contact_Person ?? cd.contactPerson ?? '',
      mobile: cd.mobile_No ?? cd.mobileNo ?? '',
      gstin: cd.gstin ?? cd.GSTIN ?? COMPANY.gstin,
      pan: cd.paN_No ?? cd.panNo ?? cd.PAN_No ?? COMPANY.pan,
      bank: cd.bank_Name ?? cd.bankName ?? COMPANY.bank,
      branch: cd.branch_Name ?? cd.branchName ?? COMPANY.branch,
      account: cd.account_No ?? cd.accountNo ?? COMPANY.account,
      ifsc: cd.ifsC_Code ?? cd.ifscCode ?? COMPANY.ifsc,
      accountHolder: cd.account_Holder_Name ?? cd.accountHolderName ?? COMPANY.name,
      signatory: COMPANY.signatory,
    };
  } catch (err) {
    console.warn('[fetchCompanyDetailsForQuotation] Failed:', err?.message);
    return null;
  }
}

function buildPrintHTML({ rows, withPrinting, selectedCustomer, quotNo, quotDate,
  revisionNo, cgstPct, sgstPct, subTotal, cgstAmt, sgstAmt,
  roundOff, finalTotal, selectedTerms, termsTexts,
  docNoLabel = 'Quotation No.', docNoValue = null, docTitle = null,
  companyDetails = null }) {

  // Use API-fetched company data when available, falling back to hardcoded COMPANY
  const co = companyDetails || COMPANY;

  const pages = rows.length === 0 ? [[]] : [];
  for (let i = 0; i < rows.length; i += ROWS_PER_PRINT_PAGE) pages.push(rows.slice(i, i + ROWS_PER_PRINT_PAGE));

  let run = 0;
  const pageRun = pages.map(pg => { run += pg.reduce((s, r) => s + Number(r.amount || 0), 0); return run; });

  const stdCols = 6;
  const prtCols = 7;
  const nCols = withPrinting ? prtCols : stdCols;

  const upiStr = `upi://pay?pa=${co.account}@axisbank&pn=${encodeURIComponent(co.name)}&cu=INR`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(upiStr)}&size=100x100&bgcolor=ffffff&color=000000&ecc=M`;

  const css = `
*{margin:0;padding:0;box-sizing:border-box;}
body{font-family:Arial,Helvetica,sans-serif;font-size:11px;color:#000;background:#fff;}
.page{width:210mm;padding:7mm 10mm 5mm 10mm;page-break-after:always;}
.page:last-child{page-break-after:avoid;}
.co-hdr{text-align:center;border:1px solid #000;border-bottom:none;padding:5px 8px 4px;}
.co-name{font-size:17px;font-weight:bold;letter-spacing:.5px;}
.co-addr{font-size:10px;margin-top:2px;}
.doc-row{display:flex;border:1px solid #000;border-bottom:none;}
.dleft{padding:3px 8px;border-right:1px solid #000;font-weight:bold;width:28%;font-size:11px;}
.dctr{flex:1;text-align:center;padding:3px 8px;font-size:13px;font-weight:bold;letter-spacing:1px;}
.dright{padding:3px 8px;border-left:1px solid #000;width:18%;text-align:right;font-weight:bold;font-size:11px;}
.cust-row{border:1px solid #000;border-bottom:none;display:flex;}
.cleft{padding:6px 8px;flex:1;border-right:1px solid #000;font-size:10.5px;line-height:1.5;}
.cright{padding:5px 8px;width:250px;}
.cright table{width:100%;border-collapse:collapse;}
.cright td{padding:2px 2px;font-size:10.5px;vertical-align:top;}
.clbl{white-space:nowrap;font-weight:bold;width:88px;}
.itbl{width:100%;border-collapse:collapse;border:1px solid #000;}
.itbl th{border:1px solid #000;padding:4px 5px;font-size:10px;background:#f0f0f0;text-align:center;font-weight:bold;line-height:1.3;}
.itbl td{border:1px solid #000;padding:3px 5px;font-size:10.5px;vertical-align:middle;}
.c{text-align:center;}.r{text-align:right;}.l{text-align:left;}
.bf td{font-weight:bold;background:#f5f5f5;}
.cf td{font-weight:bold;background:#f0f0f0;font-size:11px;}
.erow{height:21px;}
.merged-tag{display:inline-block;font-size:8.5px;font-weight:bold;color:#6a22c0;background:#f0e8ff;border:1px solid #d4aaff;border-radius:3px;padding:1px 5px;margin-bottom:2px;}
.fwrap{border:1px solid #000;border-top:none;}
.f-words-row{display:flex;border-top:1px solid #000;}
.f-words{flex:1;padding:5px 8px;border-right:1px solid #000;font-size:10.5px;line-height:1.6;}
.f-totals{width:220px;padding:4px 8px;}
.f-totals table{width:100%;border-collapse:collapse;}
.f-totals td{padding:2px 3px;font-size:10.5px;}
.gr{text-align:right;}
.f-grand td{font-weight:bold;font-size:11.5px;border-top:1px solid #000;padding-top:3px;}
.f-main-row{display:flex;border-top:1px solid #000;}
.f-bank{flex:1;padding:5px 8px;border-right:1px solid #000;font-size:10px;line-height:1.7;}
.f-qr{width:110px;border-right:1px solid #000;display:flex;align-items:center;justify-content:center;padding:5px;}
.f-sig{width:165px;padding:5px 8px;text-align:center;font-size:10px;display:flex;flex-direction:column;}
.f-sig-company{font-weight:bold;font-size:11px;margin-bottom:5px;}
.f-sig-name{font-size:10px;}
.f-grand-box{border-top:1px solid #000;margin-top:5px;padding-top:4px;display:flex;justify-content:space-between;align-items:center;}
.f-grand-lbl{font-size:10.5px;font-weight:bold;}
.f-grand-val{font-size:12px;font-weight:bold;}
.f-blank{display:inline-block;min-width:200px;border-bottom:1px solid #bbb;}
.f-terms{padding:5px 8px;border-top:1px solid #000;font-size:10px;line-height:1.6;}
.f-eoe{padding:3px 8px;border-top:1px solid #000;font-size:10px;display:flex;justify-content:space-between;}
@media print{body{margin:0;}.page{padding:7mm 10mm 5mm 10mm;width:100%;}}
@page{size:A4;margin:0;}
`;

  const renderHdr = (pgIdx) => {
    const first = pgIdx === 0;
    const c = selectedCustomer;
    const contactStr = (co.contactPerson || co.mobile)
      ? ` ${[co.contactPerson, co.mobile].filter(Boolean).join(' # ')}`
      : '';
    return `
<div class="co-hdr">
  <div class="co-name">${co.name}</div>
  <div class="co-addr">${co.line1}</div>
  <div class="co-addr">${co.line2}${contactStr}</div>
</div>
<div class="doc-row">
  <div class="dleft">Debit Memo</div>
  <div class="dctr">QUOTATION</div>
  <div class="dright">Original</div>
</div>
<div class="cust-row">
  <div class="cleft">
    <span style="font-size:9.5px;color:#555;">M/s. :</span>
    <strong style="font-size:11px;">${c?.customerName || '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;'}</strong>
    ${first && c?.addressLine1 ? `<br>${c.addressLine1}` : ''}
    ${first && c?.city ? `<br>${[c.city, c.district].filter(Boolean).join(', ')}` : ''}
    ${first && c?.gstNumber ? `<br><span style="font-size:10px;"><strong>GSTIN :</strong> ${c.gstNumber}</span>` : ''}
    ${first ? `<br><span style="font-size:10px;"><strong>GST No :</strong> &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</span>` : ''}
  </div>
  <div class="cright">
    <table>
      <tr><td class="clbl">${docNoLabel}</td><td>: ${docNoValue ?? `${quotNo}${Number(revisionNo) > 1 ? ` Rev.${revisionNo}` : ''}`}</td></tr>
      <tr><td class="clbl">Date</td><td>: ${fmtDateFull(quotDate)}</td></tr>
      
    </table>
  </div>
</div>`;
  };
  {/* <tr><td class="clbl">PO No.</td><td>: &nbsp;</td></tr> */ }
  const renderTblHdr = () => withPrinting
    ? `<tr>
        <th style="width:30px;">SR No.</th>
        <th class="l">Site Address / Product</th>
        <th style="width:62px;">SIZE</th>
        <th style="width:55px;">Month</th>
        <th style="width:70px;">Rate PER MONTH</th>
        <th style="width:82px;">Printing Cost</th>
        <th style="width:80px;">Amount</th>
      </tr>`
    : `<tr>
        <th style="width:30px;">SR No.</th>
        <th class="l">Site Address</th>
        <th style="width:62px;">SIZE</th>
        <th style="width:48px;">PERIOD (MONTH)</th>
        <th style="width:72px;">Rate PER MONTH</th>
        <th style="width:80px;">Amount</th>
      </tr>`;

  const renderRowStd = (row, sr) => {
    const dates = (row.rowType !== 'extra' && row.rowType !== 'printing') && row.startDate && row.endDate
      ? `<br><span style="font-size:9px;color:#555;">${fmtDateShort(row.startDate)} TO ${fmtDateShort(row.endDate)}</span>` : '';
    const mergeTag = row.rowType === 'merged'
      ? `<div class="merged-tag">${row.mergeDirection === 'S' ? '⊞ Square Merge' : (row.mergeDirection === 'H' ? '↔ Horizontal Merge' : '↕ Vertical Merge')}</div>`
      : '';
    return `<tr${row.rowType === 'merged' ? ' style="background:#faf5ff;"' : ''}>
      <td class="c">${sr}</td>
      <td class="l">${mergeTag}${row.location || ''}${dates}</td>
      <td class="c">${row.size || ''}</td>
      <td class="c">${row.nos || 1}.00</td>
      <td class="r">${fmtCurrency(row.ratePerMonth)}</td>
      <td class="r">${fmtCurrency(row.amount)}</td>
    </tr>`;
  };

  const renderRowPrint = (row, sr) => {
    const dates = (row.rowType !== 'extra' && row.rowType !== 'printing') && row.startDate && row.endDate
      ? `<br><span style="font-size:9px;color:#555;">${fmtDateShort(row.startDate)} TO ${fmtDateShort(row.endDate)}</span>` : '';
    const printCell = row.rowType === 'printing'
      ? `<td class="r" style="font-weight:bold;">${row.amount > 0 ? fmtCurrency(row.amount) : '—'}</td>`
      : `<td class="r">${Number(row.printingCost || 0) > 0 ? fmtCurrency(row.printingCost) : '—'}</td>`;
    const mergeTag = row.rowType === 'merged'
      ? `<div class="merged-tag">${row.mergeDirection === 'S' ? '⊞ Square Merge' : (row.mergeDirection === 'H' ? '↔ H' : '↕ V')}</div>`
      : '';
    return `<tr${row.rowType === 'merged' ? ' style="background:#faf5ff;"' : ''}>
      <td class="c">${sr}</td>
      <td class="l">${mergeTag}${row.location || ''}${dates}</td>
      <td class="c">${row.size || ''}</td>
      <td class="c">${row.rowType === 'printing' ? fmtCurrency(row.sqFt) : (row.nos || 1)}</td>
      <td class="r">${fmtCurrency(row.ratePerMonth)}</td>
      ${printCell}
      <td class="r">${fmtCurrency(row.amount)}</td>
    </tr>`;
  };

  const renderRow = (row, sr) => withPrinting ? renderRowPrint(row, sr) : renderRowStd(row, sr);

  const renderFooter = (isLast) => {
    const val = (v) => isLast ? v : '';
    const bv = (w = 100) => isLast ? null : `<span style="display:inline-block;min-width:${w}px;border-bottom:1px solid #ccc;vertical-align:middle;">&nbsp;</span>`;

    const termsHtml = selectedTerms.length > 0
      ? `<div class="f-terms"><strong>Terms &amp; Condition :</strong><br>${selectedTerms.map((termID, n) => `${n + 1}. ${termsTexts[n] || ''}`).join('<br>')
      }</div>`
      : '';

    const bankSection = isLast
      ? `<strong>GSTIN :</strong> ${co.gstin} &nbsp; <strong>PAN No :</strong> ${co.pan}<br>
         <strong>Bank Name &nbsp;&nbsp;</strong>: ${co.bank}<br>
         <strong>Branch Name</strong>: ${co.branch}<br>
         <strong>Bank A/c No &nbsp;</strong>: ${co.account}<br>
         <strong>RTGS/IFSC &nbsp;&nbsp;</strong>: ${co.ifsc}`
      : `<strong>GSTIN :</strong> ${co.gstin} &nbsp; <strong>PAN No :</strong> ${co.pan}<br>
         <strong>Bank Name &nbsp;&nbsp;</strong>: ${bv(130)}<br>
         <strong>Branch Name</strong>: ${bv(120)}<br>
         <strong>Bank A/c No &nbsp;</strong>: ${bv(130)}<br>
         <strong>RTGS/IFSC &nbsp;&nbsp;</strong>: ${bv(90)}`;

    return `
<div class="fwrap">
  <div class="f-words-row">
    <div class="f-words">
      <strong>Rs. (in words) :</strong>
      ${isLast ? numberToWords(Math.round(finalTotal)) : '<span class="f-blank">&nbsp;</span>'}
    </div>
    <div class="f-totals">
      <table>
        <tr><td>Sub Total</td><td class="gr">${isLast ? `&#8377;&nbsp;${fmtCurrency(subTotal)}` : ''}</td></tr>
        <tr><td>CGST  ${cgstPct}%</td><td class="gr">${val(fmtCurrency(cgstAmt))}</td></tr>
        <tr><td>SGST  ${sgstPct}%</td><td class="gr">${val(fmtCurrency(sgstAmt))}</td></tr>
        <tr><td style="font-size:9px;color:#666;">${Number(cgstPct) + Number(sgstPct)}%</td><td></td></tr>
        <tr class="f-grand"><td>ROUND OFF</td><td class="gr">${val(fmtCurrency(roundOff))}</td></tr>
        <tr class="f-grand"><td>Grand Total</td><td class="gr">${isLast ? `&#8377;&nbsp;${fmtCurrency(finalTotal)}` : ''}</td></tr>
      </table>
    </div>
  </div>
  <div class="f-main-row">
    <div class="f-bank">
      ${bankSection}
      
    </div>
    <div class="f-qr">
      <img src="${qrUrl}" width="98" height="98" style="display:block;" alt="UPI QR"/>
    </div>
    <div class="f-sig">
      <div class="f-sig-company">For, ${co.name}</div>
      <div style="height:45px; display:flex; align-items:center; justify-content:center; margin-bottom:5px;">
        <img src="${paragSign}" style="max-height:45px; max-width:140px; object-fit:contain;" alt="Signature"/>
      </div>
      <div class="f-sig-name">${co.signatory ?? ''}</div>
      <div class="f-sig-name">(Authorised Signatory)</div>
      <div class="f-grand-box">
        <span class="f-grand-lbl">Grand Total &#8377;</span>
        <span class="f-grand-val">${val(fmtCurrency(finalTotal))}</span>
      </div>
    </div>
  </div>
  ${termsHtml}
  <div class="f-eoe"><span>E.&amp;O.E</span><span></span></div>
</div>`;
  };
  // <div style="margin-top:5px;padding-top:4px;border-top:1px dashed #bbb;display:flex;align-items:center;gap:6px;">
  //       <strong style="white-space:nowrap;">Pending Amt :</strong>
  //       <span style="flex:1;border-bottom:1px solid #ccc;">&nbsp;</span>
  //     </div>

  const pagesHtml = pages.map((pgRows, pgIdx) => {
    const isLast = pgIdx === pages.length - 1;
    const isFirst = pgIdx === 0;
    const startSR = pgIdx * ROWS_PER_PRINT_PAGE + 1;
    const prevTotal = pgIdx > 0 ? pageRun[pgIdx - 1] : 0;
    const bfSlot = !isFirst ? 1 : 0;
    const cfSlot = !isLast ? 1 : 0;
    const emptyN = Math.max(0, ROWS_PER_PRINT_PAGE - pgRows.length - bfSlot - cfSlot);

    return `<div class="page">
${renderHdr(pgIdx)}
<table class="itbl">
  <thead>${renderTblHdr()}</thead>
  <tbody>
    ${!isFirst ? `<tr class="bf"><td colspan="${nCols - 1}" class="r">B/F &rarr;</td><td class="r">${fmtCurrency(prevTotal)}</td></tr>` : ''}
    ${pgRows.map((r, i) => renderRow(r, startSR + i)).join('')}
    ${Array(emptyN).fill(`<tr class="erow">${Array(nCols).fill('<td></td>').join('')}</tr>`).join('')}
    ${!isLast ? `<tr class="cf"><td colspan="${nCols - 1}" class="r">C/F to Next Page &rarr;</td><td class="r">${fmtCurrency(pageRun[pgIdx])}</td></tr>` : ''}
  </tbody>
</table>
${renderFooter(isLast)}
</div>`;
  }).join('');

  return `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"><title>${docTitle || `Quotation - ${quotNo}`}</title><style>${css}</style></head><body>${pagesHtml}<script>window.onload=()=>setTimeout(()=>window.print(),400);</script></body></html>`;
}
function buildProformaHTML(params) {
  let invoiceId = params.invoiceNumber || (params.invoiceID != null && params.invoiceID !== 0
    ? String(params.invoiceID)
    : params.quotNo);

  if (params.quotNo) {
    const fyMatch = params.quotNo.match(/-\d{2,4}-\d{2}$/);
    if (fyMatch) {
      const fySuffix = fyMatch[0];
      if (invoiceId && !invoiceId.endsWith(fySuffix)) {
        invoiceId = invoiceId + fySuffix;
      }
    }
  }

  const html = buildPrintHTML({
    ...params,
    docNoLabel: 'Invoice ID',
    docNoValue: invoiceId,
    docTitle: `proforma_Invoice - ${invoiceId}`,
  });
  return html
    .replace(/>QUOTATION</g, '>PROFORMA INVOICE<')
    .replace(/>Site Address</g, '>Product Name<')
    .replace(/>Site Address \/ Product</g, '>Product Name<');
}
/* ═══════════════════════════════════════════
   HOARDING SELECT MODAL
═══════════════════════════════════════════ */
function HoardingSelectModal({ allHoardings, existingIds, onAdd, onClose, siteColorMap, siteMap, startDate, endDate }) {
  const [hoardingsList, setHoardingsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    let active = true;
    const fetchAvailable = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiService.getAvailableHoardings(startDate, endDate);
        const list = normalizeList(res);
        if (active) {
          const filteredInternal = list.filter(item => {
            const full = allHoardings.find(h => h.hoardingID === (item.hoardingId ?? item.hoardingID));
            const isExt = item.isExternal ?? item.isexternal ?? item.IsExternal ?? full?.isExternal ?? full?.isexternal ?? full?.IsExternal ?? false;
            return isExt !== true && String(isExt).toLowerCase() !== 'true';
          });
          const mapped = filteredInternal.map(item => {
            const full = allHoardings.find(h => h.hoardingID === (item.hoardingId ?? item.hoardingID));
            return {
              hoardingID: item.hoardingId ?? item.hoardingID,
              hoardingCode: item.hoardingCode ?? full?.hoardingCode ?? '',
              monthlyRent: item.monthlyRent ?? full?.monthlyRent ?? 0,
              width: item.width ?? full?.width ?? 0,
              height: item.height ?? full?.height ?? 0,
              status: 'Available',
              isExternal: false,
              siteID: full?.siteID ?? null,
              site: full?.site || {
                addressLine1: item.addressLine1 || '',
                city: item.city || '',
                district: item.district || '',
                siteType: item.material || '',
              }
            };
          });
          setHoardingsList(mapped);
        }
      } catch (err) {
        if (active) {
          setError(err?.response?.data?.message || err?.message || 'Failed to load available hoardings.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    if (startDate && endDate) {
      fetchAvailable();
    } else {
      setLoading(false);
      setError('Global period dates are not set.');
    }

    return () => {
      active = false;
    };
  }, [startDate, endDate, allHoardings]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return s ? hoardingsList.filter(h =>
      (h.hoardingCode || '').toLowerCase().includes(s) ||
      (h.site?.addressLine1 || '').toLowerCase().includes(s) ||
      (h.site?.city || '').toLowerCase().includes(s)
    ) : hoardingsList;
  }, [hoardingsList, search]);

  const selectable = filtered.filter(h => !existingIds.has(h.hoardingID));
  const allSelected = selectable.length > 0 && selectable.every(h => selected.has(h.hoardingID));
  const someSelected = selectable.some(h => selected.has(h.hoardingID));

  const toggle = (id) => setSelected(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => {
    if (allSelected) {
      setSelected(p => { const n = new Set(p); selectable.forEach(h => n.delete(h.hoardingID)); return n; });
    } else {
      setSelected(p => { const n = new Set(p); selectable.forEach(h => n.add(h.hoardingID)); return n; });
    }
  };

  return ReactDOM.createPortal(
    <div className="pg-overlay">
      <div className="pg-modal" style={{ maxWidth: 660, display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>
        <div className="pg-modal__head" style={{ flexShrink: 0 }}>
          <div className="pg-modal__head-left">
            <div className="pg-modal__icon-wrap"><Building2 size={20} color="#049edf" /></div>
            <div>
              <h5 className="pg-modal__title">Select Available Hoardings</h5>
              <p className="pg-modal__subtitle">{loading ? 'Loading...' : `${hoardingsList.length} available`} · Colour-coded by site</p>
            </div>
          </div>
          <button className="pg-modal__close" onClick={onClose}><X size={15} /></button>
        </div>
        <div style={{ padding: '12px 24px', borderBottom: '1px solid #f0f0f8', flexShrink: 0 }}>
          <div className="pg-search-box">
            <Search size={13} color="#c0c0d8" style={{ flexShrink: 0 }} />
            <input placeholder="Search by code, site address…" value={search} onChange={e => setSearch(e.target.value)} disabled={loading || !!error} />
            {search && <X size={12} className="pg-search-clear" onClick={() => setSearch('')} />}
          </div>
        </div>
        {!loading && !error && selectable.length > 0 && (
          <div className="qt-select-all-row" style={{ flexShrink: 0 }} onClick={toggleAll}>
            <div className={`qt-modal-check ${allSelected ? 'qt-modal-check--all' : someSelected ? 'qt-modal-check--on' : ''}`}>
              {allSelected ? <Check size={12} color="#fff" /> : someSelected ? <div style={{ width: 8, height: 2, background: '#049edf', borderRadius: 2 }} /> : null}
            </div>
            <span>{allSelected ? 'Deselect All' : `Select All (${selectable.length})`}</span>
          </div>
        )}
        <div style={{ flex: '1 1 auto', overflowY: 'auto', maxHeight: 360, minHeight: 0 }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', gap: 8 }}>
              <Loader2 size={24} className="pg-spin" color="#049edf" />
              <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, color: '#9090a8' }}>Loading available hoardings…</span>
            </div>
          ) : error ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#dc2626', fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 600 }}>
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="pg-empty__inner" style={{ padding: '32px 20px' }}>
              <Building2 size={32} color="#d0d0e8" />
              <span className="pg-empty__label">No available hoardings found</span>
            </div>
          ) : (
            filtered.map(h => {
              const checked = selected.has(h.hoardingID);
              const alreadyIn = existingIds.has(h.hoardingID);
              const sid = h.siteID ?? h.site?.siteID;
              const siteColor = toSID(sid) != null ? siteColorMap.get(toSID(sid)) : null;
              const site = h.site ? normalizeSite(h.site) : null;
              const resolvedSite = site ?? (toSID(sid) != null ? siteMap?.get(toSID(sid)) ?? null : null);
              const { line1, line2 } = getSiteDisplayLines(resolvedSite, h.hoardingCode);
              return (
                <div key={h.hoardingID}
                  onClick={() => !alreadyIn && toggle(h.hoardingID)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 24px',
                    borderBottom: '1px solid #f8f8f8',
                    borderLeft: siteColor ? `4px solid ${siteColor.border}` : '4px solid transparent',
                    cursor: alreadyIn ? 'not-allowed' : 'pointer',
                    background: checked ? 'rgba(4,158,223,0.05)' : siteColor ? siteColor.bg : '#fff',
                    opacity: alreadyIn ? 0.55 : 1,
                  }}
                >
                  <div className={`qt-modal-check ${checked ? 'qt-modal-check--on' : ''}`}>
                    {checked && <Check size={12} color="#fff" />}
                  </div>
                  {siteColor && (
                    <div style={{ width: 8, height: 8, borderRadius: '50%', background: siteColor.dot, flexShrink: 0 }} />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="pg-td__primary" style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      <MapPin size={11} color="#9090a8" style={{ flexShrink: 0, marginTop: 4 }} />
                      <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                        {line1 || h.hoardingCode}
                        {alreadyIn && <span style={{ color: '#9090a8', fontSize: 11, marginLeft: 6, fontWeight: 500, whiteSpace: 'nowrap' }}>· Already added</span>}
                      </span>
                    </div>
                    {line2 && (
                      <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, color: '#7a8499', marginTop: 1, wordBreak: 'break-word', whiteSpace: 'normal' }}>{line2}</div>
                    )}
                    {/* ✅ MODIFIED: Elevated hoarding size styling to make it larger and more visible */}
                    <div className="pg-td__secondary" style={{ wordBreak: 'break-word', whiteSpace: 'normal', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      <span>Code: {h.hoardingCode}</span>
                      <span>·</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#049edf', background: 'rgba(4,158,223,0.08)', padding: '2px 8px', borderRadius: 6, border: '1px solid rgba(4,158,223,0.18)' }}>
                        {h.width}×{h.height} ft
                      </span>
                      <span>·</span>
                      <span>₹{Number(h.monthlyRent || 0).toLocaleString('en-IN')}/mo</span>
                    </div>
                  </div>
                  <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 5, background: 'rgba(22,163,74,0.10)', color: '#16a34a', border: '1px solid rgba(22,163,74,0.2)', flexShrink: 0 }}>Available</span>
                </div>
              );
            })
          )}
        </div>
        <div className="pg-modal__foot" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12.5, color: '#9090a8', fontWeight: 600 }}>{selected.size} selected</span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="pg-btn-cancel" onClick={onClose}>Cancel</button>
            <button className="pg-btn-save" onClick={() => onAdd(selected)} disabled={selected.size === 0 || loading || !!error}>
              <Plus size={14} /> Add {selected.size > 0 ? `(${selected.size})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════════════════════
   COMPLETE ManualHoardingModal replacement
   Replace the ENTIRE function with this.
═══════════════════════════════════════════════════════════ */

function ManualHoardingModal({ allHoardings, existingIds, onAdd, onClose, siteColorMap, siteMap, startDate, endDate }) {
  const [hoardingsList, setHoardingsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    let active = true;
    const fetchAvailable = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await apiService.getAvailableHoardings(startDate, endDate);
        const list = normalizeList(res);
        if (active) {
          const filteredInternal = list.filter(item => {
            const full = allHoardings.find(h => h.hoardingID === (item.hoardingId ?? item.hoardingID));
            const isExt = item.isExternal ?? item.isexternal ?? item.IsExternal ?? full?.isExternal ?? full?.isexternal ?? full?.IsExternal ?? false;
            return isExt !== true && String(isExt).toLowerCase() !== 'true';
          });
          const mapped = filteredInternal.map(item => {
            const full = allHoardings.find(h => h.hoardingID === (item.hoardingId ?? item.hoardingID));
            return {
              hoardingID: item.hoardingId ?? item.hoardingID,
              hoardingCode: item.hoardingCode ?? full?.hoardingCode ?? '',
              monthlyRent: item.monthlyRent ?? full?.monthlyRent ?? 0,
              width: item.width ?? full?.width ?? 0,
              height: item.height ?? full?.height ?? 0,
              status: 'Available',
              isExternal: false,
              siteID: full?.siteID ?? null,
              site: full?.site || {
                addressLine1: item.addressLine1 || '',
                city: item.city || '',
                district: item.district || '',
                siteType: item.material || '',
              }
            };
          });
          setHoardingsList(mapped);
        }
      } catch (err) {
        if (active) {
          setError(err?.response?.data?.message || err?.message || 'Failed to load available hoardings.');
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    if (startDate && endDate) {
      fetchAvailable();
    } else {
      setLoading(false);
      setError('Global period dates are not set.');
    }

    return () => {
      active = false;
    };
  }, [startDate, endDate, allHoardings]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return s ? hoardingsList.filter(h =>
      (h.hoardingCode || '').toLowerCase().includes(s) ||
      (h.site?.addressLine1 || '').toLowerCase().includes(s) ||
      (h.site?.city || '').toLowerCase().includes(s)
    ) : hoardingsList;
  }, [hoardingsList, search]);

  const selectable = filtered.filter(h => !existingIds.has(h.hoardingID));
  const allSelected = selectable.length > 0 && selectable.every(h => selected.has(h.hoardingID));
  const someSelected = selectable.some(h => selected.has(h.hoardingID));

  const toggle = (id) => setSelected(p => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleAll = () => {
    if (allSelected) {
      setSelected(p => { const n = new Set(p); selectable.forEach(h => n.delete(h.hoardingID)); return n; });
    } else {
      setSelected(p => { const n = new Set(p); selectable.forEach(h => n.add(h.hoardingID)); return n; });
    }
  };

  return ReactDOM.createPortal(
    <div className="pg-overlay">
      <div className="pg-modal" style={{ maxWidth: 660, display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>
        <div className="pg-modal__head" style={{ flexShrink: 0 }}>
          <div className="pg-modal__head-left">
            <div className="pg-modal__icon-wrap"><Building2 size={20} color="#049edf" /></div>
            <div>
              <h5 className="pg-modal__title">Add Hoarding Manually</h5>
              <p className="pg-modal__subtitle">{loading ? 'Loading...' : `${hoardingsList.length} available`} · Colour-coded by site</p>
            </div>
          </div>
          <button className="pg-modal__close" onClick={onClose}><X size={15} /></button>
        </div>

        <div style={{ padding: '12px 24px', borderBottom: '1px solid #f0f0f8', flexShrink: 0 }}>
          <div className="pg-search-box">
            <Search size={13} color="#c0c0d8" style={{ flexShrink: 0 }} />
            <input
              placeholder="Search by code, site address…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              disabled={loading || !!error}
            />
            {search && <X size={12} className="pg-search-clear" onClick={() => setSearch('')} />}
          </div>
        </div>

        {!loading && !error && selectable.length > 0 && (
          <div className="qt-select-all-row" style={{ flexShrink: 0 }} onClick={toggleAll}>
            <div className={`qt-modal-check ${allSelected ? 'qt-modal-check--all' : someSelected ? 'qt-modal-check--on' : ''}`}>
              {allSelected ? <Check size={12} color="#fff" /> : someSelected ? <div style={{ width: 8, height: 2, background: '#049edf', borderRadius: 2 }} /> : null}
            </div>
            <span>{allSelected ? 'Deselect All' : `Select All (${selectable.length})`}</span>
          </div>
        )}

        <div style={{ flex: '1 1 auto', overflowY: 'auto', maxHeight: 360, minHeight: 0 }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', gap: 8 }}>
              <Loader2 size={24} className="pg-spin" color="#049edf" />
              <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, color: '#9090a8' }}>Loading available hoardings…</span>
            </div>
          ) : error ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#dc2626', fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 600 }}>
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="pg-empty__inner" style={{ padding: '32px 20px' }}>
              <Building2 size={32} color="#d0d0e8" />
              <span className="pg-empty__label">No available hoardings found</span>
            </div>
          ) : (
            filtered.map(h => {
              const checked = selected.has(h.hoardingID);
              const alreadyIn = existingIds.has(h.hoardingID);
              const sid = h.siteID ?? h.site?.siteID;
              const siteColor = toSID(sid) != null ? siteColorMap.get(toSID(sid)) : null;
              const site = h.site ? normalizeSite(h.site) : null;
              const resolvedSite = site ?? (toSID(sid) != null ? siteMap?.get(toSID(sid)) ?? null : null);
              const { line1, line2 } = getSiteDisplayLines(resolvedSite, h.hoardingCode);

              return (
                <div
                  key={h.hoardingID}
                  onClick={() => !alreadyIn && toggle(h.hoardingID)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 24px',
                    borderBottom: '1px solid #f8f8f8',
                    borderLeft: siteColor ? `4px solid ${siteColor.border}` : '4px solid transparent',
                    cursor: alreadyIn ? 'not-allowed' : 'pointer',
                    background: checked ? 'rgba(4,158,223,0.05)' : siteColor ? siteColor.bg : '#fff',
                    opacity: alreadyIn ? 0.55 : 1,
                  }}
                >
                  <div className={`qt-modal-check ${checked ? 'qt-modal-check--on' : ''}`}>
                    {checked && <Check size={12} color="#fff" />}
                  </div>

                  {siteColor && (
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: siteColor.dot, flexShrink: 0,
                    }} />
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="pg-td__primary" style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      <MapPin size={11} color="#9090a8" style={{ flexShrink: 0, marginTop: 4 }} />
                      <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                        {line1 || h.hoardingCode}
                        {alreadyIn && <span style={{ color: '#9090a8', fontSize: 11, marginLeft: 6, fontWeight: 500, whiteSpace: 'nowrap' }}>· Already added</span>}
                      </span>
                    </div>
                    {line2 && (
                      <div style={{
                        fontFamily: 'Nunito,sans-serif', fontSize: 11,
                        color: '#7a8499', marginTop: 1,
                        wordBreak: 'break-word', whiteSpace: 'normal'
                      }}>
                        {line2}
                      </div>
                    )}
                    {/* ✅ MODIFIED: Elevated hoarding size styling to make it larger and more visible */}
                    <div className="pg-td__secondary" style={{ wordBreak: 'break-word', whiteSpace: 'normal', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      <span>Code: {h.hoardingCode}</span>
                      <span>·</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#049edf', background: 'rgba(4,158,223,0.08)', padding: '2px 8px', borderRadius: 6, border: '1px solid rgba(4,158,223,0.18)' }}>
                        {h.width}×{h.height} ft
                      </span>
                      <span>·</span>
                      <span>₹{Number(h.monthlyRent || 0).toLocaleString('en-IN')}/mo</span>
                    </div>
                  </div>

                  <span style={{
                    fontSize: 10.5, fontWeight: 700, padding: '2px 8px',
                    borderRadius: 5, flexShrink: 0,
                    background: 'rgba(22,163,74,0.10)',
                    color: '#16a34a',
                    border: '1px solid rgba(22,163,74,0.2)',
                  }}>
                    Available
                  </span>
                </div>
              );
            })
          )}
        </div>

        <div className="pg-modal__foot" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{
            fontFamily: 'Nunito,sans-serif', fontSize: 12.5,
            color: '#9090a8', fontWeight: 600,
          }}>
            {selected.size} selected
          </span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="pg-btn-cancel" onClick={onClose}>Cancel</button>
            <button
              className="pg-btn-save"
              onClick={() => onAdd(selected)}
              disabled={selected.size === 0 || loading || !!error}
            >
              <Plus size={14} /> Add {selected.size > 0 ? `(${selected.size})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════
   EXTERNAL HOARDING SELECT MODAL
═══════════════════════════════════════════ */
function ExternalHoardingSelectModal({ allHoardings, existingIds, onAdd, onClose, siteColorMap, siteMap, outsideSiteMap, startDate, endDate, showToast, changeTab }) {
  const [hoardingsList, setHoardingsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [mappedHoardingIDs, setMappedHoardingIDs] = useState(new Set()); // ✅ MODIFIED: Track mapped external hoardings
  const [showAddNewModal, setShowAddNewModal] = useState(() => {
    return (
      sessionStorage.getItem('reopen_quotation_external_hoarding_modal') === 'true' ||
      sessionStorage.getItem('unsaved_add_external_hoarding_form') !== null ||
      sessionStorage.getItem('newly_created_external_site_id') !== null
    );
  });

  useEffect(() => {
    if (
      sessionStorage.getItem('reopen_quotation_external_hoarding_modal') === 'true' ||
      sessionStorage.getItem('unsaved_add_external_hoarding_form') !== null ||
      sessionStorage.getItem('newly_created_external_site_id') !== null
    ) {
      setShowAddNewModal(true);
    }
  }, []);

  const handleClose = () => {
    sessionStorage.removeItem('reopen_quotation_external_hoarding_modal');
    sessionStorage.removeItem('unsaved_add_external_hoarding_form');
    sessionStorage.removeItem('newly_created_external_site_id');
    onClose();
  };

  const fetchExternal = useCallback(async () => {
    if (!startDate || !endDate) {
      setLoading(false);
      setError('Global period dates are not set.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // ✅ Fetch available hoardings and vendor details in parallel
      const [res, vendorsRes] = await Promise.all([
        apiService.getAvailableHoardings(startDate, endDate),
        apiService.getAllVendors().catch(err => {
          console.error('Failed to load vendors:', err);
          return [];
        })
      ]);
      const list = normalizeList(res);
      const vendors = Array.isArray(vendorsRes) ? vendorsRes
        : Array.isArray(vendorsRes?.data) ? vendorsRes.data
          : Array.isArray(vendorsRes?.$values) ? vendorsRes.$values : [];

      const tempMapped = new Set();
      vendors.forEach(v => {
        (v.hoardingId || v.HoardingId || []).forEach(id => tempMapped.add(Number(id)));
      });

      setMappedHoardingIDs(tempMapped);
      // Sort by effdt descending first to keep the latest version
      const sortedList = [...list].sort((a, b) => new Date(b.effdt || b.Effdt) - new Date(a.effdt || a.Effdt));

      // Deduplicate by hoardingID
      const seen = new Set();
      const uniqueList = [];
      for (const item of sortedList) {
        const hid = item.hoardingID ?? item.HoardingID ?? item.hoardingId;
        if (hid && !seen.has(hid)) {
          seen.add(hid);
          uniqueList.push(item);
        }
      }

      // Filter: only keep items where isExternal is true
      const filteredExternal = uniqueList.filter(item => {
        const full = allHoardings.find(h => h.hoardingID === (item.hoardingId ?? item.hoardingID));
        const isExt = item.isExternal ?? item.isexternal ?? item.IsExternal ?? full?.isExternal ?? full?.isexternal ?? full?.IsExternal ?? false;
        return isExt === true || String(isExt).toLowerCase() === 'true';
      });

      const mapped = filteredExternal.map(item => {
        const hid = item.hoardingID ?? item.HoardingID ?? item.hoardingId;
        const full = allHoardings.find(h => h.hoardingID === hid);
        const sid = toSID(item.siteID ?? item.SiteID ?? item.siteId ?? item.outsideSiteID ?? full?.siteID);
        const siteObj = getHoardingSite({ ...item, ...full, isExternal: true, siteID: sid }, siteMap, outsideSiteMap);
        return {
          hoardingID: hid,
          hoardingCode: item.hoardingCode ?? full?.hoardingCode ?? item.HoardingCode ?? '',
          monthlyRent: item.monthlyRent ?? full?.monthlyRent ?? item.MonthlyRent ?? 0,
          width: item.width ?? full?.width ?? item.Width ?? 0,
          height: item.height ?? full?.height ?? item.Height ?? 0,
          status: 'Available',
          isExternal: true,
          siteID: sid ?? null,
          site: siteObj || full?.site || {
            addressLine1: item.addressLine1 || '',
            city: item.city || '',
            district: item.district || '',
            siteType: item.material || '',
          }
        };
      });
      setHoardingsList(mapped);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Failed to load external hoardings.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, siteMap, outsideSiteMap, allHoardings]);

  useEffect(() => {
    fetchExternal();
  }, [fetchExternal]);

  const handleHoardingCreated = (newHoarding, vendorID) => {
    setShowAddNewModal(false);
    if (newHoarding && newHoarding.hoardingID) {
      // Auto-select the newly created hoarding
      setSelected(prev => new Set([...prev, newHoarding.hoardingID]));
    }
    // Refresh list and vendor mapping
    fetchExternal();
  };

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return s ? hoardingsList.filter(h =>
      (h.hoardingCode || '').toLowerCase().includes(s) ||
      (h.site?.addressLine1 || '').toLowerCase().includes(s) ||
      (h.site?.city || '').toLowerCase().includes(s)
    ) : hoardingsList;
  }, [hoardingsList, search]);

  const selectable = filtered.filter(h => !existingIds.has(h.hoardingID));
  const tyrannicalSelectable = selectable;
  const allSelected = tyrannicalSelectable.length > 0 && tyrannicalSelectable.every(h => selected.has(h.hoardingID));
  const someSelected = tyrannicalSelectable.some(h => selected.has(h.hoardingID));

  const toggle = (id) => setSelected(p => {
    const n = new Set(p);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const toggleAll = () => {
    if (allSelected) {
      setSelected(p => { const n = new Set(p); tyrannicalSelectable.forEach(h => n.delete(h.hoardingID)); return n; });
    } else {
      setSelected(p => { const n = new Set(p); tyrannicalSelectable.forEach(h => n.add(h.hoardingID)); return n; });
    }
  };

  return ReactDOM.createPortal(
    <div className="pg-overlay">
      <div className="pg-modal" style={{ maxWidth: 660, display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>
        <div className="pg-modal__head" style={{ flexShrink: 0 }}>
          <div className="pg-modal__head-left">
            <div className="pg-modal__icon-wrap" style={{ background: 'rgba(22,163,74,0.10)' }}><Building2 size={20} color="#16a34a" /></div>
            <div>
              <h5 className="pg-modal__title">Select External Hoardings</h5>
              <p className="pg-modal__subtitle">{loading ? 'Loading...' : `${hoardingsList.length} external hoardings`} · Colour-coded by site</p>
            </div>
          </div>
          <button className="pg-modal__close" onClick={handleClose}><X size={15} /></button>
        </div>

        <div style={{ padding: '12px 24px', borderBottom: '1px solid #f0f0f8', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="pg-search-box" style={{ flex: 1 }}>
              <Search size={13} color="#c0c0d8" style={{ flexShrink: 0 }} />
              <input
                placeholder="Search by code, site address…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                disabled={loading || !!error}
              />
              {search && <X size={12} className="pg-search-clear" onClick={() => setSearch('')} />}
            </div>
            <button
              type="button"
              onClick={() => setShowAddNewModal(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '9px 15px',
                borderRadius: 10,
                border: 'none',
                background: 'linear-gradient(135deg,#049edf,#6c63ff)',
                color: '#fff',
                fontFamily: 'Nunito,sans-serif',
                fontSize: 12.5,
                fontWeight: 800,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                boxShadow: '0 2px 8px rgba(4,158,223,0.25)',
                flexShrink: 0,
              }}
            >
              <Plus size={14} /> Add New Hoarding
            </button>
          </div>
        </div>

        {!loading && !error && tyrannicalSelectable.length > 0 && (
          <div className="qt-select-all-row" style={{ flexShrink: 0 }} onClick={toggleAll}>
            <div className={`qt-modal-check ${allSelected ? 'qt-modal-check--all' : someSelected ? 'qt-modal-check--on' : ''}`}>
              {allSelected ? <Check size={12} color="#fff" /> : someSelected ? <div style={{ width: 8, height: 2, background: '#16a34a', borderRadius: 2 }} /> : null}
            </div>
            <span>{allSelected ? 'Deselect All' : `Select All (${tyrannicalSelectable.length})`}</span>
          </div>
        )}

        <div style={{ flex: '1 1 auto', overflowY: 'auto', maxHeight: 360, minHeight: 0 }}>
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', gap: 8 }}>
              <Loader2 size={24} className="pg-spin" color="#16a34a" />
              <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, color: '#9090a8' }}>Loading external hoardings…</span>
            </div>
          ) : error ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', color: '#dc2626', fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 600 }}>
              {error}
            </div>
          ) : filtered.length === 0 ? (
            <div className="pg-empty__inner" style={{ padding: '32px 20px' }}>
              <Building2 size={32} color="#d0d0e8" />
              <span className="pg-empty__label">No external hoardings found</span>
            </div>
          ) : (
            filtered.map(h => {
              const checked = selected.has(h.hoardingID);
              const alreadyIn = existingIds.has(h.hoardingID);
              const sid = h.siteID ?? h.site?.siteID ?? h.site?.outsideSiteID;
              const siteColor = toSID(sid) != null ? siteColorMap.get(toSID(sid)) : null;
              const resolvedSite = getHoardingSite(h, siteMap, outsideSiteMap);
              const { line1, line2 } = getSiteDisplayLines(resolvedSite, h.hoardingCode);

              return (
                <div
                  key={h.hoardingID}
                  onClick={() => !alreadyIn && toggle(h.hoardingID)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 24px',
                    borderBottom: '1px solid #f8f8f8',
                    borderLeft: siteColor ? `4px solid ${siteColor.border}` : '4px solid transparent',
                    cursor: alreadyIn ? 'not-allowed' : 'pointer',
                    background: checked ? 'rgba(22,163,74,0.05)' : siteColor ? siteColor.bg : '#fff',
                    opacity: alreadyIn ? 0.55 : 1,
                  }}
                >
                  <div className={`qt-modal-check ${checked ? 'qt-modal-check--on' : ''}`}>
                    {checked && <Check size={12} color="#fff" />}
                  </div>

                  {siteColor && (
                    <div style={{
                      width: 8, height: 8, borderRadius: '50%',
                      background: siteColor.dot, flexShrink: 0,
                    }} />
                  )}

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="pg-td__primary" style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
                      <MapPin size={11} color="#9090a8" style={{ flexShrink: 0, marginTop: 4 }} />
                      <span style={{ flex: 1, minWidth: 0, wordBreak: 'break-word', whiteSpace: 'normal' }}>
                        {line1 || h.hoardingCode}
                        {alreadyIn && <span style={{ color: '#9090a8', fontSize: 11, marginLeft: 6, fontWeight: 500, whiteSpace: 'nowrap' }}>· Already added</span>}
                      </span>
                    </div>
                    {line2 && (
                      <div style={{
                        fontFamily: 'Nunito,sans-serif', fontSize: 11,
                        color: '#7a8499', marginTop: 1,
                        wordBreak: 'break-word', whiteSpace: 'normal'
                      }}>
                        {line2}
                      </div>
                    )}
                    {/* ✅ MODIFIED: Elevated hoarding size styling to make it larger and more visible */}
                    <div className="pg-td__secondary" style={{ wordBreak: 'break-word', whiteSpace: 'normal', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      <span>Code: {h.hoardingCode}</span>
                      <span>·</span>
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#049edf', background: 'rgba(4,158,223,0.08)', padding: '2px 8px', borderRadius: 6, border: '1px solid rgba(4,158,223,0.18)' }}>
                        {h.width}×{h.height} ft
                      </span>
                      <span>·</span>
                      <span>₹{Number(h.monthlyRent || 0).toLocaleString('en-IN')}/mo</span>
                    </div>
                  </div>

                  <span style={{
                    fontSize: 10.5, fontWeight: 700, padding: '2px 8px',
                    borderRadius: 5, flexShrink: 0,
                    background: 'rgba(22,163,74,0.10)',
                    color: '#16a34a',
                    border: '1px solid rgba(22,163,74,0.2)',
                  }}>
                    {h.status}
                  </span>
                </div>
              );
            })
          )}
        </div>

        <div className="pg-modal__foot" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{
            fontFamily: 'Nunito,sans-serif', fontSize: 12.5,
            color: '#9090a8', fontWeight: 600,
          }}>
            {selected.size} selected
          </span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button className="pg-btn-cancel" onClick={handleClose}>Cancel</button>
            <button
              className="pg-btn-save"
              onClick={() => {
                const selectedObjects = hoardingsList.filter(h => selected.has(h.hoardingID));

                // ✅ MODIFIED: Check if each selected hoarding is mapped to a vendor
                for (const h of selectedObjects) {
                  if (!mappedHoardingIDs.has(Number(h.hoardingID))) {
                    if (showToast) {
                      showToast(`The selected hoarding "${h.hoardingCode || h.hoardingID}" is not mapped with any vendor. Please map first.`, 'error');
                    } else {
                      alert(`The selected hoarding "${h.hoardingCode || h.hoardingID}" is not mapped with any vendor. Please map first.`);
                    }
                    return;
                  }
                }

                onAdd(selectedObjects);
              }}
              disabled={selected.size === 0 || loading || !!error}
              style={{
                background: 'linear-gradient(135deg,#16a34a,#10b981)',
                boxShadow: '0 2px 10px rgba(22,163,74,0.25)',
              }}
            >
              <Plus size={14} /> Add {selected.size > 0 ? `(${selected.size})` : ''}
            </button>
          </div>
        </div>
      </div>

      {showAddNewModal && (
        <AddExternalHoardingModal
          allHoardings={allHoardings}
          onClose={() => setShowAddNewModal(false)}
          onSuccess={handleHoardingCreated}
          showToast={showToast}
          changeTab={changeTab}
        />
      )}
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════
   TERMS MODAL
═══════════════════════════════════════════ */
function TermsModal({ selected, onSelect, onSelectAll, termsList, onClose }) {
  const sorted = useMemo(() => [...termsList].sort((a, b) => (a.order || 0) - (b.order || 0)), [termsList]);

  const allIds = useMemo(() => sorted.map(t => t.termID), [sorted]);
  const isAllSelected = sorted.length > 0 && sorted.every(t => selected.includes(t.termID));
  const isSomeSelected = sorted.some(t => selected.includes(t.termID)) && !isAllSelected;

  const handleSelectAll = () => {
    if (isAllSelected) {
      onSelectAll([]);
    } else {
      onSelectAll(allIds);
    }
  };

  return ReactDOM.createPortal(
    <div className="pg-overlay">
      <div className="pg-modal" style={{ maxWidth: 560, display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>
        <div className="pg-modal__head" style={{ flexShrink: 0 }}>
          <div className="pg-modal__head-left">
            <div className="pg-modal__icon-wrap"><List size={20} color="#049edf" /></div>
            <div>
              <h5 className="pg-modal__title">Terms &amp; Conditions</h5>
              <p className="pg-modal__subtitle">Select terms to display on the print layout.</p>
            </div>
          </div>
          <button className="pg-modal__close" onClick={onClose}><X size={15} /></button>
        </div>

        {sorted.length > 0 && (
          <div className="qt-select-all-row" style={{ flexShrink: 0 }} onClick={handleSelectAll}>
            <div className={`qt-modal-check ${isAllSelected ? 'qt-modal-check--all' : isSomeSelected ? 'qt-modal-check--on' : ''}`}>
              {isAllSelected ? <Check size={12} color="#fff" /> : isSomeSelected ? <div style={{ width: 8, height: 2, background: '#049edf', borderRadius: 2 }} /> : null}
            </div>
            <span>{isAllSelected ? 'Deselect All' : `Select All (${sorted.length})`}</span>
          </div>
        )}

        <div style={{ flex: '1 1 auto', overflowY: 'auto', maxHeight: 440, minHeight: 0, padding: '16px 24px' }}>
          {sorted.length === 0 && (
            <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, color: '#9090a8', textAlign: 'center', padding: '28px 0' }}>
              No terms found. Add them in Customer Terms settings.
            </div>
          )}
          {sorted.map(term => {
            const checked = selected.includes(term.termID);
            return (
              <div key={term.termID} style={{ border: `1.5px solid ${checked ? '#049edf40' : '#f0f0f8'}`, borderRadius: 12, padding: '11px 13px', marginBottom: 10, background: checked ? 'rgba(4,158,223,0.03)' : '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <button onClick={() => onSelect(term.termID)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 3, flexShrink: 0 }}>
                    <div style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${checked ? '#049edf' : '#d0d0e0'}`, background: checked ? '#049edf' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {checked && <Check size={12} color="#fff" />}
                    </div>
                  </button>
                  <div style={{ flex: 1, fontFamily: 'Nunito,sans-serif', fontSize: 12.5, color: '#1a1a2e', fontWeight: 600, lineHeight: 1.5 }}>
                    {term.description}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="pg-modal__foot" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, color: '#9090a8', fontWeight: 600 }}>{selected.length} selected</span>
          <button className="pg-btn-save" onClick={onClose}><Check size={14} /> Done</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
function TermsConfirmModal({ onConfirm, onCancel }) {
  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 99998,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 22, width: '100%', maxWidth: 440,
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)', overflow: 'hidden',
          fontFamily: 'Nunito,sans-serif',
        }}
      >
        {/* Header — Warning / Yellow theme */}
        <div style={{
          background: 'linear-gradient(135deg,#f59e0b,#d97706)',
          padding: '24px 26px 18px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10,
          color: '#fff',
        }}>
          <div style={{
            width: 56, height: 56, borderRadius: '50%',
            background: 'rgba(255,255,255,0.20)', border: '2px solid rgba(255,255,255,0.35)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26,
          }}>⚠️</div>
          <div style={{ fontWeight: 900, fontSize: 18 }}>
            Warning
          </div>
          <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
            Terms &amp; Conditions Not Selected
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '24px 24px 20px', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: 14.5, color: '#374151', lineHeight: 1.6, fontWeight: 700 }}>
            Terms and Conditions are not selected.
          </p>
          <p style={{ margin: '8px 0 0 0', fontSize: 13, color: '#6b7280', lineHeight: 1.5, fontWeight: 600 }}>
            Are you sure you want to proceed without terms?
          </p>
        </div>

        {/* Footer */}
        <div style={{ padding: '16px 24px 20px', borderTop: '1px solid #f0f0f8', display: 'flex', gap: 12 }}>
          <button
            className="pg-btn-cancel"
            onClick={onCancel}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              flex: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '10px 0', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg,#f59e0b,#d97706)',
              color: '#fff',
              fontSize: 13.5, fontWeight: 800,
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(245,158,11,0.25)',
            }}
          >
            Yes, Proceed
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
function HoardingConflictModal({ conflicts, onClose }) {
  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 99998,
        background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(6px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 22, width: '100%', maxWidth: 640,
          maxHeight: '88vh', display: 'flex', flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)', overflow: 'hidden',
        }}
      >
        {/* ── Header — white with amber accent ── */}
        <div style={{
          padding: '22px 24px 18px',
          borderBottom: '1.5px solid #f0f0f8',
          display: 'flex', alignItems: 'flex-start', gap: 16,
          flexShrink: 0,
        }}>
          {/* Icon */}
          <div style={{
            width: 48, height: 48, borderRadius: 14, flexShrink: 0,
            background: '#fffbeb', border: '2px solid #fde68a',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AlertTriangle size={22} color="#d97706" />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontFamily: 'Nunito,sans-serif', fontWeight: 900, fontSize: 18,
              color: '#1a1a2e', lineHeight: 1.1,
            }}>
              Booking Conflicts Detected
            </div>
            <div style={{
              fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 600,
              color: '#7878a0', marginTop: 5, lineHeight: 1.5,
            }}>
              {conflicts.length} hoarding{conflicts.length !== 1 ? 's are' : ' is'} already
              booked under active contracts during your selected dates.
              Please update the hoarding dates in Step 2 before proceeding.
            </div>
          </div>

          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 9, flexShrink: 0, marginTop: 2,
              border: '1.5px solid #e8e8f4', background: '#f8f8fd',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', color: '#7878a0',
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* ── Conflict list ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px 20px' }}>
          {conflicts.map((c, i) => (
            <div
              key={i}
              style={{
                borderRadius: 14, overflow: 'hidden', marginBottom: 12,
                border: '1.5px solid #e8e8f4',
              }}
            >
              {/* Card header — hoarding name + status */}
              <div style={{
                padding: '11px 16px',
                background: 'linear-gradient(135deg,#f8f8fd,#fafafe)',
                borderBottom: '1px solid #f0f0f8',
                display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <div style={{
                  width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                  background: 'rgba(4,158,223,0.08)', border: '1px solid rgba(4,158,223,0.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Building2 size={15} color="#049edf" />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Nunito,sans-serif', fontWeight: 900, fontSize: 14, color: '#1a1a2e' }}>
                    {c.hoardingCode}
                  </div>
                  <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, color: '#9090a8', fontWeight: 600, marginTop: 1 }}>
                    ID #{c.hoardingID} · Contract #{c.contractID}
                  </div>
                </div>
                <span style={{
                  padding: '3px 11px', borderRadius: 20, flexShrink: 0,
                  background: '#fffbeb', border: '1px solid #fde68a',
                  fontFamily: 'Nunito,sans-serif', fontSize: 11, fontWeight: 800, color: '#d97706',
                }}>
                  ⚠ Conflict
                </span>
              </div>

              {/* Card body */}
              <div style={{ padding: '13px 16px', background: '#fff' }}>
                <div style={{ display: 'flex', gap: 0, flexDirection: 'column' }}>

                  {/* Row 1 — date comparison */}
                  <div style={{ display: 'flex', gap: 10, alignItems: 'stretch', marginBottom: 10, flexWrap: 'wrap' }}>
                    {/* Your dates */}
                    <div style={{
                      flex: 1, minWidth: 170,
                      padding: '10px 12px', borderRadius: 10,
                      background: '#fffbeb', border: '1.5px solid #fde68a',
                    }}>
                      <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 10, fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>
                        📋 Your Quotation Dates
                      </div>
                      <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 800, color: '#d97706' }}>
                        {fmtDateDisplay(c.rowStart)}
                        <span style={{ fontWeight: 600, color: '#b0b0c8', margin: '0 6px' }}>→</span>
                        {fmtDateDisplay(c.rowEnd)}
                      </div>
                    </div>

                    {/* VS divider */}
                    <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0, padding: '0 4px' }}>
                      <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, fontWeight: 900, color: '#c0c0d8', letterSpacing: '0.05em' }}>VS</div>
                    </div>

                    {/* Contract dates */}
                    <div style={{
                      flex: 1, minWidth: 170,
                      padding: '10px 12px', borderRadius: 10,
                      background: '#f8f8fd', border: '1.5px solid #e8e8f4',
                    }}>
                      <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 10, fontWeight: 800, color: '#7878a0', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>
                        📄 Existing Contract #{c.contractID}
                      </div>
                      <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 800, color: '#1a1a2e' }}>
                        {fmtDateDisplay(c.contractStart)}
                        <span style={{ fontWeight: 600, color: '#b0b0c8', margin: '0 6px' }}>→</span>
                        {fmtDateDisplay(c.contractEnd)}
                      </div>
                    </div>
                  </div>

                  {/* Row 2 — customer */}
                  <div style={{
                    display: 'inline-flex', alignItems: 'center', gap: 7,
                    padding: '6px 11px', borderRadius: 8,
                    background: 'rgba(4,158,223,0.05)', border: '1px solid rgba(4,158,223,0.15)',
                    alignSelf: 'flex-start',
                  }}>
                    <User size={12} color="#049edf" style={{ flexShrink: 0 }} />
                    <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 700, color: '#049edf' }}>
                      Booked by:
                    </span>
                    <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 800, color: '#1a1a2e' }}>
                      {c.customerName}
                    </span>
                  </div>

                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding: '14px 24px 18px', borderTop: '1.5px solid #f0f0f8',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: '#fafafe', gap: 12, flexShrink: 0, flexWrap: 'wrap',
        }}>
          <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, color: '#9090a8', fontWeight: 600, flex: 1, minWidth: 180 }}>
            Go to Step 2 and update the hoarding dates, or remove the conflicting hoardings.
          </span>
          <button
            onClick={onClose}
            style={{
              padding: '10px 24px', borderRadius: 11, border: 'none',
              background: 'linear-gradient(135deg,#049edf,#6c63ff)',
              color: '#fff', cursor: 'pointer',
              fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 800,
              display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0,
              boxShadow: '0 4px 16px rgba(4,158,223,0.30)',
            }}
          >
            <Check size={14} /> Got It, I'll Fix the Dates
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
function QuotationDeleteConfirmModal({ count, onConfirm, onClose }) {
  const noBtnRef = useRef(null);

  useEffect(() => {
    if (noBtnRef.current) {
      noBtnRef.current.focus();
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 22, width: '100%', maxWidth: 440,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column', padding: '24px 24px 20px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: '#fef2f2', border: '2px solid #fee2e2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Trash2 size={20} color="#ef4444" />
          </div>
          <div>
            <h3 style={{ fontFamily: 'Nunito,sans-serif', fontWeight: 900, fontSize: 18, color: '#1a1a2e', margin: 0 }}>
              Confirm Deletion
            </h3>
            <p style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 600, color: '#7878a0', margin: '4px 0 0' }}>
              This action cannot be undone.
            </p>
          </div>
        </div>

        <div style={{
          fontFamily: 'Nunito,sans-serif', fontSize: 14, fontWeight: 600,
          color: '#4a5568', lineHeight: 1.5, marginBottom: 24,
        }}>
          Are you sure you want to delete the <strong>{count}</strong> selected quotation{count !== 1 ? 's' : ''}?
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12,
        }}>
          {/* Yes button: Red secondary/action style */}
          <button
            onClick={onConfirm}
            style={{
              padding: '10px 20px', borderRadius: 10, border: '1.5px solid #fca5a5',
              background: '#fff', color: '#dc2626', cursor: 'pointer',
              fontFamily: 'Nunito,sans-serif', fontSize: 13.5, fontWeight: 800,
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.target.style.background = '#fef2f2'; }}
            onMouseLeave={e => { e.target.style.background = '#fff'; }}
          >
            Yes, Delete
          </button>

          {/* No/Cancel button: Focused by default, with premium gradient */}
          <button
            ref={noBtnRef}
            onClick={onClose}
            style={{
              padding: '11px 24px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg,#049edf,#6c63ff)',
              color: '#fff', cursor: 'pointer',
              fontFamily: 'Nunito,sans-serif', fontSize: 13.5, fontWeight: 800,
              outline: '3px solid rgba(4,158,223,0.3)',
              boxShadow: '0 4px 12px rgba(108,99,255,0.2)',
            }}
          >
            No, Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
function RowDeleteConfirmModal({ row, onConfirm, onClose }) {
  const cancelBtnRef = useRef(null);

  useEffect(() => {
    if (cancelBtnRef.current) {
      cancelBtnRef.current.focus();
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  if (!row) return null;

  const itemDisplay = row.rowType === 'hoarding'
    ? `hoarding ${row.hoardingCode}`
    : row.location;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 22, width: '100%', maxWidth: 440,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column', padding: '24px 24px 20px',
          animation: 'modalIn 0.24s cubic-bezier(0.22,1,0.36,1) both',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <div style={{
            width: 44, height: 44, borderRadius: 12, flexShrink: 0,
            background: '#fffbeb', border: '2px solid #fde68a',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AlertTriangle size={20} color="#d97706" />
          </div>
          <div>
            <h3 style={{ fontFamily: 'Nunito,sans-serif', fontWeight: 900, fontSize: 18, color: '#1a1a2e', margin: 0 }}>
              Remove Item?
            </h3>
            <p style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 600, color: '#7878a0', margin: '4px 0 0' }}>
              Confirm removing this item from the quotation.
            </p>
          </div>
        </div>

        <div style={{
          fontFamily: 'Nunito,sans-serif', fontSize: 14, fontWeight: 600,
          color: '#4a5568', lineHeight: 1.5, marginBottom: 24,
        }}>
          Are you sure you want to remove <strong>{itemDisplay}</strong>? Any custom rates or adjustments for this item will be lost.
        </div>

        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 12,
        }}>
          <button
            onClick={onConfirm}
            style={{
              padding: '10px 20px', borderRadius: 10, border: '1.5px solid #fca5a5',
              background: '#fff', color: '#dc2626', cursor: 'pointer',
              fontFamily: 'Nunito,sans-serif', fontSize: 13.5, fontWeight: 800,
              transition: 'all 0.2s',
            }}
            onMouseEnter={e => { e.target.style.background = '#fef2f2'; }}
            onMouseLeave={e => { e.target.style.background = '#fff'; }}
          >
            Yes, Remove
          </button>

          <button
            ref={cancelBtnRef}
            onClick={onClose}
            style={{
              padding: '11px 24px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg,#049edf,#6c63ff)',
              color: '#fff', cursor: 'pointer',
              fontFamily: 'Nunito,sans-serif', fontSize: 13.5, fontWeight: 800,
              boxShadow: '0 4px 12px rgba(108,99,255,0.2)',
            }}
          >
            No, Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
function ProformaConfirmModal({ target, onConfirm, onCancel, onClose }) {
  const viewBtnRef = useRef(null);

  useEffect(() => {
    if (viewBtnRef.current) {
      viewBtnRef.current.focus();
    }
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 22, width: '100%', maxWidth: 480,
          boxShadow: '0 20px 60px rgba(0,0,0,0.25)', overflow: 'hidden',
          display: 'flex', flexDirection: 'column', padding: '28px 24px 20px',
        }}
      >
        {/* Header with Warning Icon */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 18 }}>
          <div style={{
            width: 48, height: 48, borderRadius: 14, flexShrink: 0,
            background: '#fffbeb', border: '2px solid #fef3c7',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <AlertTriangle size={24} color="#d97706" />
          </div>
          <div>
            <h3 style={{ fontFamily: 'Nunito,sans-serif', fontWeight: 900, fontSize: 18, color: '#1a1a2e', margin: 0 }}>
              Proforma Invoice Exists
            </h3>
            <p style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12.5, fontWeight: 600, color: '#7878a0', margin: '4px 0 0', lineHeight: 1.4 }}>
              A Proforma Invoice has already been generated for this quotation.
            </p>
          </div>
        </div>

        {/* Message body */}
        <div style={{
          fontFamily: 'Nunito,sans-serif', fontSize: 13.5, fontWeight: 600,
          color: '#4a5568', lineHeight: 1.6, marginBottom: 26, background: '#fcfcfd',
          padding: '12px 16px', borderRadius: 12, border: '1px dashed #e2e8f0'
        }}>
          Quotation: <strong style={{ color: '#1a1a2e' }}>{target.quot.quotationNumber || `#${target.quot.quotationID}`}</strong><br />
          Revision: <strong style={{ color: '#1a1a2e' }}>Rev.{target.quot.quotationRevisionNumber ?? 0}</strong><br />
          <span style={{ display: 'block', marginTop: 8 }}>
            Would you like to view/print the existing proforma invoice?
          </span>
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap'
        }}>
          {/* Neutral Close button */}
          <button
            onClick={onClose}
            style={{
              padding: '10px 16px', borderRadius: 10, border: '1.5px solid #e2e8f0',
              background: '#fff', color: '#64748b', cursor: 'pointer',
              fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 800,
              transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.target.style.background = '#f8fafc'; }}
            onMouseLeave={e => { e.target.style.background = '#fff'; }}
          >
            Cancel
          </button>

          {/* View Proforma button */}
          <button
            ref={viewBtnRef}
            onClick={onCancel}
            style={{
              padding: '11.5px 22px', borderRadius: 10, border: 'none',
              background: 'linear-gradient(135deg, #f59e0b, #d97706)',
              color: '#fff', cursor: 'pointer',
              fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 800,
              boxShadow: '0 4px 12px rgba(245,158,11,0.25)',
              transition: 'transform 0.15s',
            }}
            onMouseEnter={e => { e.target.style.transform = 'scale(1.02)'; }}
            onMouseLeave={e => { e.target.style.transform = 'scale(1)'; }}
          >
            View Proforma
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
/* ═══════════════════════════════════════════
   SERIES ALERT MODAL
═══════════════════════════════════════════ */
function SeriesAlertModal({ seriesType, reason, onClose }) {
  useEffect(() => {
    const handleKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const isNotFound = reason === 'notFound';
  const title = isNotFound
    ? `"${seriesType}" Series Not Found`
    : `"${seriesType}" Series is Inactive`;
  const subtitle = isNotFound
    ? 'No number series has been configured for this document type.'
    : 'This number series has been disabled and cannot generate new numbers.';
  const message = isNotFound
    ? `To create Quotations or Proforma Invoices, you first need to configure a number series for "${seriesType}" in the Series Setup page.`
    : `The "${seriesType}" series is currently inactive. Please go to Series Setup and mark it as Active to proceed.`;

  return ReactDOM.createPortal(
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(10px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
      }}
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 24, width: '100%', maxWidth: 460,
          boxShadow: '0 24px 80px rgba(0,0,0,0.22)',
          overflow: 'hidden', display: 'flex', flexDirection: 'column',
          animation: 'slideUpFadeIn 0.22s ease',
        }}
      >
        {/* Coloured top strip */}
        <div style={{
          height: 5,
          background: 'linear-gradient(90deg, #f59e0b, #ef4444)',
        }} />

        <div style={{ padding: '28px 28px 24px' }}>
          {/* Icon + title */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 18 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 16, flexShrink: 0,
              background: 'linear-gradient(135deg, #fff7ed, #fef3c7)',
              border: '2px solid #fde68a',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(245,158,11,0.18)',
            }}>
              <AlertTriangle size={26} color="#d97706" strokeWidth={2.2} />
            </div>
            <div style={{ paddingTop: 2 }}>
              <h3 style={{
                fontFamily: 'Nunito, sans-serif', fontWeight: 900,
                fontSize: 17, color: '#1e293b', margin: 0, lineHeight: 1.2,
              }}>
                {title}
              </h3>
              <p style={{
                fontFamily: 'Nunito, sans-serif', fontWeight: 600,
                fontSize: 12.5, color: '#94a3b8', margin: '5px 0 0', lineHeight: 1.4,
              }}>
                {subtitle}
              </p>
            </div>
          </div>

          {/* Message body */}
          <div style={{
            background: '#fffbeb',
            border: '1.5px solid #fde68a',
            borderRadius: 14, padding: '14px 16px', marginBottom: 22,
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <Info size={16} color="#d97706" style={{ flexShrink: 0, marginTop: 2 }} />
            <p style={{
              fontFamily: 'Nunito, sans-serif', fontWeight: 700,
              fontSize: 13.5, color: '#92400e', margin: 0, lineHeight: 1.65,
            }}>
              {message}
            </p>
          </div>

          {/* Steps hint */}
          <div style={{
            background: '#f8fafc', borderRadius: 12, padding: '12px 16px',
            marginBottom: 24, border: '1px solid #e2e8f0',
          }}>
            <p style={{
              fontFamily: 'Nunito, sans-serif', fontWeight: 800,
              fontSize: 12, color: '#64748b', margin: '0 0 8px', letterSpacing: 0.4,
              textTransform: 'uppercase',
            }}>How to fix:</p>
            <ol style={{
              margin: 0, paddingLeft: 18,
              fontFamily: 'Nunito, sans-serif', fontWeight: 700,
              fontSize: 13, color: '#475569', lineHeight: 1.7,
            }}>
              <li>Open <strong style={{ color: '#1e293b' }}>Series Setup</strong> from the sidebar</li>
              {isNotFound
                ? <li>Create a new series with type <strong style={{ color: '#1e293b' }}>"{seriesType}"</strong></li>
                : <li>Find the <strong style={{ color: '#1e293b' }}>"{seriesType}"</strong> row and set it to <strong style={{ color: '#059669' }}>Active</strong></li>
              }
              <li>Come back and try again</li>
            </ol>
          </div>

          {/* Button */}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={onClose}
              autoFocus
              style={{
                padding: '11px 28px', borderRadius: 12, border: 'none',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: '#fff', cursor: 'pointer',
                fontFamily: 'Nunito, sans-serif', fontSize: 14, fontWeight: 800,
                boxShadow: '0 4px 14px rgba(245,158,11,0.35)',
                transition: 'all 0.18s',
              }}
              onMouseEnter={e => { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 6px 18px rgba(245,158,11,0.45)'; }}
              onMouseLeave={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 4px 14px rgba(245,158,11,0.35)'; }}
            >
              Got it, I'll fix it
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
/* ═══════════════════════════════════════════
   MERGE HOARDING MODAL
═══════════════════════════════════════════ */
function MergeModal({ rows, onMerge, onClose, siteColorMap }) {
  const hoardingRows = rows.filter(r => r.rowType === 'hoarding');
  const [sel, setSel] = useState([]);
  const [dir, setDir] = useState('V');

  const isExtRow = (r) => Boolean(
    r?.isExternal === true ||
    String(r?.isExternal).toLowerCase() === 'true' ||
    r?.is_External === true ||
    String(r?.is_External).toLowerCase() === 'true' ||
    r?.IsExternal === true ||
    String(r?.IsExternal).toLowerCase() === 'true' ||
    r?.Is_External === true ||
    String(r?.Is_External).toLowerCase() === 'true'
  );

  const firstSelectedRow = sel.length > 0
    ? hoardingRows.find(r => r._id === sel[0])
    : null;
  const firstSiteID = firstSelectedRow ? (firstSelectedRow.siteID ?? null) : undefined;
  const firstIsExternal = firstSelectedRow ? isExtRow(firstSelectedRow) : undefined;

  // Toggle selection
  const toggle = (id) => {
    const row = hoardingRows.find(r => r._id === id);
    if (!row) return;
    if (!sel.includes(id)) {
      if (firstSiteID !== undefined && row.siteID !== firstSiteID) return;
      if (firstIsExternal !== undefined && isExtRow(row) !== firstIsExternal) return;
    }
    setSel(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const preview = useMemo(() => {
    if (sel.length < 2) return null;
    const selRows = sel.map(id => rows.find(r => r._id === id)).filter(Boolean);
    const sizes = selRows.map(r => parseSize(r.size));
    const gaps = selRows.length - 1;
    let mw, mh;
    if (dir === 'S') {
      const singleW = sizes[0]?.w || 0;
      const singleH = sizes[0]?.h || 0;
      mw = (singleW * 2) + 1;
      mh = (singleH * 2) + 1;
    } else if (dir === 'H') {
      mw = sizes.reduce((s, sz) => s + sz.w, 0) + gaps;
      mh = Math.max(...sizes.map(s => s.h));
    } else {
      mw = Math.max(...sizes.map(s => s.w));
      mh = sizes.reduce((s, sz) => s + sz.h, 0) + gaps;
    }
    return { size: `${mw} × ${mh} ft`, sqFt: (mw * mh).toFixed(1), count: selRows.length };
  }, [sel, dir, rows]);

  const { canMerge, validationError } = useMemo(() => {
    if (sel.length === 0) {
      return { canMerge: false, validationError: null };
    }
    const selRows = sel.map(id => rows.find(r => r._id === id)).filter(Boolean);

    // Validate isExternal consistency
    const firstExt = isExtRow(selRows[0]);
    const allSameExternal = selRows.every(r => isExtRow(r) === firstExt);
    if (!allSameExternal) {
      return {
        canMerge: false,
        validationError: 'Cannot merge internal and external hoardings together.'
      };
    }

    if (dir === 'S') {
      if (sel.length !== 4) {
        return {
          canMerge: false,
          validationError: `Square merge requires exactly 4 hoardings (${sel.length} currently selected).`
        };
      }
      const sizes = selRows.map(r => parseSize(r.size));
      const firstW = sizes[0]?.w;
      const firstH = sizes[0]?.h;
      const allSameSize = sizes.every(s => s.w === firstW && s.h === firstH);
      if (!allSameSize) {
        return {
          canMerge: false,
          validationError: 'All 4 selected hoardings must be the exact same size (width and height).'
        };
      }
      return { canMerge: true, validationError: null };
    }

    if (sel.length < 2) {
      return { canMerge: false, validationError: 'Select at least 2 hoardings to merge.' };
    }

    return { canMerge: true, validationError: null };
  }, [sel, dir, rows]);

  const siteGroups = useMemo(() => {
    const map = new Map();
    for (const r of hoardingRows) {
      const isExt = isExtRow(r);
      const sid = r.siteID ?? '__none__';
      const groupKey = `${sid}__${isExt ? 'EXT' : 'INT'}`;
      if (!map.has(groupKey)) {
        const site = r.siteObj;
        const baseLabel = site
          ? [site.addressLine1 || site.city, site.city, site.district]
            .filter(Boolean).filter((v, i, a) => a.indexOf(v) === i).join(', ')
          : (sid === '__none__' ? 'Unknown Site' : `Site ${sid}`);
        const label = isExt ? `${baseLabel} (External)` : baseLabel;
        map.set(groupKey, { label, siteID: r.siteID, isExternal: isExt, rows: [] });
      }
      map.get(groupKey).rows.push(r);
    }
    return [...map.values()];
  }, [hoardingRows]);

  if (hoardingRows.length < 2) {
    return ReactDOM.createPortal(
      <div className="pg-overlay">
        <div className="pg-modal" style={{ maxWidth: 440 }}>
          <div className="pg-modal__head">
            <div className="pg-modal__head-left">
              <div className="pg-modal__icon-wrap" style={{ background: 'rgba(124,58,237,0.10)' }}><Link2 size={20} color="#7c3aed" /></div>
              <div><h5 className="pg-modal__title">Merge Hoardings</h5></div>
            </div>
            <button className="pg-modal__close" onClick={onClose}><X size={15} /></button>
          </div>
          <div style={{ padding: '32px 24px', textAlign: 'center', fontFamily: 'Nunito,sans-serif', fontSize: 13, color: '#9090a8' }}>
            <Building2 size={36} color="#d0d0e8" style={{ marginBottom: 12 }} />
            <p>You need at least <strong>2 hoarding rows from the same site</strong> before merging.</p>
          </div>
          <div className="pg-modal__foot">
            <button className="pg-btn-cancel" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return ReactDOM.createPortal(
    <div className="pg-overlay">
      <div className="pg-modal qt-merge-modal" style={{ maxWidth: 640, width: '95%', display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }}>
        <div className="pg-modal__head" style={{ flexShrink: 0, padding: '14px 20px' }}>
          <div className="pg-modal__head-left">
            <div className="pg-modal__icon-wrap" style={{ background: 'rgba(124,58,237,0.10)', width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Link2 size={18} color="#7c3aed" />
            </div>
            <div>
              <h5 className="pg-modal__title" style={{ fontSize: 16, fontWeight: 800 }}>Merge Hoardings</h5>
              <p className="pg-modal__subtitle" style={{ fontSize: 11.5, color: '#9090a8', marginTop: 2 }}>
                {dir === 'S'
                  ? <>Select <strong>exactly 4</strong> same-size hoardings from the <strong>same site</strong> (all internal or all external)</>
                  : <>Select <strong>2 or more</strong> hoardings from the <strong>same site</strong> (all internal or all external)</>
                }
              </p>
            </div>
          </div>
          <button className="pg-modal__close" onClick={onClose}><X size={15} /></button>
        </div>

        {/* Scrollable Modal Body */}
        <div className="qt-merge-modal-body" style={{ flex: '1 1 auto', overflowY: 'auto', padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: 12, minHeight: 0 }}>
          {/* Direction */}
          <div className="qt-merge-pad-custom" style={{ flexShrink: 0 }}>
            <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 800, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: 6 }}>
              Merge Direction
            </div>
            <div className="qt-merge-dir-container" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {[
                { val: 'H', label: 'Horizontal', sub: 'Side by side · sum(widths)', icon: '↔' },
                { val: 'V', label: 'Vertical', sub: 'Top to bottom · sum(heights)', icon: '↕' },
                { val: 'S', label: 'Square', sub: '2×2 grid · exact 4 same-size', icon: '⊞' },
              ].map(({ val, label, sub, icon }) => {
                const isActive = dir === val;
                return (
                  <button key={val} onClick={() => setDir(val)}
                    type="button"
                    className={`qt-merge-dir-btn ${isActive ? 'active' : ''}`}
                    style={{
                      padding: '8px 10px', borderRadius: 10, cursor: 'pointer', textAlign: 'left',
                      border: `1.5px solid ${isActive ? '#7c3aed' : '#e2e8f0'}`,
                      background: isActive ? '#f5f3ff' : '#ffffff',
                      boxShadow: isActive ? '0 0 0 1px #7c3aed inset' : 'none',
                      transition: 'all 0.15s ease',
                      fontFamily: 'Nunito,sans-serif',
                      display: 'flex', flexDirection: 'column', gap: 2
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span className="qt-merge-dir-icon" style={{ fontSize: 14, fontWeight: 900, color: isActive ? '#7c3aed' : '#64748b' }}>{icon}</span>
                      <span className="qt-merge-dir-label" style={{ fontSize: 12.5, fontWeight: 800, color: isActive ? '#7c3aed' : '#1e293b' }}>{label}</span>
                    </div>
                    <div className="qt-merge-dir-sub" style={{ fontSize: 10, color: isActive ? '#6d28d9' : '#94a3b8', lineHeight: 1.2 }}>{sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Hoardings Selection */}
          <div style={{ display: 'flex', flexDirection: 'column', flex: '1 1 auto', minHeight: 0 }}>
            <div className="qt-merge-pad-custom-top" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexShrink: 0 }}>
              <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 800, color: '#4b5563', textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                Select Hoardings
              </span>
              <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 700, color: sel.length >= 2 ? '#7c3aed' : '#9090a8' }}>
                {sel.length} selected — {dir === 'S' ? 'exactly 4 required' : 'min. 2 required'}
              </span>
            </div>

            <div className="qt-merge-scroll-area" style={{ maxHeight: 260, minHeight: 130, overflowY: 'auto', border: '1px solid #eef0f6', borderRadius: 10, padding: '8px 10px', background: '#fbfbff' }}>
              {siteGroups.map(group => {
                const groupColor = group.siteID != null ? siteColorMap.get(group.siteID) : null;
                const siteMismatch = firstSiteID !== undefined && group.siteID !== firstSiteID;
                const extMismatch = firstIsExternal !== undefined && group.isExternal !== firstIsExternal;
                const groupLocked = siteMismatch || extMismatch;
                const lockReason = siteMismatch
                  ? '✕ Different site'
                  : (group.isExternal ? '✕ External hoardings' : '✕ Internal hoardings');
                return (
                  <div key={`${String(group.siteID ?? '__none__')}_${group.isExternal ? 'ext' : 'int'}`} style={{ marginBottom: 10 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5,
                      padding: '4px 8px', borderRadius: 6,
                      background: groupLocked ? '#f8f8f8' : (groupColor ? groupColor.bg : '#f4f4fb'),
                      border: `1px solid ${groupLocked ? '#e8e8f0' : (groupColor ? groupColor.border : '#e8e8f4')}`,
                      opacity: groupLocked ? 0.5 : 1,
                      flexWrap: 'wrap',
                    }}>
                      {groupColor && !groupLocked && (
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: groupColor.dot, flexShrink: 0 }} />
                      )}
                      <MapPin size={11} color={groupLocked ? '#c0c0d8' : (groupColor?.dot || '#9090a8')} />
                      <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 800, color: groupLocked ? '#b0b0c8' : '#1a1a2e' }}>
                        {group.label}
                      </span>
                      {group.isExternal && (
                        <span style={{
                          fontFamily: 'Nunito,sans-serif', fontSize: 9.5, fontWeight: 800,
                          padding: '1px 5px', borderRadius: 3,
                          background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a'
                        }}>
                          External
                        </span>
                      )}
                      {groupLocked && (
                        <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 10.5, color: '#dc2626', marginLeft: 'auto', fontWeight: 700 }}>
                          {lockReason}
                        </span>
                      )}
                    </div>

                    {group.rows.map(r => {
                      const checked = sel.includes(r._id);
                      const isExternalRow = isExtRow(r);
                      const extMismatch = firstIsExternal !== undefined && isExternalRow !== firstIsExternal;
                      const siteMismatch = firstSiteID !== undefined && r.siteID !== firstSiteID;
                      const disabled = groupLocked || extMismatch || siteMismatch;
                      const { line1, line2 } = getSiteDisplayLines(r.siteObj, r.hoardingCode);
                      const selIdx = sel.indexOf(r._id);
                      return (
                        <div key={r._id}
                          onClick={() => !disabled && toggle(r._id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '7px 10px', borderRadius: 8, marginBottom: 5,
                            cursor: disabled ? 'not-allowed' : 'pointer',
                            border: `1.5px solid ${checked ? '#7c3aed' : '#e8e8f0'}`,
                            background: checked ? '#f5f3ff' : (groupLocked ? '#f8f8f8' : '#ffffff'),
                            opacity: disabled ? 0.45 : 1,
                            transition: 'all 0.15s ease',
                          }}
                        >
                          <div style={{
                            width: 18, height: 18, borderRadius: 5, flexShrink: 0,
                            border: `2px solid ${checked ? '#7c3aed' : '#cbd5e1'}`,
                            background: checked ? '#7c3aed' : '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            {checked && <Check size={11} color="#fff" strokeWidth={3} />}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 800, color: groupLocked ? '#b0b0c8' : '#1e293b' }}>
                                {line1 || r.hoardingCode}
                              </span>
                              {isExternalRow && (
                                <span style={{
                                  fontFamily: 'Nunito,sans-serif', fontSize: 9, fontWeight: 800,
                                  padding: '1px 4px', borderRadius: 3,
                                  background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a'
                                }}>
                                  External
                                </span>
                              )}
                            </div>
                            {line2 && (
                              <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 10.5, color: '#64748b', marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{line2}</div>
                            )}
                            <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 10.5, color: '#94a3b8', marginTop: 1 }}>
                              Code: <strong style={{ color: '#475569' }}>{r.hoardingCode}</strong> · Size: <strong style={{ color: '#475569' }}>{r.size}</strong> · <strong style={{ color: '#475569' }}>{r.sqFt} sq.ft</strong>
                            </div>
                          </div>
                          {checked && (
                            <div style={{
                              fontFamily: 'Nunito,sans-serif', fontSize: 10.5, fontWeight: 800,
                              padding: '2px 7px', borderRadius: 5, flexShrink: 0,
                              background: '#ede9fe', color: '#7c3aed',
                              border: '1px solid #ddd6fe',
                            }}>
                              #{selIdx + 1}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Preview */}
          {preview && (
            <div className="qt-merge-preview-block" style={{ padding: '8px 12px', borderRadius: 8, background: '#f5f3ff', border: '1px solid #ddd6fe', flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 800, color: '#7c3aed', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Link2 size={13} /> Merge Preview ({preview.count} hoardings):
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 10.5, color: '#64748b' }}>Size:</span>
                    <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12.5, fontWeight: 900, color: '#1e293b' }}>{preview.size}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 10.5, color: '#64748b' }}>Area:</span>
                    <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12.5, fontWeight: 900, color: '#1e293b' }}>{preview.sqFt} sq.ft</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 10.5, color: '#64748b' }}>Dir:</span>
                    <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 800, color: '#7c3aed' }}>
                      {dir === 'S' ? '⊞ Square' : (dir === 'H' ? '↔ Horizontal' : '↕ Vertical')}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Validation Error Banner */}
          {validationError && (
            <div style={{
              padding: '6px 10px',
              borderRadius: 6,
              background: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              fontFamily: 'Nunito,sans-serif',
              fontSize: 11.5,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              flexShrink: 0
            }}>
              <AlertTriangle size={13} color="#dc2626" />
              <span>{validationError}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="pg-modal__foot qt-merge-foot-container" style={{ padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0, borderTop: '1px solid #f0f0f8' }}>
          <div className="qt-merge-foot-text" style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, color: '#64748b', fontWeight: 600 }}>
            {dir === 'S'
              ? (sel.length === 4 ? '4 hoardings selected for Square Merge' : 'Square merge requires exactly 4 hoardings')
              : (sel.length < 2 ? 'Select at least 2 hoardings' : `${sel.length} hoardings will be merged`)}
          </div>
          <div className="qt-merge-foot-actions" style={{ display: 'flex', gap: 10 }}>
            <button className="pg-btn-cancel" onClick={onClose} style={{ padding: '8px 16px', borderRadius: 8, fontSize: 12.5, fontWeight: 700 }}>Cancel</button>
            <button
              disabled={!canMerge}
              onClick={() => {
                if (!canMerge) return;
                const selectedRowData = sel.map(id => rows.find(r => r._id === id)).filter(Boolean);
                onMerge(selectedRowData, dir);
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 18px', borderRadius: 8, border: 'none',
                background: canMerge ? '#7c3aed' : '#d0d0e0',
                color: '#fff', cursor: canMerge ? 'pointer' : 'not-allowed',
                fontFamily: 'Nunito,sans-serif', fontSize: 12.5, fontWeight: 800,
                transition: 'background 0.15s ease'
              }}
            >
              <Link2 size={13} /> Merge {sel.length >= 2 ? `(${sel.length})` : ''}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════
   MERGED HOARDINGS VIEW MODAL
   Show details of individual hoardings that are merged together in a row.
═══════════════════════════════════════════ */
function MergedHoardingsViewModal({ row, hoardings, siteMap, outsideSiteMap, onClose }) {
  const mergedItems = useMemo(() => {
    return (row.mergedHoardingIDs || [])
      .map(hid => hoardings.find(h => h.hoardingID === hid))
      .filter(Boolean);
  }, [row, hoardings]);

  return ReactDOM.createPortal(
    // <div className="pg-overlay" onClick={onClose}>
    <div className="pg-overlay">
      <div className="pg-modal" style={{ maxWidth: 660, display: 'flex', flexDirection: 'column', maxHeight: '90vh', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
        <div className="pg-modal__head" style={{ flexShrink: 0 }}>
          <div className="pg-modal__head-left">
            <div className="pg-modal__icon-wrap" style={{ background: 'rgba(124,58,237,0.10)' }}>
              <Link2 size={20} color="#7c3aed" />
            </div>
            <div>
              <h5 className="pg-modal__title">Merged Hoardings Details</h5>
              <p className="pg-modal__subtitle">
                {row.mergeDirection === 'S' ? 'Square' : (row.mergeDirection === 'H' ? 'Horizontal' : 'Vertical')} Merge · Size: {row.size} · {row.sqFt} sq.ft
              </p>
            </div>
          </div>
          <button className="pg-modal__close" onClick={onClose}><X size={15} /></button>
        </div>

        <div style={{ padding: '20px 24px', flex: '1 1 auto', overflowY: 'auto', minHeight: 0 }}>
          <div style={{
            fontFamily: 'Nunito,sans-serif', fontSize: 13.5, fontWeight: 700,
            color: '#1a1a2e', marginBottom: 12
          }}>
            This merged row is composed of the following {mergedItems.length} hoarding(s):
          </div>

          <div style={{ overflowX: 'auto', border: '1px solid #e8e8f4', borderRadius: 12 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f8f8fd', borderBottom: '1px solid #e8e8f4' }}>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 800, color: '#7878a0', fontSize: 11, textTransform: 'uppercase', width: 40 }}>#</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 800, color: '#7878a0', fontSize: 11, textTransform: 'uppercase', width: 90 }}>Code</th>
                  <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 800, color: '#7878a0', fontSize: 11, textTransform: 'uppercase' }}>Site Address</th>
                  <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 800, color: '#7878a0', fontSize: 11, textTransform: 'uppercase', width: 90 }}>Size</th>
                  <th style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 800, color: '#7878a0', fontSize: 11, textTransform: 'uppercase', width: 110 }}>Rent/Mo</th>
                </tr>
              </thead>
              <tbody>
                {mergedItems.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '20px', textAlign: 'center', color: '#9090a8', fontFamily: 'Nunito,sans-serif' }}>
                      No hoarding details found.
                    </td>
                  </tr>
                ) : (
                  mergedItems.map((h, idx) => {
                    const resolvedSite = getHoardingSite(h, siteMap, outsideSiteMap);
                    const siteAddress = buildSiteAddress(resolvedSite, h.hoardingCode);

                    return (
                      <tr key={h.hoardingID} style={{ borderBottom: idx < mergedItems.length - 1 ? '1px solid #f0f0f8' : 'none' }}>
                        <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#9090a8' }}>{idx + 1}</td>
                        <td style={{ padding: '10px 12px', fontWeight: 800, color: '#049edf' }}>{h.hoardingCode}</td>
                        <td style={{ padding: '10px 12px', color: '#1a1a2e', fontWeight: 600 }}>{siteAddress}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 700, color: '#4a5568' }}>{h.width} × {h.height} ft</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: 800, color: '#1a1a2e' }}>₹{fmtCurrency(h.monthlyRent)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="pg-modal__foot" style={{ display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
          <button className="pg-btn-cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
}



































/* ═══════════════════════════════════════════
   CUSTOMER EDIT MODAL  ← NEW
═══════════════════════════════════════════ */
function CustomerEditModal({ customer, onSave, onClose }) {
  const [form, setForm] = useState({
    customerName: customer?.customerName ?? '',
    authorizedName: customer?.authorizedName ?? '',
    phone1: customer?.phone1 ?? '',
    phone2: customer?.phone2 ?? '',
    addressLine1: customer?.addressLine1 ?? '',
    addressLine2: customer?.addressLine2 ?? '',
    addressLine3: customer?.addressLine3 ?? '',
    city: customer?.city ?? '',
    district: customer?.district ?? '',
    gstNumber: customer?.gstNumber ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [apiErr, setApiErr] = useState('');

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {

    if (!form.customerName.trim()) { setApiErr('Customer name is required.'); return; }
    setSaving(true); setApiErr('');
    try {
      await apiService.updateCustomer({ ...form, customerID: customer.customerID, country: 'India' });
      onSave({ ...customer, ...form, country: 'India' });
      onClose();
    } catch (err) {
      setApiErr(err?.response?.data?.message || err?.message || 'Save failed.');
    } finally { setSaving(false); }
  };

  const FIELDS = [
    { key: 'customerName', label: 'Customer Name', req: true, full: true },
    { key: 'authorizedName', label: 'Authorized Person', req: false, full: false },
    { key: 'phone1', label: 'Phone 1', req: true, full: false },
    { key: 'phone2', label: 'Phone 2', req: false, full: false },
    { key: 'addressLine1', label: 'Address Line 1', req: true, full: true },
    { key: 'addressLine2', label: 'Address Line 2', req: false, full: false },
    { key: 'addressLine3', label: 'Address Line 3 / Landmark', req: false, full: false },
    { key: 'city', label: 'City', req: true, full: false },
    { key: 'district', label: 'District', req: false, full: false },
    { key: 'gstNumber', label: 'GST Number (15 chars)', req: false, full: false },
  ];

  return ReactDOM.createPortal(
    <div className="pg-overlay">
      <div className="pg-modal" style={{ maxWidth: 600 }}>
        <div className="pg-modal__head">
          <div className="pg-modal__head-left">
            <div className="pg-modal__icon-wrap"><User size={20} color="#049edf" /></div>
            <div>
              <h5 className="pg-modal__title">Edit Customer</h5>
              <p className="pg-modal__subtitle">#{customer.customerID} · {customer.customerName}</p>
            </div>
          </div>
          <button className="pg-modal__close" onClick={onClose}><X size={15} /></button>
        </div>

        <div style={{ padding: '20px 24px', overflowY: 'auto', maxHeight: '62vh' }}>
          {apiErr && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '9px 12px', marginBottom: 14,
              background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 9,
              color: '#dc2626', fontSize: 12.5, fontFamily: 'Nunito,sans-serif', fontWeight: 700,
            }}>
              <AlertCircle size={13} /> {apiErr}
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            {FIELDS.map(f => (
              <div key={f.key} style={f.full ? { gridColumn: '1 / -1' } : {}}>
                <label style={{
                  fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 700,
                  color: '#5a5a78', marginBottom: 4, display: 'block',
                }}>
                  {f.label}
                  {f.req && <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>}
                </label>
                <div className="qt-input-wrap">
                  <input
                    className="qt-input"
                    value={form[f.key]}
                    placeholder={f.key === 'gstNumber' ? '24AAAAA0000A1Z5' : ''}
                    onChange={e =>
                      set(f.key, f.key === 'gstNumber' ? e.target.value.toUpperCase() : e.target.value)
                    }
                    maxLength={f.key === 'gstNumber' ? 15 : undefined}
                    style={f.key === 'gstNumber' ? { letterSpacing: '0.05em', textTransform: 'uppercase' } : {}}
                  />
                </div>
              </div>
            ))}

            {/* Country — read-only */}
            <div>
              <label style={{
                fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 700,
                color: '#5a5a78', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6,
              }}>
                Country
                <span style={{ fontSize: 10, color: '#049edf', fontWeight: 800, background: 'rgba(4,158,223,0.08)', padding: '1px 6px', borderRadius: 4 }}>
                  🔒 Fixed
                </span>
              </label>
              <div className="qt-input-wrap" style={{ background: 'rgba(4,158,223,0.03)', borderBottomColor: '#049edf', cursor: 'not-allowed' }}>
                <input
                  className="qt-input"
                  value="India"
                  readOnly
                  style={{ color: '#049edf', fontWeight: 800, cursor: 'not-allowed', pointerEvents: 'none' }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="pg-modal__foot">
          <button className="pg-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="pg-btn-save" onClick={handleSave} disabled={saving}>
            {saving
              ? <><Loader2 size={13} className="pg-spin" /> Saving…</>
              : <><Check size={13} /> Save Changes</>}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ═══════════════════════════════════════════
   CREATE CONTRACT FROM QUOTATION MODAL  ← NEW
═══════════════════════════════════════════ */
async function addHoardingEffdtRows(hoardingIDs, allHoardings, effdt, status) {
  if (!hoardingIDs.length || !effdt) return;

  await Promise.allSettled(
    hoardingIDs.map(async (hid) => {
      const h = allHoardings.find(hh =>
        Number(hh.hoardingID ?? hh.HoardingID) === Number(hid)
      );
      if (!h || (!h.hoardingCode && !h.HoardingCode)) {
        console.warn('[ContractEffdt] hoarding not found:', hid);
        return;
      }

      const hCode = h.hoardingCode ?? h.HoardingCode;

      const payload = {
        effdt,                              // "YYYY-MM-DD"
        material: h.material ?? h.Material ?? '',
        hoardingType: Number(h.hoardingType ?? h.HoardingType ?? 0),
        status,                             // 'Occupied' or 'Available'
        monthlyRent: Number(h.monthlyRent ?? h.MonthlyRent ?? 0),
        width: Number(h.width ?? h.Width ?? 0),
        height: Number(h.height ?? h.Height ?? 0),
        siteID: Number(h.siteID ?? h.SiteID ?? h.site?.siteID ?? 0),
      };

      console.log('[ContractEffdt]', hCode, '→', effdt, status, payload);
      return apiService.addHoardingEffdt(hCode, payload);
    })
  );
}

function CreateContractFromQuotModal({
  quot, quotLines, quotMerges = [], hoardings, customers, siteMap, outsideSiteMap, paymentFreqs, onClose, onCreated, showToast,
}) {
  const myLines = quotLines.filter(l =>
    Number(l.quotationID) === Number(quot.quotationID) &&
    Number(l.quotationRevisionNumber) === Number(quot.quotationRevisionNumber)
  );
  const regularLines = myLines.filter(l => !l.mergeFlag);
  const myMerges = quotMerges.filter(m =>
    Number(m.quotationID) === Number(quot.quotationID) &&
    Number(m.quotationRevisionNumber) === Number(quot.quotationRevisionNumber)
  );

  // Collect all unique hoardingIDs from merges that are NOT already in regular lines
  const regularHoardingIds = new Set(regularLines.map(l => Number(l.hoardingID)));
  const mergedHoardingIds = [...new Set(
    myMerges
      .map(m => Number(m.hoardingID))
      .filter(id => id > 0 && !regularHoardingIds.has(id))
  )];

  const initCustomer = customers.find(c => c.customerID === quot.customerID) || null;
  const [localCustomer, setLocalCustomer] = useState(initCustomer);
  const [showEditCust, setShowEditCust] = useState(false);

  const freqOptions = paymentFreqs.length ? paymentFreqs : PAYMENT_FREQ_FALLBACK;
  const [freqID, setFreqID] = useState(String(freqOptions[0]?.value ?? 1));
  const [saving, setSaving] = useState(false);
  const [rowErrors, setRowErrors] = useState({});
  const [occupancyWarnings, setOccupancyWarnings] = useState([]);


  /* Build one editable row per quotation line */
  const [contractRows, setContractRows] = useState(() => {
    // Regular lines (non-merged)
    const regularRows = regularLines.map(l => {
      const h = hoardings.find(hh => hh.hoardingID === l.hoardingID);
      const siteObj = getHoardingSite(h, siteMap, outsideSiteMap);
      return {
        _id: uid(),
        selected: true,
        isMerged: false,
        hoardingID: l.hoardingID,
        mergedHoardingIDs: [],
        hoardingCode: h?.hoardingCode || l.purpose || `Hoarding ${l.hoardingID}`,
        location: h ? buildSiteAddress(siteObj, h.hoardingCode || '') : '',
        size: h ? `${h.width} X ${h.height}` : '',
        startDate: l.periodBeginDate || '',
        endDate: l.periodEndDate || '',
        contractOrigValue: l.rentAmount || 0,
        amountPerFreq: l.rentAmount || 0,
        status: 'Active',
      };
    });

    // Build ONE merged row per merge group (grouped by quotationLineNumber)
    const mergedRows = [];
    if (myMerges.length >= 2) {
      const byLine = new Map();
      for (const m of myMerges) {
        const ln = m.quotationLineNumber;
        if (!byLine.has(ln)) byLine.set(ln, []);
        byLine.get(ln).push(m);
      }

      const usedLineNums = new Set();
      for (const [ln, records] of byLine.entries()) {
        if (records.length < 2) continue;

        const hoardingObjs = records
          .map(m => hoardings.find(h => h.hoardingID === Number(m.hoardingID)))
          .filter(Boolean);

        const dir = records[0].mergeAlongFlag === 'S' ? 'S' : (records[0].mergeAlongFlag === 'H' ? 'H' : 'V');

        // Compute merged size
        const sizes = hoardingObjs.map(h => ({ w: h.width || 0, h: h.height || 0 }));
        const gaps = hoardingObjs.length - 1;
        let mw, mh;
        if (dir === 'S') {
          const singleW = sizes[0]?.w || 0;
          const singleH = sizes[0]?.h || 0;
          mw = (singleW * 2) + 1;
          mh = (singleH * 2) + 1;
        } else if (dir === 'H') {
          mw = sizes.reduce((s, sz) => s + sz.w, 0) + gaps;
          mh = Math.max(...sizes.map(s => s.h));
        } else {
          mw = Math.max(...sizes.map(s => s.w));
          mh = sizes.reduce((s, sz) => s + sz.h, 0) + gaps;
        }

        // Combined location
        const locations = hoardingObjs.map(h => {
          const siteObj = getHoardingSite(h, siteMap, outsideSiteMap);
          return buildSiteAddress(siteObj, h.hoardingCode || '');
        });

        const combinedCodes = hoardingObjs.map(h => h.hoardingCode || '').join(' + ');
        const combinedLocation = locations.join(' + ');
        const totalRent = hoardingObjs.reduce((s, h) => s + (h.monthlyRent || 0), 0);

        // Find matching line with robust fallbacks
        let matchingLine = quotLines.find(l =>
          Number(l.quotationID) === Number(quot.quotationID) &&
          Number(l.quotationRevisionNumber) === Number(quot.quotationRevisionNumber) &&
          records.some(m => Number(m.quotationLineNumber) === Number(l.quotationLineNumber)) &&
          !usedLineNums.has(Number(l.quotationLineNumber))
        );
        if (!matchingLine) {
          matchingLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)) && l.purpose && hoardingObjs.some(ho => {
            const loc = buildSiteAddress(ho.siteID ? siteMap.get(ho.siteID) : null, ho.hoardingCode);
            return l.purpose.includes(ho.hoardingCode) || l.purpose.includes(loc);
          }));
        }
        if (!matchingLine) {
          matchingLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)));
        }

        if (matchingLine) {
          usedLineNums.add(Number(matchingLine.quotationLineNumber));
        }

        const rentVal = matchingLine?.rentAmount || totalRent || 0;
        // Dates: from quotation line's period date or fallback to quotation date
        const startDate = matchingLine?.periodBeginDate || quot.quotationDate?.split('T')[0] || '';
        const endDate = matchingLine?.periodEndDate || '';

        mergedRows.push({
          _id: uid(),
          selected: true,
          isMerged: true,
          mergeDirection: dir,
          mergedHoardingIDs: hoardingObjs.map(h => h.hoardingID),
          hoardingID: 0,
          hoardingCode: combinedCodes,
          location: combinedLocation,
          size: `${mw} X ${mh}`,
          startDate,
          endDate,
          contractOrigValue: rentVal,
          amountPerFreq: rentVal,
          status: 'Active',
        });
      }
    }

    return [...regularRows, ...mergedRows];
  });

  // ── Resizable columns — must come AFTER contractRows ──
  const contractTableRef = useRef(null);
  const [contractTableReady, setContractTableReady] = useState(false);
  useResizableColumns(contractTableRef, contractTableReady, [44, 220, 80, 136, 136, 130, 118, 110]);

  useEffect(() => {
    setContractTableReady(false);
    const t = setTimeout(() => setContractTableReady(true), 120);
    return () => clearTimeout(t);
  }, [contractRows.length]);

  const selectedRows = contractRows.filter(r => r.selected);
  const allSelected = contractRows.length > 0 && contractRows.every(r => r.selected);
  const someSelected = contractRows.some(r => r.selected);

  const updateRow = (id, field, val) =>
    setContractRows(p => p.map(r => r._id === id ? { ...r, [field]: val } : r));

  const toggleRow = (id) =>
    setContractRows(p => p.map(r => r._id === id ? { ...r, selected: !r.selected } : r));

  const toggleAll = () => {
    const next = !allSelected;
    setContractRows(p => p.map(r => ({ ...r, selected: next })));
  };

  const handleCreate = async () => {
    if (selectedRows.length === 0) {
      showToast('No hoarding lines found.', 'error');
      return;
    }

    const startDate = selectedRows.reduce((e, r) =>
      !e || (r.startDate && r.startDate < e) ? r.startDate : e, '');
    const endDate = selectedRows.reduce((l, r) =>
      !l || (r.endDate && r.endDate > l) ? r.endDate : l, '');

    if (!startDate || !endDate) {
      showToast('Start and end dates are required.', 'error');
      return;
    }

    const totalValue = selectedRows.reduce((s, r) => s + Number(r.contractOrigValue || 0), 0);
    const totalPerFreq = selectedRows.reduce((s, r) => s + Number(r.amountPerFreq || 0), 0);
    const validRows = selectedRows.filter(r => Number(r.hoardingID) > 0);

    setSaving(true);

    // Step 1: QuotationCustomer (non-blocking)
    try {
      await apiService.createQuotationCustomer({
        quotationID: quot.quotationID,
        quotationRevisionNumber: quot.quotationRevisionNumber ?? 0,
        customerID: quot.customerID,
      });
    } catch (err) {
      console.warn('[QuotationCustomer]:', err?.message);
    }

    // Step 2: Create CustomerContract
    let savedContractID = 0;
    let targetCompanyID = null;
    try {
      const revNum = Number(quot.quotationRevisionNumber ?? quot.QuotationRevisionNumber ?? quot.revisionNumber ?? 0);
      let qcRes;
      try {
        qcRes = await apiService.getQuotationCompanyByQuotation(quot.quotationID, revNum);
      } catch {
        qcRes = await apiService.getQuotationCompanyByQuotationId(quot.quotationID);
      }
      const qcList = Array.isArray(qcRes) ? qcRes : Array.isArray(qcRes?.data) ? qcRes.data : [];
      if (qcList.length > 0) {
        const matchingQc = qcList.find(qc =>
          Number(qc.quotationRevisionNumber ?? qc.QuotationRevisionNumber ?? 0) === revNum
        ) || qcList[0];
        targetCompanyID = matchingQc.company_ID ?? matchingQc.companyID ?? matchingQc.CompanyID ?? null;
      }
    } catch (qcErr) {
      console.warn('[QuotationCompany] Fetch failed:', qcErr?.message);
    }

    try {
      const commentsText = `From Quotation ${quot.quotationNumber || quot.quotationID} Rev.${quot.quotationRevisionNumber || 1}` + (targetCompanyID ? `\n[CompanyID: ${targetCompanyID}]` : '');
      const rawRes = await apiService.createCustomerContract({
        customerContractID: 0,
        customerID: Number(quot.customerID),
        companyID: targetCompanyID,
        startDate,
        endDate,
        contractOrigValue: totalValue,
        paymentFreqID: Number(freqID),
        amountPerFreq: totalPerFreq,
        advancePaid: 0,
        status: 'Active',
        discountAmount: 0,
        adjustmentAmount: 0,
        contractFinalValue: totalValue,
        comments: commentsText,
      });
      savedContractID = Number(
        rawRes?.data?.customerContractID
        ?? rawRes?.data?.CustomerContractID
        ?? rawRes?.customerContractID
        ?? rawRes?.CustomerContractID
        ?? 0
      );

      // Backend is returning wrong ID — fetch the real newly created contract
      if (savedContractID <= 1) {
        console.warn('[Contract] Fetching real contract ID via apiService...');
        try {
          const allContracts = await apiService.getAllCustomerContracts();
          const allList = Array.isArray(allContracts) ? allContracts : [];

          const forThisCustomer = allList
            .filter(c => Number(c.customerContractID ?? c.CustomerContractID) > 0)
            .sort((a, b) =>
              Number(b.customerContractID ?? b.CustomerContractID ?? 0) -
              Number(a.customerContractID ?? a.CustomerContractID ?? 0)
            );

          if (forThisCustomer.length > 0) {
            savedContractID = Number(
              forThisCustomer[0].customerContractID ?? forThisCustomer[0].CustomerContractID
            );
          }
        } catch (err) {
          console.error('[Contract] apiService fetch failed:', err?.message);
        }
      }

    } catch (err) {
      setSaving(false);
      showToast(err?.response?.data?.message || err?.message || 'Failed to create contract.', 'error');
      return;
    }

    if (savedContractID > 0) {
      // Collect all hoardingIDs to map from selected rows only
      const allHoardingIDsToMap = new Set();

      for (const row of selectedRows) {
        if (row.isMerged && row.mergedHoardingIDs?.length) {
          row.mergedHoardingIDs.forEach(id => {
            if (Number(id) > 0) allHoardingIDsToMap.add(Number(id));
          });
        } else if (Number(row.hoardingID) > 0) {
          allHoardingIDsToMap.add(Number(row.hoardingID));
        }
      }

      // Step 3: Create hoarding maps
      const occupiedMsgs = [];
      for (const hID of allHoardingIDsToMap) {
        try {
          await apiService.createCustomerContractHoardingMap({
            customerContractLineID: 0,
            customerContractID: savedContractID,
            customerID: Number(quot.customerID),
            hoardingID: hID,
          });
        } catch (err) {
          console.error('[HoardingMap] Error for hID', hID,
            '| status:', err?.response?.status,
            '| data:', JSON.stringify(err?.response?.data),
            '| message:', err?.message
          );

          const occ = parseOccupancyError(err);
          if (occ) {
            const h = hoardings.find(hh => hh.hoardingID === hID);
            const code = h?.hoardingCode ? ` (${h.hoardingCode})` : '';
            const msg = occ.replace(/Hoarding\s+\d+/, `Hoarding #${hID}${code}`);
            occupiedMsgs.push(msg);
            showToast(msg, 'error');
          } else {
            const fallback = err?.response?.data?.message || err?.message || `Failed to map Hoarding #${hID}`;
            showToast(fallback, 'error');
            console.error('[HoardingMap] Non-occupancy error:', hID, fallback);
          }
        }
      }
      if (occupiedMsgs.length > 0) {
        setOccupancyWarnings(occupiedMsgs);
        setSaving(false);
        return;
      }

      // Save hoarding link details with photos using new endpoint
      try {
        for (const hID of allHoardingIDsToMap) {
          const h = hoardings.find(hh => Number(hh.hoardingID) === Number(hID));
          if (!h) continue;

          // Find the row containing this hoarding to get the correct start date and rent
          const row = selectedRows.find(r =>
            r.hoardingID === hID || (r.isMerged && r.mergedHoardingIDs?.includes(hID))
          ) || {};

          const isExternal = h.isExternal === true || String(h.isExternal).toLowerCase() === 'true' || h.is_External === true || String(h.is_External).toLowerCase() === 'true';

          const payload = {
            hoardingID: Number(hID),
            effdt: row.startDate || startDate || quot.quotationDate?.split('T')[0] || new Date().toISOString().split('T')[0],
            hoardingCode: h.hoardingCode ?? h.HoardingCode ?? '',
            material: h.material ?? h.Material ?? '',
            hoardingType: Number(h.hoardingType ?? h.HoardingType ?? 0),
            status: isExternal ? 'Active' : 'Occupied',
            monthlyRent: Number(row.isMerged ? (h.monthlyRent ?? 0) : (row.contractOrigValue || h.monthlyRent || 0)),
            width: Number(h.width ?? h.Width ?? 0),
            height: Number(h.height ?? h.Height ?? 0),
            siteID: Number(h.siteID ?? h.SiteID ?? h.site?.siteID ?? 0),
            isExternal: isExternal,
          };

          await apiService.saveHoardingLinkWithPhotos(payload);
        }
      } catch (err) {
        console.error('[SaveHoardingLinkWithPhotos] Failed:', err?.message);
        showToast(err?.response?.data?.message || err?.message || 'Failed to save hoarding link with photos.', 'error');
      }

      /*
      // ← NEW: add Occupied effdt row for each hoarding (startDate)
      try {
        await addHoardingEffdtRows(
          Array.from(allHoardingIDsToMap),
          hoardings,
          startDate,
          'Occupied'
        );
      } catch (effdtErr) {
        console.error('[ContractEffdt] Failed to add effdt rows:', effdtErr);
      }
      */

      // Step 4: Merge records — only for hoardings that were actually mapped from this quotation revision
      const thisMerges = quotMerges.filter(m =>
        Number(m.quotationID ?? m.QuotationID) === Number(quot.quotationID ?? quot.QuotationID) &&
        Number(m.quotationRevisionNumber ?? m.QuotationRevisionNumber ?? 0) === Number(quot.quotationRevisionNumber ?? quot.QuotationRevisionNumber ?? 0)
      );

      // Group merges by quotationLineNumber to assign distinct hoardingLineNumber per merge group
      const mergeGroups = new Map();
      for (const m of thisMerges) {
        const ln = Number(m.quotationLineNumber ?? m.QuotationLineNumber ?? 0);
        if (!mergeGroups.has(ln)) mergeGroups.set(ln, []);
        mergeGroups.get(ln).push(m);
      }

      let lineCounter = 1;
      for (const groupRecords of mergeGroups.values()) {
        const validRecords = groupRecords.filter(m => allHoardingIDsToMap.has(Number(m.hoardingID ?? m.HoardingID)));
        if (validRecords.length === 0) continue;
        const lineNumToSend = lineCounter++;
        for (const m of validRecords) {
          const hID = Number(m.hoardingID ?? m.HoardingID);
          const rawFlag = m.mergeAlongFlag ?? m.MergeAlongFlag ?? m.mergeDirection ?? '';
          const flag = (rawFlag === 'S' || rawFlag === 's') ? 'S' : ((rawFlag === 'V' || rawFlag === 'v') ? 'V' : 'H');
          try {
            await apiService.createHoardingMerge({
              hoardingLineNumber: lineNumToSend,
              hoardingID: hID,
              customerContractID: Number(savedContractID),
              mergeAlongFlag: flag,
            });
          } catch (err) {
            console.error('[Merge] Failed:', hID, err?.message);
          }
        }
      }
    }

    setSaving(false);
    await new Promise(resolve => setTimeout(resolve, 500));
    showToast('Customer contract created successfully!', 'success');
    onCreated?.();
    onClose();
  };

  const totalContractValue = selectedRows.reduce((s, r) => s + Number(r.contractOrigValue || 0), 0);
  const totalAmtPerFreq = selectedRows.reduce((s, r) => s + Number(r.amountPerFreq || 0), 0);

  return ReactDOM.createPortal(
    <div className="pg-overlay">
      <div className="pg-modal" style={{ maxWidth: 940, display: 'flex', flexDirection: 'column', maxHeight: '92vh', overflowY: 'auto' }}>

        {/* ── Head ── */}
        <div className="pg-modal__head" style={{ padding: '14px 20px', flexShrink: 0 }}>
          <div className="pg-modal__head-left">
            <div className="pg-modal__icon-wrap" style={{ background: 'rgba(124,58,237,0.10)' }}>
              <FileCheck size={20} color="#7c3aed" />
            </div>
            <div>
              <h5 className="pg-modal__title">Create Customer Contracts</h5>
              <p className="pg-modal__subtitle">
                From Quotation&nbsp;<strong>{quot.quotationNumber || `#${quot.quotationID}`}</strong>
                &nbsp;·&nbsp;{(regularLines.length + mergedHoardingIds.length)} hoarding{(regularLines.length + mergedHoardingIds.length) !== 1 ? 's' : ''}
                {Number(quot.quotationRevisionNumber) > 1 && (
                  <span style={{ marginLeft: 6, padding: '1px 7px', borderRadius: 8, background: 'rgba(217,119,6,0.10)', color: '#7c7c7c', fontSize: 11, fontWeight: 800, border: '1px solid rgba(217,119,6,0.25)' }}>
                    Rev. {quot.quotationRevisionNumber}
                  </span>
                )}
              </p>
            </div>
          </div>
          <button className="pg-modal__close" onClick={onClose}><X size={15} /></button>
        </div>
        {/* ── Occupancy warning ── */}
        {occupancyWarnings.length > 0 && (
          <div style={{ padding: '10px 20px 0', flexShrink: 0 }}>
            <OccupancyWarningBanner
              messages={occupancyWarnings}
              onDismiss={() => setOccupancyWarnings([])}
            />
          </div>
        )}
        {/* ── Customer bar ── */}
        <div style={{
          padding: '8px 20px', borderBottom: '1px solid #f0f0f8', background: '#fafafe',
          display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', flexShrink: 0,
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 8,
            background: 'rgba(4,158,223,0.10)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <User size={14} color="#049edf" />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 900, color: '#1a1a2e', lineHeight: 1.1 }}>
              {localCustomer?.customerName || `Customer ID ${quot.customerID}`}
            </div>
            {localCustomer && (
              <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, color: '#9090a8', fontWeight: 600, marginTop: 2 }}>
                {[localCustomer.addressLine1, localCustomer.city, localCustomer.district].filter(Boolean).join(', ')}
                {localCustomer.gstNumber && <span style={{ marginLeft: 8 }}>· GST: {localCustomer.gstNumber}</span>}
                {localCustomer.phone1 && <span style={{ marginLeft: 8 }}>· {localCustomer.phone1}</span>}
              </div>
            )}
          </div>
          <button
            onClick={() => setShowEditCust(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px',
              borderRadius: 8, border: '1.5px solid rgba(4,158,223,0.30)',
              background: 'rgba(4,158,223,0.06)', cursor: 'pointer',
              color: '#049edf', fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 800,
              flexShrink: 0,
            }}
          >
            <Edit2 size={12} /> Edit Customer
          </button>
        </div>

        {/* ── Config bar ── */}
        <div style={{
          padding: '8px 20px', borderBottom: '1px solid #f0f0f8', background: '#fff',
          display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', flexShrink: 0,
        }}>
          <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 800, color: '#5a5a78', whiteSpace: 'nowrap' }}>
            Payment Frequency:
          </span>
          <select
            value={freqID}
            onChange={e => setFreqID(e.target.value)}
            style={{
              padding: '5px 12px', border: '1.5px solid #e8e8f4', borderRadius: 8,
              fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 700,
              color: '#1a1a2e', background: '#fff', cursor: 'pointer', outline: 'none',
            }}
          >
            {freqOptions.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}
          </select>
          <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11.5, color: '#b0b0c8', fontWeight: 600 }}>
            Applied to all selected contracts
          </span>
          <div style={{ marginLeft: 'auto', fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 700, color: '#9090a8' }}>
            {selectedRows.length} / {contractRows.length} selected
          </div>
        </div>

        {/* ── Table ── */}
        <div style={{ flex: '1 1 auto', overflow: 'auto', minHeight: 180, maxHeight: 380 }}>
          {contractRows.length === 0 ? (
            <div style={{ padding: '40px 20px', textAlign: 'center', fontFamily: 'Nunito,sans-serif', color: '#9090a8' }}>
              <Building2 size={36} color="#d0d0e8" style={{ marginBottom: 10 }} />
              <div>No hoarding lines in this quotation.</div>
            </div>
          ) : (
            <table ref={contractTableRef} style={{ width: '100%', minWidth: 800, borderCollapse: 'collapse', tableLayout: 'fixed' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <tr style={{ background: '#f8f8fd' }}>
                  {/* Select-all */}
                  <th style={{ padding: '10px 14px', width: 44, textAlign: 'center', borderBottom: '1.5px solid #e8e8f4' }}>
                    <div
                      onClick={toggleAll}
                      title={allSelected ? 'Deselect all' : 'Select all'}
                      style={{
                        width: 20, height: 20, borderRadius: 6, cursor: 'pointer', margin: '0 auto',
                        border: `2px solid ${allSelected ? '#049edf' : someSelected ? '#049edf' : '#d0d0e0'}`,
                        background: allSelected ? '#049edf' : '#fff',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      {allSelected && <Check size={12} color="#fff" />}
                      {!allSelected && someSelected && (
                        <div style={{ width: 8, height: 2, background: '#049edf', borderRadius: 2 }} />
                      )}
                    </div>
                  </th>
                  {[
                    { label: 'Hoarding / Location', align: 'left', w: null },
                    { label: 'Size', align: 'center', w: 80 },
                    { label: 'Start Date', align: 'center', w: 136 },
                    { label: 'End Date', align: 'center', w: 136 },
                    { label: 'Contract Value (₹)', align: 'center', w: 130 },
                    { label: 'Amt / Freq (₹)', align: 'center', w: 118 },
                    { label: 'Status', align: 'center', w: 110 },
                  ].map(col => (
                    <th key={col.label} style={{
                      padding: '10px 12px', textAlign: col.align,
                      fontSize: 10.5, fontFamily: 'Nunito,sans-serif', fontWeight: 800, color: '#7878a0',
                      textTransform: 'uppercase', letterSpacing: '0.05em',
                      borderBottom: '1.5px solid #e8e8f4', whiteSpace: 'nowrap',
                      ...(col.w ? { width: col.w } : {}),
                    }}>
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {contractRows.map((row, idx) => {
                  const hasErr = rowErrors[row._id];
                  return (
                    <tr key={row._id} style={{
                      background: !row.selected ? '#f9f9fb' : idx % 2 === 0 ? '#fff' : '#fafafe',
                      opacity: row.selected ? 1 : 0.45,
                      transition: 'opacity 0.15s',
                    }}>

                      {/* Checkbox */}
                      <td style={{ padding: '10px 14px', textAlign: 'center', borderBottom: '1px solid #f0f0f8' }}>
                        <div
                          onClick={() => toggleRow(row._id)}
                          style={{
                            width: 20, height: 20, borderRadius: 6, cursor: 'pointer', margin: '0 auto',
                            border: `2px solid ${row.selected ? '#049edf' : '#d0d0e0'}`,
                            background: row.selected ? '#049edf' : '#fff',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}
                        >
                          {row.selected && <Check size={12} color="#fff" />}
                        </div>
                      </td>
                      {/* Hoarding info */}
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f8' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                          <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12.5, fontWeight: 800, color: '#049edf', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {row.hoardingCode}
                          </div>
                          {row.isMerged && (
                            <span style={{
                              fontSize: 10, fontWeight: 800, padding: '1px 6px', borderRadius: 4,
                              background: 'rgba(124,58,237,0.10)', color: '#7c3aed',
                              border: '1px solid rgba(124,58,237,0.25)', whiteSpace: 'nowrap',
                            }}>
                              From Merge
                            </span>
                          )}
                        </div>
                        {row.location && (
                          <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, color: '#9090a8', fontWeight: 600, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <MapPin size={10} color="#c0c0d8" style={{ marginRight: 3, verticalAlign: 'middle' }} />
                            {row.location}
                          </div>
                        )}
                      </td>
                      {/* Size */}
                      <td style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f8', textAlign: 'center' }}>
                        <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 700, color: '#4a5568' }}>{row.size || '—'}</span>
                      </td>

                      {/* Start Date */}
                      <td style={{ padding: '7px 8px', borderBottom: '1px solid #f0f0f8' }}>
                        <input
                          type="date"
                          value={row.startDate}
                          disabled={true}
                          onChange={e => { updateRow(row._id, 'startDate', e.target.value); setRowErrors(p => ({ ...p, [row._id]: '' })); }}
                          style={{
                            width: '100%', padding: '6px 8px',
                            border: `1.5px solid ${hasErr ? '#ef4444' : '#e8e8f4'}`,
                            borderRadius: 8, fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 700,
                            color: '#718096', background: '#f4f4fb', outline: 'none',
                            cursor: 'not-allowed',
                          }}
                        />
                        {hasErr && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 3, marginTop: 3, color: '#ef4444', fontSize: 10.5, fontFamily: 'Nunito,sans-serif', fontWeight: 700 }}>
                            <AlertCircle size={10} /> {hasErr}
                          </div>
                        )}
                      </td>

                      {/* End Date */}
                      <td style={{ padding: '7px 8px', borderBottom: '1px solid #f0f0f8' }}>
                        <input
                          type="date"
                          value={row.endDate}
                          disabled={true}
                          onChange={e => { updateRow(row._id, 'endDate', e.target.value); setRowErrors(p => ({ ...p, [row._id]: '' })); }}
                          style={{
                            width: '100%', padding: '6px 8px',
                            border: `1.5px solid ${hasErr ? '#ef4444' : '#e8e8f4'}`,
                            borderRadius: 8, fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 700,
                            color: '#718096', background: '#f4f4fb', outline: 'none',
                            cursor: 'not-allowed',
                          }}
                        />
                      </td>

                      {/* Contract Value */}
                      <td style={{ padding: '7px 8px', borderBottom: '1px solid #f0f0f8' }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          border: '1.5px solid #e8e8f4', borderRadius: 8, padding: '5px 8px',
                          background: '#f4f4fb',
                        }}>
                          <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, color: '#b0b0c8', flexShrink: 0 }}>₹</span>
                          <input
                            type="number" min="0"
                            value={row.contractOrigValue}
                            disabled={true}
                            onChange={e => updateRow(row._id, 'contractOrigValue', e.target.value)}
                            style={{
                              width: '100%', border: 'none', outline: 'none',
                              fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 800,
                              color: '#718096', background: 'transparent',
                              cursor: 'not-allowed',
                            }}
                          />
                        </div>
                      </td>

                      {/* Amount per Freq */}
                      <td style={{ padding: '7px 8px', borderBottom: '1px solid #f0f0f8' }}>
                        <div style={{
                          display: 'flex', alignItems: 'center', gap: 4,
                          border: '1.5px solid #e8e8f4', borderRadius: 8, padding: '5px 8px',
                          background: '#f4f4fb',
                        }}>
                          <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, color: '#b0b0c8', flexShrink: 0 }}>₹</span>
                          <input
                            type="number" min="0"
                            value={row.amountPerFreq}
                            disabled={true}
                            onChange={e => updateRow(row._id, 'amountPerFreq', e.target.value)}
                            style={{
                              width: '100%', border: 'none', outline: 'none',
                              fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 800,
                              color: '#718096', background: 'transparent',
                              cursor: 'not-allowed',
                            }}
                          />
                        </div>
                      </td>

                      {/* Status */}
                      <td style={{ padding: '7px 8px', borderBottom: '1px solid #f0f0f8' }}>
                        <select
                          value={row.status}
                          disabled={true}
                          onChange={e => updateRow(row._id, 'status', e.target.value)}
                          style={{
                            width: '100%', padding: '6px 8px',
                            border: '1.5px solid #e8e8f4', borderRadius: 8,
                            fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 700,
                            color: '#718096', background: '#f4f4fb',
                            outline: 'none', cursor: 'not-allowed',
                          }}
                        >
                          {['Active', 'Pending', 'Expired', 'Terminated'].map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Summary strip ── */}
        {selectedRows.length > 0 && (() => {
          const cgstPct = Number(quot.cGSTPercent ?? 9);
          const sgstPct = Number(quot.sGSTPercent ?? 9);
          const cgstAmt = (totalContractValue * cgstPct) / 100;
          const sgstAmt = (totalContractValue * sgstPct) / 100;
          const grossTotal = totalContractValue + cgstAmt + sgstAmt;
          const finalTotal = Math.round(grossTotal);
          const roundOff = finalTotal - grossTotal;

          return (
            <div style={{
              padding: '8px 20px', borderTop: '1px solid #f0f0f8',
              background: 'rgba(124,58,237,0.04)',
              display: 'flex', alignItems: 'center', gap: '16px 24px', flexWrap: 'wrap', flexShrink: 0,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 700, color: '#5a5a78' }}>
                  Sub Total:&nbsp;
                  <span style={{ fontSize: 12.5, fontWeight: 800, color: '#1a1a2e' }}>
                    ₹ {fmtCurrency(totalContractValue)}
                  </span>
                </div>
                {cgstPct > 0 && (
                  <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 700, color: '#5a5a78' }}>
                    CGST ({cgstPct}%):&nbsp;
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: '#1a1a2e' }}>
                      ₹ {fmtCurrency(cgstAmt)}
                    </span>
                  </div>
                )}
                {sgstPct > 0 && (
                  <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 700, color: '#5a5a78' }}>
                    SGST ({sgstPct}%):&nbsp;
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: '#1a1a2e' }}>
                      ₹ {fmtCurrency(sgstAmt)}
                    </span>
                  </div>
                )}
                {Math.abs(roundOff) > 0.001 && (
                  <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 700, color: '#5a5a78' }}>
                    Round Off:&nbsp;
                    <span style={{ fontSize: 12.5, fontWeight: 800, color: '#1a1a2e' }}>
                      ₹ {fmtCurrency(roundOff)}
                    </span>
                  </div>
                )}
                <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 700, color: '#7c3aed' }}>
                  Total Contract Value:&nbsp;
                  <span style={{ fontSize: 13.5, fontWeight: 900, background: 'rgba(124,58,237,0.1)', padding: '2px 7px', borderRadius: 6 }}>
                    ₹ {fmtCurrency(finalTotal)}
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', marginLeft: 'auto' }}>
                <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 700, color: '#16a34a' }}>
                  Total Amt / Freq:&nbsp;
                  <span style={{ fontSize: 13, fontWeight: 900 }}>
                    ₹ {fmtCurrency(finalTotal)}
                  </span>
                </div>
                <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, color: '#9090a8', fontWeight: 600 }}>
                  Frequency: <strong style={{ color: '#1a1a2e' }}>{freqOptions.find(f => String(f.value) === String(freqID))?.label || '—'}</strong>
                </div>
              </div>
            </div>
          );
        })()}

        {/* ── Footer ── */}
        <div className="pg-modal__foot qt-merge-foot-container" style={{ padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span className="qt-merge-foot-text" style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12.5, color: '#9090a8', fontWeight: 600 }}>
            {selectedRows.length} of {contractRows.length} hoardings selected
          </span>
          <div className="qt-merge-foot-actions" style={{ display: 'flex', gap: 10 }}>
            <button className="pg-btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button
              onClick={handleCreate}
              disabled={saving || selectedRows.length === 0 || occupancyWarnings.length > 0}
              style={{
                display: 'flex', alignItems: 'center', gap: 7,
                padding: '9px 20px', borderRadius: 9, border: 'none',
                background:
                  occupancyWarnings.length > 0 ? '#d0d0e0' :
                    selectedRows.length > 0 ? 'linear-gradient(135deg,#7c3aed,#6d28d9)'
                      : '#d0d0e0',
                color: '#fff',
                cursor: saving || selectedRows.length === 0 || occupancyWarnings.length > 0
                  ? 'not-allowed' : 'pointer',
                fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 800,
                boxShadow: selectedRows.length > 0 && !occupancyWarnings.length
                  ? '0 2px 10px rgba(124,58,237,0.30)' : 'none',
                pointerEvents: saving ? 'none' : 'auto',   // ← hard block on double-click
              }}
            >
              {saving
                ? <><Loader2 size={13} className="pg-spin" /> Creating…</>
                : <><FileCheck size={14} /> Create Contract</>}
            </button>
          </div>
        </div>
      </div>

      {/* Nested customer-edit modal */}
      {showEditCust && localCustomer && (
        <CustomerEditModal
          customer={localCustomer}
          onSave={updated => setLocalCustomer(updated)}
          onClose={() => setShowEditCust(false)}
        />
      )}
    </div>,
    document.body
  );
}

const getQuotationDraft = () => {
  try {
    const draft = sessionStorage.getItem('quotation_form_draft');
    return draft ? JSON.parse(draft) : null;
  } catch (e) {
    console.error("Failed to parse quotation draft", e);
    return null;
  }
};

/* ═══════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════ */
export default function QuotationPage({ onNavigateToContracts, changeTab }) {

  const quotationDraft = useMemo(() => getQuotationDraft(), []);

  /* ── API Data ── */
  const [customers, setCustomers] = useState([]);
  const [hoardings, setHoardings] = useState([]);
  const [sites, setSites] = useState([]);
  const [outsideSites, setOutsideSites] = useState([]);
  const [termsList, setTermsList] = useState([]);
  const [paymentFreqs, setPaymentFreqs] = useState([]);   // ← NEW
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  /* ── History ── */
  const [quotations, setQuotations] = useState([]);
  const [quotLines, setQuotLines] = useState([]);
  const [histSearch, setHistSearch] = useState('');
  const [histSortKey, setHistSortKey] = useState('quotationDate');
  const [histSortDir, setHistSortDir] = useState('desc');
  const [histPage, setHistPage] = useState(1);
  const [histPageSize, setHistPageSize] = useState(10);
  const [expandedGroups, setExpandedGroups] = useState(new Set());

  /* ── Creator state ── */
  // Original code:
  // const [isCreating, setIsCreating] = useState(false);
  const [isCreating, setIsCreating] = useState(() => quotationDraft?.isCreating ?? false);

  /* ── Delete Selection Mode ── */
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedQuotIds, setSelectedQuotIds] = useState(new Set());
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isCreating && e.ctrlKey && (e.key === 'j' || e.key === 'J')) {
        e.preventDefault();
        setDeleteMode(prev => {
          const next = !prev;
          if (!next) {
            setSelectedQuotIds(new Set());
          }
          return next;
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCreating]);

  // Original code:
  // const [step, setStep] = useState(1);
  const [step, setStep] = useState(() => quotationDraft?.step ?? 1);
  const [step1Error, setStep1Error] = useState('');
  const [step2Error, setStep2Error] = useState('');
  // Original code:
  // const [editingQuotID, setEditingQuotID] = useState(null);
  const [editingQuotID, setEditingQuotID] = useState(() => quotationDraft?.editingQuotID ?? null);
  // Original code:
  // const [originalQuotID, setOriginalQuotID] = useState(0);
  const [originalQuotID, setOriginalQuotID] = useState(() => quotationDraft?.originalQuotID ?? 0);
  const [customerContracts, setCustomerContracts] = useState([]);
  /* ── Form fields ── */
  // Original code:
  // const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(() => quotationDraft?.selectedCustomer ?? null);
  // Original code:
  // const [withPrinting, setWithPrinting] = useState(false);
  const [withPrinting, setWithPrinting] = useState(() => quotationDraft?.withPrinting ?? false);
  // Original code:
  // const [quotNo, setQuotNo] = useState('');
  const [quotNo, setQuotNo] = useState(() => quotationDraft?.quotNo ?? '');
  // Original code:
  // const [quotDate, setQuotDate] = useState(todayISO());
  const [quotDate, setQuotDate] = useState(() => quotationDraft?.quotDate ?? todayISO());
  // Original code:
  // const [revisionNo, setRevisionNo] = useState(0);
  const [revisionNo, setRevisionNo] = useState(() => quotationDraft?.revisionNo ?? 0);
  // Original code:
  // const [rows, setRows] = useState([]);
  const [rows, setRows] = useState(() => quotationDraft?.rows ?? []);
  // Original code:
  // const [cgstPct, setCgstPct] = useState(9);
  const [cgstPct, setCgstPct] = useState(() => quotationDraft?.cgstPct ?? 9);
  // Original code:
  // const [sgstPct, setSgstPct] = useState(9);
  const [sgstPct, setSgstPct] = useState(() => quotationDraft?.sgstPct ?? 9);
  // Original code:
  // const [selectedTerms, setSelectedTerms] = useState([]);
  const [selectedTerms, setSelectedTerms] = useState(() => quotationDraft?.selectedTerms ?? []);
  // Original code:
  // const [globalStart, setGlobalStart] = useState('');
  const [globalStart, setGlobalStart] = useState(() => quotationDraft?.globalStart ?? '');
  // Original code:
  // const [globalEnd, setGlobalEnd] = useState('');
  const [globalEnd, setGlobalEnd] = useState(() => quotationDraft?.globalEnd ?? '');
  // Original code:
  // const [globalDays, setGlobalDays] = useState('');
  const [globalDays, setGlobalDays] = useState(() => quotationDraft?.globalDays ?? '');

  const [highlightTerms, setHighlightTerms] = useState(false); // ✅ MODIFIED: Highlight Terms & Conditions block if submit occurs without terms selected
  useEffect(() => {
    if (selectedTerms.length > 0) {
      setHighlightTerms(false);
    }
  }, [selectedTerms]);

  /* ── Modals ── */
  const [showHoardModal, setShowHoardModal] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showExternalModal, setShowExternalModal] = useState(() => {
    return (
      sessionStorage.getItem('reopen_quotation_external_hoarding_modal') === 'true' ||
      sessionStorage.getItem('unsaved_add_external_hoarding_form') !== null ||
      sessionStorage.getItem('newly_created_external_site_id') !== null
    );
  });

  useEffect(() => {
    if (
      sessionStorage.getItem('reopen_quotation_external_hoarding_modal') === 'true' ||
      sessionStorage.getItem('unsaved_add_external_hoarding_form') !== null ||
      sessionStorage.getItem('newly_created_external_site_id') !== null
    ) {
      setShowExternalModal(true);
    }
  }, []);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showTermsConfirmModal, setShowTermsConfirmModal] = useState(false); // ✅ MODIFIED: Show Terms & Conditions confirmation modal if terms are missing
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [showCustomerEditModal, setShowCustomerEditModal] = useState(false);  // ← NEW
  const [showContractModal, setShowContractModal] = useState(false);  // ← NEW
  const [contractQuot, setContractQuot] = useState(null);   // ← NEW
  const [quotMerges, setQuotMerges] = useState([]);
  const [contractedQuotIds, setContractedQuotIds] = useState(new Set());
  const [allContracts, setAllContracts] = useState([]);   // ← NEW
  const [allContractMaps, setAllContractMaps] = useState([]);   // ← NEW
  const [conflictWarnings, setConflictWarnings] = useState([]);   // ← NEW
  const [showConflictModal, setShowConflictModal] = useState(false);// ← NEW
  const [showPrintTypeDD, setShowPrintTypeDD] = useState(false);
  const [proformaConfirmTarget, setProformaConfirmTarget] = useState(null);
  const [viewMergedRow, setViewMergedRow] = useState(null);
  const [proformaInvoices, setProformaInvoices] = useState([]);
  const [proformaGeneratingQuot, setProformaGeneratingQuot] = useState(null);
  const [seriesAlertModal, setSeriesAlertModal] = useState(null); // { type, reason } where reason = 'notFound' | 'inactive'
  const [showProformaTermsModal, setShowProformaTermsModal] = useState(false);
  const [selectedProformaTerms, setSelectedProformaTerms] = useState([]);
  const [companyDetailsList, setCompanyDetailsList] = useState([]);
  const [quotationCompanies, setQuotationCompanies] = useState([]);
  // Original code:
  // const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [selectedCompanyId, setSelectedCompanyId] = useState(() => quotationDraft?.selectedCompanyId ?? '');
  // Original code:
  // const [quotationCompanyRecId, setQuotationCompanyRecId] = useState(0);
  const [quotationCompanyRecId, setQuotationCompanyRecId] = useState(() => quotationDraft?.quotationCompanyRecId ?? 0);

  useEffect(() => {
    if (isCreating) {
      const draftData = {
        isCreating,
        step,
        selectedCustomer,
        withPrinting,
        quotNo,
        quotDate,
        revisionNo,
        rows,
        cgstPct,
        sgstPct,
        selectedTerms,
        editingQuotID,
        originalQuotID,
        globalStart,
        globalEnd,
        globalDays,
        selectedCompanyId,
        quotationCompanyRecId,
      };
      sessionStorage.setItem('quotation_form_draft', JSON.stringify(draftData));
    }
  }, [
    isCreating,
    step,
    selectedCustomer,
    withPrinting,
    quotNo,
    quotDate,
    revisionNo,
    rows,
    cgstPct,
    sgstPct,
    selectedTerms,
    editingQuotID,
    originalQuotID,
    globalStart,
    globalEnd,
    globalDays,
    selectedCompanyId,
    quotationCompanyRecId,
  ]);
  /* ── Step 2 resizable table ── */
  const step2TableRef = useRef(null);
  const printTypeBtnRef = useRef(null);
  const printTypePanelRef = useRef(null);
  const [step2TableReady, setStep2TableReady] = useState(false);
  const histTableRef = useRef(null);
  const [histTableReady, setHistTableReady] = useState(false);
  const step2ColWidths = useMemo(() => {
    return withPrinting
      ? [40, 160, 70, 50, 50, 100, 50, 100, 80, 110, 80, 80, 80, 46]
      : [40, 200, 80, 60, 60, 120, 60, 120, 80, 80, 46];
  }, [withPrinting]);

  const histColWidths = useMemo(() => {
    return deleteMode
      ? [44, 40, 140, 200, 110, 110, 120, 220]
      : [44, 140, 200, 110, 110, 120, 220];
  }, [deleteMode]);

  useResizableColumns(step2TableRef, step2TableReady, step2ColWidths);
  useResizableColumns(histTableRef, histTableReady, histColWidths);

  useEffect(() => {
    setStep2TableReady(false);
    if (step === 2 && rows.length > 0) {
      const t = setTimeout(() => setStep2TableReady(true), 120);
      return () => clearTimeout(t);
    }
  }, [step, withPrinting, rows.length]);
  useEffect(() => {
    if (!loading && !isCreating) {
      setHistTableReady(false);
      const t = setTimeout(() => setHistTableReady(true), 150);
      return () => clearTimeout(t);
    }
  }, [loading, isCreating]);

  const formRef = useRef(null);
  const loadCustomerContracts = async () => {
    try {
      const response = await apiService.getCustomerContracts();

      setCustomerContracts(
        Array.isArray(response)
          ? response
          : response?.data || []
      );
    }
    catch (err) {
      console.error(err);
    }
  };
  const showToast = useCallback((msg, type = 'success') => setToast({ msg, type }), []);
  const checkSeriesActive = async (type) => {
    try {
      const res = await apiService.getAllSeriesIDs();
      const list = normalizeList(res);
      const series = list.find(s => s.seriesType === type);
      if (!series) {
        setSeriesAlertModal({ type, reason: 'notFound' });
        return false;
      }
      if (!series.isActive) {
        setSeriesAlertModal({ type, reason: 'inactive' });
        return false;
      }
      return true;
    } catch (err) {
      console.error(`Failed to check Series Setup for ${type}:`, err);
      return true;
    }
  };

  /* ── Site lookup map ── */
  const siteMap = useMemo(() => {
    const map = new Map();
    for (const s of sites) {
      const sid = toSID(s?.siteID ?? s?.SiteID);
      if (sid) map.set(sid, s);
    }
    for (const h of hoardings) {
      const sid = toSID(h.siteID);
      const s = h.site ? normalizeSite(h.site) : null;
      if (sid && s && !map.has(sid)) map.set(sid, s);
    }
    return map;
  }, [sites, hoardings]);

  /* ── Outside Site lookup map (for external hoardings) ── */
  const outsideSiteMap = useMemo(() => {
    const map = new Map();
    for (const os of outsideSites) {
      const sid = toSID(os?.outsideSiteID ?? os?.siteID ?? os?.id);
      if (sid) map.set(sid, os);
    }
    return map;
  }, [outsideSites]);

  /* ── Site colour map ── */
  const siteColorMap = useMemo(() => {
    const map = buildSiteColorMap(hoardings, sites, outsideSites);
    return map;
  }, [hoardings, sites, outsideSites]);

  const getRowSiteColor = (row) => {
    const sid = toSID(row.siteID ?? row.siteObj?.siteID);
    const color = sid == null ? null : siteColorMap.get(sid) || null;
    return color;
  };

  const toggleGroup = useCallback((key) => {
    setExpandedGroups(p => {
      const n = new Set(p);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  }, []);
  const handleViewProforma = async (quot) => {
    const matchingInvoices = proformaInvoices.filter(
      inv => Number(inv.quotationID ?? inv.QuotationID) === Number(quot.quotationID) &&
        Number(inv.quotationRevisionNumber ?? inv.QuotationRevisionNumber) === Number(quot.quotationRevisionNumber)
    );

    const myLines = quotLines.filter(l =>
      Number(l.quotationID) === Number(quot.quotationID) &&
      Number(l.quotationRevisionNumber) === Number(quot.quotationRevisionNumber)
    );
    const cust = customers.find(c => c.customerID === quot.customerID) || null;

    /* ── Build rows from QuotationLineDTL ── */
    const pdfRows = myLines
      .filter(l => !l.mergeFlag)
      .map(l => {
        if (!l.hoardingID && l.purpose) {
          const isPrinting = PRINTING_TYPES.some(pt =>
            pt.toLowerCase() === (l.purpose || '').toLowerCase()
          );
          return {
            rowType: isPrinting ? 'printing' : 'extra',
            hoardingID: 0,
            location: l.purpose,
            size: '',
            sqFt: 0,
            nos: 1,
            startDate: l.periodBeginDate,
            endDate: l.periodEndDate,
            ratePerMonth: l.rentAmount,
            amount: l.rentAmount,
            printingCost: 0,
          };
        }
        const h = hoardings.find(hh => hh.hoardingID === l.hoardingID);
        const siteObj = getHoardingSite(h, siteMap, outsideSiteMap);

        const { ratePerMonth, printingCost, printType, printRate } = parsePurposeMeta(l.purpose, h?.monthlyRent || 0);
        const days = calculateDays(l.periodBeginDate, l.periodEndDate) || 30;

        const loc = siteObj ? `${siteObj.addressLine1 || ''}${siteObj.city ? `, ${siteObj.city}` : ''}` : h?.hoardingCode || '';

        return {
          rowType: 'hoarding',
          hoardingID: l.hoardingID,
          location: loc,
          size: h ? `${h.width} X ${h.height}` : '',
          sqFt: h ? (h.width * h.height) : 0,
          nos: calcNOSFromDays(days),
          startDate: l.periodBeginDate,
          endDate: l.periodEndDate,
          days: days,
          ratePerMonth: ratePerMonth,
          amount: l.rentAmount,
          printType: printType,
          printRate: printRate,
          printingCost: printingCost,
        };
      });

    /* ── Merged rows from QuotationMergeDTL ── */
    const myMerges = quotMerges.filter(m =>
      Number(m.quotationID) === Number(quot.quotationID) &&
      Number(m.quotationRevisionNumber) === Number(quot.quotationRevisionNumber)
    );

    if (myMerges.length >= 2) {
      const byLine = new Map();
      for (const m of myMerges) {
        const ln = m.quotationLineNumber;
        if (!byLine.has(ln)) byLine.set(ln, []);
        byLine.get(ln).push(m);
      }

      const usedLineNums = new Set();
      const sortedKeys = [...byLine.keys()].sort((a, b) => a - b);
      for (const ln of sortedKeys) {
        const records = byLine.get(ln);
        if (records.length < 2) continue;

        const dir = records[0].mergeAlongFlag === 'S' ? 'S' : (records[0].mergeAlongFlag === 'H' ? 'H' : 'V');
        const hoardingObjs = records
          .map(r => hoardings.find(h => h.hoardingID === r.hoardingID))
          .filter(Boolean);

        const sizes = hoardingObjs.map(h => ({ w: h.width || 0, h: h.height || 0 }));
        const gaps = hoardingObjs.length - 1;
        let mw, mh;
        if (dir === 'S') {
          const singleW = sizes[0]?.w || 0;
          const singleH = sizes[0]?.h || 0;
          mw = (singleW * 2) + 1;
          mh = (singleH * 2) + 1;
        } else if (dir === 'H') {
          mw = sizes.reduce((s, sz) => s + sz.w, 0) + gaps;
          mh = Math.max(...sizes.map(s => s.h));
        } else {
          mw = Math.max(...sizes.map(s => s.w));
          mh = sizes.reduce((s, sz) => s + sz.h, 0) + gaps;
        }

        const locations = hoardingObjs.map(h => {
          const site = getHoardingSite(h, siteMap, outsideSiteMap);
          return site
            ? `${site.addressLine1 || ''}${site.city ? `, ${site.city}` : ''}`
            : h?.hoardingCode || '';
        });
        const codes = hoardingObjs.map(h => h.hoardingCode || '').join(' + ');

        const locFallback = [...new Set(locations)].join(' + ');
        let savedLine = myLines.find(l => Number(l.quotationLineNumber) === Number(ln) && !usedLineNums.has(Number(l.quotationLineNumber)));
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)) && l.purpose && (
            l.purpose.includes(locFallback) ||
            locFallback.includes(l.purpose.split('|')[0]) ||
            (codes && l.purpose.includes(codes))
          ));
        }
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)) && l.purpose && hoardingObjs.some(ho => {
            const loc = buildSiteAddress(ho.siteID ? siteMap.get(ho.siteID) : null, ho.hoardingCode);
            return l.purpose.includes(ho.hoardingCode) || l.purpose.includes(loc);
          }));
        }
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)));
        }

        if (savedLine) {
          usedLineNums.add(Number(savedLine.quotationLineNumber));
        }

        const totalRent = hoardingObjs.reduce((s, ho) => s + (ho.monthlyRent || 0), 0);
        const { ratePerMonth, printingCost, printType, printRate } = parsePurposeMeta(savedLine?.purpose, totalRent);
        const days = calculateDays(savedLine?.periodBeginDate, savedLine?.periodEndDate) || 30;

        pdfRows.push({
          rowType: 'merged',
          isMerged: true,
          mergeDirection: dir,
          hoardingID: 0,
          location: locFallback,
          hoardingCode: codes,
          size: `${mw} X ${mh}`,
          sqFt: mw * mh,
          nos: calcNOSFromDays(days),
          startDate: savedLine?.periodBeginDate || '',
          endDate: savedLine?.periodEndDate || '',
          days: days,
          ratePerMonth: ratePerMonth,
          amount: savedLine ? savedLine.rentAmount : 0,
          printType: printType,
          printRate: printRate,
          printingCost: printingCost,
        });
      }
    }

    const storedSub = quot.totalAmount / (1 + (quot.cGSTPercent + quot.sGSTPercent) / 100);
    const storedCgst = (storedSub * quot.cGSTPercent) / 100;
    const storedSgst = (storedSub * quot.sGSTPercent) / 100;
    const storedGross = storedSub + storedCgst + storedSgst;
    const storedFinal = Math.round(storedGross);

    const targetParams = {
      quot,
      pdfRows,
      cust,
      storedSub,
      storedCgst,
      storedSgst,
      storedGross,
      storedFinal,
      matchingInvoices
    };

    if (matchingInvoices.length > 0) {
      setProformaConfirmTarget(targetParams);
    } else {
      let dbTerms = [];
      try {
        const res = await apiService.getQuotationTerms(quot.quotationID, quot.quotationRevisionNumber);
        const list = normalizeList(res);
        dbTerms = list.map(t => Number(t.termId ?? t.TermId ?? t.termID ?? t.TermID ?? 0)).filter(id => id > 0);
      } catch (err) {
        console.warn('Failed to fetch terms:', err);
      }
      await generateAndStoreProforma(quot, dbTerms);
    }
  };

  const generateAndStoreProforma = async (quot, terms) => {
    const isActive = await checkSeriesActive('Proforma Invoice');
    if (!isActive) return;

    const myLines = quotLines.filter(l =>
      Number(l.quotationID) === Number(quot.quotationID) &&
      Number(l.quotationRevisionNumber) === Number(quot.quotationRevisionNumber)
    );
    const cust = customers.find(c => c.customerID === quot.customerID) || null;

    /* ── Build rows from QuotationLineDTL ── */
    const pdfRows = myLines
      .filter(l => !l.mergeFlag)
      .map(l => {
        if (!l.hoardingID && l.purpose) {
          const isPrinting = PRINTING_TYPES.some(pt =>
            pt.toLowerCase() === (l.purpose || '').toLowerCase()
          );
          return {
            rowType: isPrinting ? 'printing' : 'extra',
            hoardingID: 0,
            location: l.purpose,
            size: '',
            sqFt: 0,
            nos: 1,
            startDate: l.periodBeginDate,
            endDate: l.periodEndDate,
            ratePerMonth: l.rentAmount,
            amount: l.rentAmount,
            printingCost: 0,
          };
        }
        const h = hoardings.find(hh => hh.hoardingID === l.hoardingID);
        const siteObj = getHoardingSite(h, siteMap, outsideSiteMap);

        const { ratePerMonth, printingCost, printType, printRate } = parsePurposeMeta(l.purpose, h?.monthlyRent || 0);
        const days = calculateDays(l.periodBeginDate, l.periodEndDate) || 30;

        const loc = siteObj ? `${siteObj.addressLine1 || ''}${siteObj.city ? `, ${siteObj.city}` : ''}` : h?.hoardingCode || '';

        return {
          rowType: 'hoarding',
          hoardingID: l.hoardingID,
          location: loc,
          size: h ? `${h.width} X ${h.height}` : '',
          sqFt: h ? (h.width * h.height) : 0,
          nos: calcNOSFromDays(days),
          startDate: l.periodBeginDate,
          endDate: l.periodEndDate,
          days: days,
          ratePerMonth: ratePerMonth,
          amount: l.rentAmount,
          printType: printType,
          printRate: printRate,
          printingCost: printingCost,
        };
      });

    /* ── Merged rows from QuotationMergeDTL ── */
    const myMerges = quotMerges.filter(m =>
      Number(m.quotationID) === Number(quot.quotationID) &&
      Number(m.quotationRevisionNumber) === Number(quot.quotationRevisionNumber)
    );

    if (myMerges.length >= 2) {
      const byLine = new Map();
      for (const m of myMerges) {
        const ln = m.quotationLineNumber;
        if (!byLine.has(ln)) byLine.set(ln, []);
        byLine.get(ln).push(m);
      }

      const usedLineNums = new Set();
      const sortedKeys = [...byLine.keys()].sort((a, b) => a - b);
      for (const ln of sortedKeys) {
        const records = byLine.get(ln);
        if (records.length < 2) continue;

        const dir = records[0].mergeAlongFlag === 'S' ? 'S' : (records[0].mergeAlongFlag === 'H' ? 'H' : 'V');
        const hoardingObjs = records
          .map(r => hoardings.find(h => h.hoardingID === r.hoardingID))
          .filter(Boolean);

        const sizes = hoardingObjs.map(h => ({ w: h.width || 0, h: h.height || 0 }));
        const gaps = hoardingObjs.length - 1;
        let mw, mh;
        if (dir === 'S') {
          const singleW = sizes[0]?.w || 0;
          const singleH = sizes[0]?.h || 0;
          mw = (singleW * 2) + 1;
          mh = (singleH * 2) + 1;
        } else if (dir === 'H') {
          mw = sizes.reduce((s, sz) => s + sz.w, 0) + gaps;
          mh = Math.max(...sizes.map(s => s.h));
        } else {
          mw = Math.max(...sizes.map(s => s.w));
          mh = sizes.reduce((s, sz) => s + sz.h, 0) + gaps;
        }

        const locations = hoardingObjs.map(h => {
          const site = getHoardingSite(h, siteMap, outsideSiteMap);
          return site
            ? `${site.addressLine1 || ''}${site.city ? `, ${site.city}` : ''}`
            : h?.hoardingCode || '';
        });
        const codes = hoardingObjs.map(h => h.hoardingCode || '').join(' + ');

        const locFallback = [...new Set(locations)].join(' + ');
        let savedLine = myLines.find(l => Number(l.quotationLineNumber) === Number(ln) && !usedLineNums.has(Number(l.quotationLineNumber)));
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)) && l.purpose && (
            l.purpose.includes(locFallback) ||
            locFallback.includes(l.purpose.split('|')[0]) ||
            (codes && l.purpose.includes(codes))
          ));
        }
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)) && l.purpose && hoardingObjs.some(ho => {
            const loc = buildSiteAddress(ho.siteID ? siteMap.get(ho.siteID) : null, ho.hoardingCode);
            return l.purpose.includes(ho.hoardingCode) || l.purpose.includes(loc);
          }));
        }
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)));
        }

        if (savedLine) {
          usedLineNums.add(Number(savedLine.quotationLineNumber));
        }

        const totalRent = hoardingObjs.reduce((s, ho) => s + (ho.monthlyRent || 0), 0);
        const { ratePerMonth, printingCost, printType, printRate } = parsePurposeMeta(savedLine?.purpose, totalRent);
        const days = calculateDays(savedLine?.periodBeginDate, savedLine?.periodEndDate) || 30;

        pdfRows.push({
          rowType: 'merged',
          isMerged: true,
          mergeDirection: dir,
          hoardingID: 0,
          location: locFallback,
          hoardingCode: codes,
          size: `${mw} X ${mh}`,
          sqFt: mw * mh,
          nos: calcNOSFromDays(days),
          startDate: savedLine?.periodBeginDate || '',
          endDate: savedLine?.periodEndDate || '',
          days: days,
          ratePerMonth: ratePerMonth,
          amount: savedLine ? savedLine.rentAmount : 0,
          printType: printType,
          printRate: printRate,
          printingCost: printingCost,
        });
      }
    }

    const storedSub = quot.totalAmount / (1 + (quot.cGSTPercent + quot.sGSTPercent) / 100);
    const storedCgst = (storedSub * quot.cGSTPercent) / 100;
    const storedSgst = (storedSub * quot.sGSTPercent) / 100;
    const storedGross = storedSub + storedCgst + storedSgst;
    const storedFinal = Math.round(storedGross);

    let invoiceID = null;
    let invoiceNumber = '';
    try {
      try {
        const nextNumRes = await apiService.getNextProformaInvoiceNumber().catch(() => apiService.getNextProformaNumber().catch(() => null));
        invoiceNumber = typeof nextNumRes === 'string' ? nextNumRes
          : nextNumRes?.invoiceNumber ?? nextNumRes?.number ?? nextNumRes?.nextNumber
          ?? nextNumRes?.seriesNumber ?? nextNumRes?.value ?? nextNumRes?.data ?? '';
      } catch (e) {
        console.warn('Failed to fetch next proforma number:', e);
      }

      const res = await apiService.createPerformaInvoice({
        invoiceID: 0,
        quotationID: Number(quot.quotationID),
        quotationRevisionNumber: Number(quot.quotationRevisionNumber ?? 0),
        quotationNumber: quot.quotationNumber || '',
        invoiceNumber: invoiceNumber,
        invoiceDate: new Date().toISOString(),
      });
      invoiceID = res?.invoiceID ?? res?.InvoiceID ?? res?.data?.invoiceID ?? res?.data?.InvoiceID ?? null;
      const returnedNum = res?.invoiceNumber ?? res?.InvoiceNumber ?? res?.data?.invoiceNumber ?? res?.data?.InvoiceNumber ?? '';
      if (returnedNum) invoiceNumber = returnedNum;

      const newInv = {
        invoiceID: invoiceID || 0,
        quotationID: Number(quot.quotationID),
        quotationRevisionNumber: Number(quot.quotationRevisionNumber ?? 0),
        quotationNumber: quot.quotationNumber || '',
        invoiceNumber: invoiceNumber,
      };
      setProformaInvoices(prev => [...prev, newInv]);
      showToast('Proforma invoice saved.', 'success');
    } catch (err) {
      console.warn('[PerformaInvoice] Save failed:', err?.message);
    }

    // ── Save selected Terms & Conditions to Database ──
    await saveQuotationTerms(quot.quotationID, quot.quotationRevisionNumber, terms);

    const companyDetails = await fetchCompanyDetailsForQuotation(quot.quotationID, quot.quotationRevisionNumber);

    const html = buildProformaHTML({
      rows: pdfRows,
      withPrinting: pdfRows.some(r => r.rowType === 'printing') || pdfRows.some(r => Number(r.printingCost || 0) > 0),
      selectedCustomer: cust,
      quotNo: quot.quotationNumber,
      quotDate: quot.quotationDate,
      revisionNo: quot.quotationRevisionNumber,
      cgstPct: quot.cGSTPercent,
      sgstPct: quot.sGSTPercent,
      subTotal: storedSub,
      cgstAmt: storedCgst,
      sgstAmt: storedSgst,
      roundOff: storedFinal - storedGross,
      finalTotal: storedFinal,
      selectedTerms: terms,
      termsTexts: terms.map(id => {
        const t = termsList.find(t => t.termID === id);
        return t?.description || '';
      }),
      invoiceID,
      invoiceNumber,
      companyDetails,
    });

    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
  };

  const proceedWithProforma = async (target, shouldCreateNew) => {
    let invoiceID = null;
    let invoiceNumber = '';
    const { quot, pdfRows, cust, storedSub, storedCgst, storedSgst, storedGross, storedFinal, matchingInvoices } = target;

    if (shouldCreateNew) {
      const isActive = await checkSeriesActive('Proforma Invoice');
      if (!isActive) return;
      try {
        try {
          const nextNumRes = await apiService.getNextProformaInvoiceNumber().catch(() => apiService.getNextProformaNumber().catch(() => null));
          invoiceNumber = typeof nextNumRes === 'string' ? nextNumRes
            : nextNumRes?.invoiceNumber ?? nextNumRes?.number ?? nextNumRes?.nextNumber
            ?? nextNumRes?.seriesNumber ?? nextNumRes?.value ?? nextNumRes?.data ?? '';
        } catch (e) {
          console.warn('Failed to fetch next proforma number:', e);
        }

        const res = await apiService.createPerformaInvoice({
          invoiceID: 0,
          quotationID: Number(quot.quotationID),
          quotationRevisionNumber: Number(quot.quotationRevisionNumber ?? 0),
          quotationNumber: quot.quotationNumber || '',
          invoiceNumber: invoiceNumber,
          invoiceDate: new Date().toISOString(),
        });
        invoiceID = res?.invoiceID ?? res?.InvoiceID ?? res?.data?.invoiceID ?? res?.data?.InvoiceID ?? null;
        const returnedNum = res?.invoiceNumber ?? res?.InvoiceNumber ?? res?.data?.invoiceNumber ?? res?.data?.InvoiceNumber ?? '';
        if (returnedNum) invoiceNumber = returnedNum;

        const newInv = {
          invoiceID: invoiceID || 0,
          quotationID: Number(quot.quotationID),
          quotationRevisionNumber: Number(quot.quotationRevisionNumber ?? 0),
          quotationNumber: quot.quotationNumber || '',
          invoiceNumber: invoiceNumber,
        };
        setProformaInvoices(prev => [...prev, newInv]);
        showToast('Proforma invoice saved.', 'success');
      } catch (err) {
        console.warn('[PerformaInvoice] Save failed:', err?.message);
      }
    } else {
      const sorted = [...matchingInvoices].sort((a, b) => {
        const idA = Number(a.invoiceID ?? a.InvoiceID ?? 0);
        const idB = Number(b.invoiceID ?? b.InvoiceID ?? 0);
        return idB - idA;
      });
      invoiceID = sorted[0]?.invoiceID ?? sorted[0]?.InvoiceID ?? null;
      invoiceNumber = sorted[0]?.invoiceNumber ?? sorted[0]?.InvoiceNumber ?? '';
    }

    let dbTerms = [];
    try {
      const res = await apiService.getQuotationTerms(quot.quotationID, quot.quotationRevisionNumber);
      const list = normalizeList(res);
      dbTerms = list.map(t => Number(t.termId ?? t.TermId ?? t.termID ?? t.TermID ?? 0)).filter(id => id > 0);
    } catch (err) {
      console.warn('Failed to fetch terms:', err);
    }

    const companyDetails = await fetchCompanyDetailsForQuotation(quot.quotationID, quot.quotationRevisionNumber);

    /* ── Open Proforma print window ── */
    const html = buildProformaHTML({
      rows: pdfRows,
      withPrinting: pdfRows.some(r => r.rowType === 'printing') || pdfRows.some(r => Number(r.printingCost || 0) > 0),
      selectedCustomer: cust,
      quotNo: quot.quotationNumber,
      quotDate: quot.quotationDate,
      revisionNo: quot.quotationRevisionNumber,
      cgstPct: quot.cGSTPercent,
      sgstPct: quot.sGSTPercent,
      subTotal: storedSub,
      cgstAmt: storedCgst,
      sgstAmt: storedSgst,
      roundOff: storedFinal - storedGross,
      finalTotal: storedFinal,
      selectedTerms: dbTerms,
      termsTexts: dbTerms.map(id => {
        const t = termsList.find(t => t.termID === id);
        return t?.description || '';
      }),
      invoiceID,
      invoiceNumber,
      companyDetails,
    });

    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
  };
  /* ── Load API data ── */
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [cRaw, hRaw, extRaw, sRaw, osRaw, tRaw, qRaw, qlRaw, pRaw, contractsRaw, mapsRaw, quotCustRaw, invoicesRaw, companyDetailsRaw, quotationCompRaw] = await Promise.all([
          apiService.getAllCustomers(),
          apiService.getAllHoardings(),
          apiService.getAllExternalHoardings().catch(() => []),
          apiService.getAllSites().catch(() => []),
          apiService.getAllOutsideSites().catch(() => []),
          apiService.getAllCustomerTerms().catch(() => []),
          apiService.getAllQuotations().catch(() => []),
          apiService.getAllQuotationLines().catch(() => []),
          apiService.getAllPaymentFreqs().catch(() => []),
          apiService.getAllCustomerContracts().catch(() => []),                  // ← NEW
          apiService.getAllCustomerContractHoardingMaps().catch(() => []),       // ← NEW
          apiService.getAllQuotationCustomers().catch(() => []),
          apiService.getAllPerformaInvoices().catch(() => []),
          apiService.getAllCompanyDetails().catch(() => []),
          apiService.getAllQuotationCompanies().catch(() => []),
        ]);

        setCustomers(normalizeList(cRaw).map(normalizeCustomer).filter(Boolean));

        // START: Ensure external hoardings have isExternal flag set
        const rawHoardings = [
          ...normalizeList(hRaw),
          ...normalizeList(extRaw).map(eh => ({ ...eh, isExternal: true }))
        ];
        // Original code:
        // const rawHoardings = [...normalizeList(hRaw), ...normalizeList(extRaw)];
        // END: Ensure external hoardings have isExternal flag set
        const seenHoardings = new Set();
        const uniqueHoardings = [];
        for (const h of rawHoardings) {
          const normalized = normalizeHoarding(h);
          const id = normalized.hoardingID;
          if (id > 0 && !seenHoardings.has(id)) {
            seenHoardings.add(id);
            uniqueHoardings.push(normalized);
          }
        }
        setHoardings(uniqueHoardings);

        setSites(normalizeList(sRaw).map(normalizeSite).filter(Boolean));
        setOutsideSites(normalizeList(osRaw).map(normalizeOutsideSite).filter(Boolean));
        setTermsList(normalizeList(tRaw));
        setQuotations(normalizeList(qRaw).map(normalizeQuotation));
        setQuotLines(normalizeList(qlRaw).map(normalizeQuotLine));
        setProformaInvoices(normalizeList(invoicesRaw));
        setCompanyDetailsList(normalizeList(companyDetailsRaw).map(normalizeCompany));
        setQuotationCompanies(normalizeList(quotationCompRaw).map(qc => ({
          quotationCompanyID: qc.quotationCompanyID ?? qc.QuotationCompanyID ?? 0,
          companyID: qc.companyID ?? qc.CompanyID ?? 0,
          quotationID: qc.quotationID ?? qc.QuotationID ?? 0,
          quotationRevisionNumber: qc.quotationRevisionNumber ?? qc.QuotationRevisionNumber ?? 0,
        })));
        const freqList = normalizeList(pRaw);
        setPaymentFreqs(freqList.map(f => ({
          value: f.paymentFreqID ?? f.PaymentFreqID,
          label: f.freqName ?? f.FreqName ?? f.name ?? String(f.paymentFreqID ?? ''),
        })));
        const contractedIds = new Set(
          normalizeList(quotCustRaw)
            .map(qc => Number(
              qc.quotation_ID ?? qc.quotationID ?? qc.QuotationID ??
              qc.Quotation_ID ?? 0
            ))
            .filter(id => id > 0)
        );
        setContractedQuotIds(contractedIds);
        // ← NEW: store contracts + maps for conflict detection
        setAllContracts(normalizeList(contractsRaw).map(c => ({
          customerContractID: c.customerContractID ?? c.CustomerContractID,
          customerID: c.customerID ?? c.CustomerID,
          startDate: (c.startDate ?? c.StartDate ?? '').split('T')[0],
          endDate: (c.endDate ?? c.EndDate ?? '').split('T')[0],
          status: c.status ?? c.Status ?? '',
        })));
        setAllContractMaps(normalizeList(mapsRaw));
      } catch (err) {
        setApiError(err?.response?.data?.message || err?.message || 'Failed to load data.');
      } finally { setLoading(false); }

      // Load merges separately — isolated so it can never break main data loading
      try {
        const mRaw = await apiService.getAllQuotationMerges();
        setQuotMerges(normalizeList(mRaw).map(m => ({
          quotationMergeID: Number(m.quotationMergeID ?? m.QuotationMergeID ?? 0),
          quotationLineNumber: Number(m.quotationLineNumber ?? m.QuotationLineNumber ?? 0),
          quotationID: Number(m.quotationID ?? m.QuotationID ?? 0),
          quotationRevisionNumber: Number(m.quotationRevisionNumber ?? m.QuotationRevisionNumber ?? 0),
          hoardingID: Number(m.hoardingID ?? m.HoardingID ?? 0),
          mergeAlongFlag: m.mergeAlongFlag ?? m.MergeAlongFlag ?? 'H',
        })));
      } catch {
        setQuotMerges([]);
      }
    })();
  }, []);
  useEffect(() => {
    if (!showPrintTypeDD) return;
    const handler = (e) => {
      if (!printTypeBtnRef.current?.contains(e.target) &&
        !printTypePanelRef.current?.contains(e.target)) {
        setShowPrintTypeDD(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showPrintTypeDD]);
  /* ── Refresh quotations ── */
  const refreshQuotations = useCallback(async () => {
    try {
      const [qRaw, qlRaw, invoicesRaw] = await Promise.all([
        apiService.getAllQuotations(),
        apiService.getAllQuotationLines(),
        apiService.getAllPerformaInvoices().catch(() => []),
      ]);
      setQuotations(normalizeList(qRaw).map(normalizeQuotation));
      setQuotLines(normalizeList(qlRaw).map(normalizeQuotLine));
      setProformaInvoices(normalizeList(invoicesRaw));

      // ← Also reload merges so handleViewPDF sees newly saved ones
      try {
        const mRaw = await apiService.getAllQuotationMerges();
        setQuotMerges(normalizeList(mRaw).map(m => ({
          quotationMergeID: Number(m.quotationMergeID ?? m.QuotationMergeID ?? 0),
          quotationLineNumber: Number(m.quotationLineNumber ?? m.QuotationLineNumber ?? 0),
          quotationID: Number(m.quotationID ?? m.QuotationID ?? 0),
          quotationRevisionNumber: Number(m.quotationRevisionNumber ?? m.QuotationRevisionNumber ?? 0),
          hoardingID: Number(m.hoardingID ?? m.HoardingID ?? 0),
          mergeAlongFlag: m.mergeAlongFlag ?? m.MergeAlongFlag ?? 'H',
        })));
      } catch { /* silent — merged rows just won't show if this fails */ }

      // ← Also reload quotation companies
      try {
        const qcRaw = await apiService.getAllQuotationCompanies();
        setQuotationCompanies(normalizeList(qcRaw).map(qc => ({
          quotationCompanyID: qc.quotationCompanyID ?? qc.QuotationCompanyID ?? 0,
          companyID: qc.companyID ?? qc.CompanyID ?? 0,
          quotationID: qc.quotationID ?? qc.QuotationID ?? 0,
          quotationRevisionNumber: qc.quotationRevisionNumber ?? qc.QuotationRevisionNumber ?? 0,
        })));
      } catch (err) {
        console.warn('Failed to refresh quotation companies:', err);
      }

      setDeleteMode(false);
      setSelectedQuotIds(new Set());
      showToast('List refreshed', 'success');
    } catch (err) {
      showToast('Refresh failed: ' + (err?.message || 'Unknown error'), 'error');
    }
  }, [showToast]);

  /* ── NEW: handle customer saved from CustomerEditModal ── */
  const handleCustomerSaved = useCallback((updated) => {
    setCustomers(prev => prev.map(c =>
      c.customerID === updated.customerID ? { ...c, ...updated } : c
    ));
    if (selectedCustomer?.customerID === updated.customerID) {
      setSelectedCustomer(prev => ({ ...prev, ...updated }));
    }
  }, [selectedCustomer]);

  // Keep printing rows in sync with available hoarding sizes
  useEffect(() => {
    const hoardingSizes = {};
    rows.forEach(r => {
      if (r.rowType === 'hoarding' && r.size) {
        if (!hoardingSizes[r.size]) hoardingSizes[r.size] = { sqFt: r.sqFt || 0, count: 0 };
        hoardingSizes[r.size].count++;
      } else if (r.rowType === 'merged' && r.size) {
        const key = `${r.size} (Merged)`;
        if (!hoardingSizes[key]) hoardingSizes[key] = { sqFt: r.sqFt || 0, count: 0 };
        hoardingSizes[key].count++;
      }
    });

    let needsUpdate = false;
    const updated = rows.map(r => {
      if (r.rowType !== 'printing' || !r.size) return r;
      const info = hoardingSizes[r.size];
      if (!info) {
        // Size no longer exists, reset print row size info
        needsUpdate = true;
        return { ...r, size: '', sqFt: 0, nos: 0, amount: 0 };
      }
      const targetSqFt = info.count * info.sqFt;
      const targetNos = info.count;
      const targetAmt = targetSqFt * Number(r.ratePerMonth || 0);
      if (r.sqFt !== targetSqFt || r.nos !== targetNos || r.amount !== targetAmt) {
        needsUpdate = true;
        return { ...r, sqFt: targetSqFt, nos: targetNos, amount: targetAmt };
      }
      return r;
    });

    if (needsUpdate) {
      setRows(updated);
    }
  }, [rows]);

  /* ── Calculations ── */
  const subTotal = useMemo(() => rows.reduce((s, r) => s + Number(r.amount || 0), 0), [rows]);
  const cgstAmt = useMemo(() => (subTotal * Number(cgstPct || 0)) / 100, [subTotal, cgstPct]);
  const sgstAmt = useMemo(() => (subTotal * Number(sgstPct || 0)) / 100, [subTotal, sgstPct]);
  const grossTotal = useMemo(() => subTotal + cgstAmt + sgstAmt, [subTotal, cgstAmt, sgstAmt]);
  const finalTotal = useMemo(() => Math.round(grossTotal), [grossTotal]);
  const roundOff = useMemo(() => finalTotal - grossTotal, [finalTotal, grossTotal]);

  const tableTotals = useMemo(() => {
    let nos = 0;
    let rate = 0;
    let printCost = 0;
    let amount = 0;
    rows.forEach(r => {
      if (r.rowType !== 'extra') nos += Number(r.nos || 0);
      if (r.rowType === 'hoarding' || r.rowType === 'merged') rate += Number(r.ratePerMonth || 0);
      if (r.rowType === 'hoarding' || r.rowType === 'merged') {
        printCost += Number(r.printingCost || 0);
      } else if (r.rowType === 'printing') {
        printCost += Number(r.amount || 0);
      }
      amount += Number(r.amount || 0);
    });
    return { nos, rate, printCost, amount };
  }, [rows]);

  /* ── Row operations ── */
  const updateRow = useCallback((id, field, val) => {
    setRows(prev => prev.map(r => {
      if (r._id !== id) return r;

      // Validation to prevent negative values
      if (['ratePerMonth', 'printingCost', 'amount', 'days', 'printRate'].includes(field)) {
        if (Number(val) < 0) {
          showToast(`${field === 'ratePerMonth' ? 'Rate' : field === 'printingCost' ? 'Print Cost' : field === 'printRate' ? 'Print Rate/Sq.Ft' : field} cannot be negative`, 'error');
          return r;
        }
      }

      let u = { ...r, [field]: val };

      if (u.rowType === 'extra') {
        // extra charge: amount is directly editable — no auto-calculation
      } else if (u.rowType === 'printing') {
        // printing: sqFt × ratePerMonth
        if (['nos', 'sqFt', 'ratePerMonth'].includes(field)) {
          u.amount = Number(u.sqFt || 0) * Number(u.ratePerMonth || 0);
        }
      } else {
        // hoarding & merged rows:
        if (field === 'startDate') {
          const computedEnd = calculateEndDate(val, u.days);
          u.endDate = computedEnd;
          u.nos = calcNOSFromDays(u.days);
          u.printingCost = Number(u.sqFt || 0) * Number(u.printRate || 0);
          u.amount = (u.nos * Number(u.ratePerMonth || 0)) + u.printingCost;
        } else if (field === 'days') {
          const computedEnd = calculateEndDate(u.startDate, val);
          u.endDate = computedEnd;
          u.nos = calcNOSFromDays(val);
          u.printingCost = Number(u.sqFt || 0) * Number(u.printRate || 0);
          u.amount = (u.nos * Number(u.ratePerMonth || 0)) + u.printingCost;
        } else if (field === 'printRate') {
          u.printingCost = Number(u.sqFt || 0) * Number(val);
          u.amount = (u.nos * Number(u.ratePerMonth || 0)) + u.printingCost;
        } else if (field === 'ratePerMonth') {
          u.amount = (u.nos * Number(val || 0)) + Number(u.printingCost || 0);
        } else if (field === 'printType') {
          // just update the printType
        }
      }

      return u;
    }));
  }, []);

  // Also add updateRowMultiple for printing-row size selection:
  const updateRowMultiple = useCallback((id, updates) => {
    setRows(prev => prev.map(r => {
      if (r._id !== id) return r;
      const u = { ...r, ...updates };
      if (u.rowType === 'printing') {
        u.amount = Number(u.sqFt || 0) * Number(u.ratePerMonth || 0);
      }
      return u;
    }));
  }, []);

  const deleteRow = useCallback((id) => {
    setRows(p => p.filter(r => r._id !== id));
  }, []);
  // Original code:
  // const existingHoardingIds = useMemo(() => new Set(rows.map(r => r.hoardingID).filter(Boolean)), [rows]);
  // Changed to also include mergedHoardingIDs of merged rows so they show as disabled in the Add Hoarding popup:
  const existingHoardingIds = useMemo(() => {
    const ids = new Set();
    rows.forEach(r => {
      if (r.hoardingID) {
        ids.add(Number(r.hoardingID));
      }
      if (Array.isArray(r.mergedHoardingIDs)) {
        r.mergedHoardingIDs.forEach(id => {
          if (id) {
            ids.add(Number(id));
          }
        });
      }
    });
    return ids;
  }, [rows]);

  const applyGlobalDates = useCallback(() => {
    if (!globalStart) return;
    if (!globalDays) return;

    const computedEnd = calculateEndDate(globalStart, globalDays);
    const nos = calcNOSFromDays(globalDays);

    const updatedRows = rows.map(r => {
      if (r.rowType !== 'hoarding' && r.rowType !== 'merged') return r;
      const printingCost = Number(r.sqFt || 0) * Number(r.printRate || 0);
      const baseRent = nos * Number(r.ratePerMonth || 0);
      const amount = baseRent + printingCost;
      return {
        ...r,
        startDate: globalStart,
        endDate: computedEnd,
        days: Number(globalDays),
        nos: nos,
        printingCost,
        amount
      };
    });
    setRows(updatedRows);
    // ── conflict check ──
    const conflicts = checkHoardingDateConflicts(updatedRows, allContracts, allContractMaps, customers);
    if (conflicts.length > 0) { setConflictWarnings(conflicts); setShowConflictModal(true); }
  }, [globalStart, globalDays, rows, allContracts, allContractMaps, customers]);

  const handleAddSelected = (selectedIds) => {
    const toAdd = hoardings
      .filter(h => selectedIds.has(h.hoardingID) && !rows.find(r => r.hoardingID === h.hoardingID))
      .map(h => newHoardingRow(h, globalStart, globalEnd, siteMap, outsideSiteMap));
    const nextRows = [...rows, ...toAdd];
    setRows(nextRows);
    setShowHoardModal(false);
    // ── conflict check ──
    const conflicts = checkHoardingDateConflicts(nextRows, allContracts, allContractMaps, customers);
    if (conflicts.length > 0) { setConflictWarnings(conflicts); setShowConflictModal(true); }
  };

  const handleAddManual = (selectedIds) => {
    const toAdd = hoardings
      .filter(h => selectedIds.has(h.hoardingID) && !rows.find(r => r.hoardingID === h.hoardingID))
      .map(h => newHoardingRow(h, globalStart, globalEnd, siteMap, outsideSiteMap));
    const nextRows = [...rows, ...toAdd];
    setRows(nextRows);
    setShowManualModal(false);
    // ── conflict check ──
    const conflicts = checkHoardingDateConflicts(nextRows, allContracts, allContractMaps, customers);
    if (conflicts.length > 0) { setConflictWarnings(conflicts); setShowConflictModal(true); }
  };

  const handleAddExternal = (selectedExternalHoardings) => {
    const withExtFlag = selectedExternalHoardings.map(h => ({ ...h, isExternal: true }));
    setHoardings(prev => {
      const existingIds = new Set(prev.map(h => h.hoardingID));
      const newItems = withExtFlag.filter(h => !existingIds.has(h.hoardingID));
      return [...prev, ...newItems];
    });

    const toAdd = withExtFlag
      .filter(h => !rows.find(r => r.hoardingID === h.hoardingID))
      .map(h => newHoardingRow(h, globalStart, globalEnd, siteMap, outsideSiteMap));
    const nextRows = [...rows, ...toAdd];
    setRows(nextRows);
    setShowExternalModal(false);
    // ── conflict check ──
    const conflicts = checkHoardingDateConflicts(nextRows, allContracts, allContractMaps, customers);
    if (conflicts.length > 0) { setConflictWarnings(conflicts); setShowConflictModal(true); }
  };

  const handleMerge = useCallback((selectedRows, direction) => {
    if (!selectedRows || selectedRows.length < 2) return;
    const isExtRow = (r) => Boolean(
      r?.isExternal === true ||
      String(r?.isExternal).toLowerCase() === 'true' ||
      r?.is_External === true ||
      String(r?.is_External).toLowerCase() === 'true' ||
      r?.IsExternal === true ||
      String(r?.IsExternal).toLowerCase() === 'true' ||
      r?.Is_External === true ||
      String(r?.Is_External).toLowerCase() === 'true'
    );
    const firstExt = isExtRow(selectedRows[0]);
    const allSameExt = selectedRows.every(r => isExtRow(r) === firstExt);
    if (!allSameExt) {
      showToast('Cannot merge internal and external hoardings together.', 'error');
      return;
    }
    const merged = newMergedRow(selectedRows, direction);
    setRows(prev => {
      const ids = new Set(selectedRows.map(r => r._id));
      return [...prev.filter(r => !ids.has(r._id)), merged];
    });
    setShowMergeModal(false);
    showToast(
      `${selectedRows.length} hoarding${selectedRows.length !== 1 ? 's' : ''} merged ` +
      `(${direction === 'S' ? 'Square' : (direction === 'H' ? 'Horizontal' : 'Vertical')}) · Size: ${merged.size}`,
      'success'
    );
  }, [showToast]);
  const toggleMergeDirection = useCallback((rowId) => {
    setRows(prev => prev.map(r => {
      if (r._id !== rowId || r.rowType !== 'merged' || r.mergeDirection === 'S') return r;

      const newDir = r.mergeDirection === 'H' ? 'V' : 'H';
      const hIds = r.mergedHoardingIDs || [];

      // Look up original dimensions from the hoardings array
      const sizes = hIds
        .map(hid => hoardings.find(hh => hh.hoardingID === hid))
        .filter(Boolean)
        .map(h => ({ w: h.width || 0, h: h.height || 0 }));

      if (sizes.length < 2) return { ...r, mergeDirection: newDir }; // no data, just flip label

      const gaps = hIds.length - 1;
      let mw, mh;
      if (newDir === 'H') {
        mw = sizes.reduce((s, sz) => s + sz.w, 0) + gaps;
        mh = Math.max(...sizes.map(s => s.h));
      } else {
        mw = Math.max(...sizes.map(s => s.w));
        mh = sizes.reduce((s, sz) => s + sz.h, 0) + gaps;
      }

      return { ...r, mergeDirection: newDir, size: `${mw} X ${mh}`, sqFt: mw * mh };
    }));
  }, [hoardings]);

  const toggleTerm = (termID) => {
    setSelectedTerms(p => {
      if (p.includes(termID)) return p.filter(i => i !== termID);
      return [...p, termID];
    });
  };

  const saveQuotationTerms = async (quotationID, revisionNo, termIDs) => {
    let existingTerms = [];
    try {
      const existingTermsRaw = await apiService.getQuotationTerms(quotationID, revisionNo);
      existingTerms = normalizeList(existingTermsRaw) || [];
    } catch (err) {
      console.warn('No existing terms found or error fetching:', err);
    }

    try {
      const selectedSet = new Set(termIDs.map(Number));
      const toDelete = existingTerms.filter(t => {
        const tid = Number(t.termId ?? t.TermId ?? t.termID ?? t.TermID ?? 0);
        return !selectedSet.has(tid);
      });

      await Promise.all(
        toDelete.map(t => {
          const termRecordId = t.id ?? t.Id ?? t.ID ?? 0;
          if (termRecordId > 0) {
            return apiService.deleteQuotationTerm(termRecordId).catch(err => console.warn('Failed to delete term record:', termRecordId, err));
          }
          return Promise.resolve();
        })
      );

      const existingMap = new Map();
      existingTerms.forEach(t => {
        const tid = Number(t.termId ?? t.TermId ?? t.termID ?? t.TermID ?? 0);
        if (tid > 0) {
          existingMap.set(tid, t);
        }
      });

      await Promise.all(
        termIDs.map(termId => {
          const existing = existingMap.get(Number(termId));
          if (existing) {
            return apiService.updateQuotationTerm({
              id: existing.id ?? existing.Id ?? existing.ID ?? 0,
              termId: Number(termId),
              quotationId: Number(quotationID),
              quotationRevisionNumber: Number(revisionNo)
            });
          } else {
            return apiService.createQuotationTerm({
              id: 0,
              termId: Number(termId),
              quotationId: Number(quotationID),
              quotationRevisionNumber: Number(revisionNo)
            });
          }
        })
      );
    } catch (termErr) {
      console.warn('Error saving quotation terms:', termErr);
    }
  };

  const resetForm = () => {
    setSelectedCustomer(null); setWithPrinting(false);
    setQuotNo(''); setQuotDate(todayISO());
    setRevisionNo(1);
    setRows([]);
    setCgstPct(9); setSgstPct(9);
    setSelectedTerms([]);
    setStep1Error(''); setStep2Error('');
    setEditingQuotID(null);
    setOriginalQuotID(0);   // ← NEW
    setGlobalStart(''); setGlobalEnd('');
    setGlobalDays('');
    setSelectedCompanyId('');
    setQuotationCompanyRecId(0);
    setHighlightTerms(false);
    setShowTermsConfirmModal(false);
  };

  const handleStartNew = async () => {
    const isActive = await checkSeriesActive('Quotation');
    if (!isActive) return;
    resetForm();
    setStep(1);
    setIsCreating(true);
    // Fetch auto-generated quotation number from backend
    try {
      const res = await apiService.getNextQuotationNumber();
      // res could be a string, or object with a field
      const num = typeof res === 'string' ? res
        : res?.quotationNumber ?? res?.number ?? res?.nextNumber
        ?? res?.seriesNumber ?? res?.value ?? res?.data ?? '';
      if (num) setQuotNo(String(num));
    } catch (err) {
      console.warn('[QuotNo] Could not fetch auto number:', err?.message);
    }
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  const handleEditQuotation = (quot) => {
    const myLines = quotLines.filter(l =>
      Number(l.quotationID) === Number(quot.quotationID) &&
      Number(l.quotationRevisionNumber) === Number(quot.quotationRevisionNumber)
    );
    const myMerges = quotMerges.filter(m =>
      Number(m.quotationID) === Number(quot.quotationID) &&
      Number(m.quotationRevisionNumber) === Number(quot.quotationRevisionNumber)
    );
    const cust = customers.find(c => c.customerID === quot.customerID) || null;

    setSelectedCustomer(cust);
    setQuotNo(quot.quotationNumber);
    setQuotDate(quot.quotationDate ? quot.quotationDate.split('T')[0] : todayISO());
    setRevisionNo(quot.quotationRevisionNumber || 1);
    setEditingQuotID(quot.quotationID);

    setSelectedCompanyId('');
    setQuotationCompanyRecId(0);
    apiService.getQuotationCompanyByQuotation(quot.quotationID, quot.quotationRevisionNumber)
      .then(res => {
        const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
        if (list && list.length > 0) {
          const qc = list[0];
          setSelectedCompanyId(qc.company_ID ?? qc.companyID ?? qc.CompanyID ?? 0);
          setQuotationCompanyRecId(qc.quotationCompany_ID ?? qc.quotationCompanyID ?? qc.QuotationCompanyID ?? 0);
        }
      })
      .catch(err => {
        console.error("Error fetching linked company details:", err);
      });

    const regularRows = myLines
      .filter(l => !l.mergeFlag)
      .map(l => {
        if (!l.hoardingID && l.purpose) {
          const isPrinting = PRINTING_TYPES.some(pt =>
            pt.toLowerCase() === (l.purpose || '').toLowerCase()
          );
          const days = calculateDays(l.periodBeginDate, l.periodEndDate) || 30;
          return {
            _id: uid(),
            rowType: isPrinting ? 'printing' : 'extra',
            hoardingID: 0, siteID: null, siteObj: null,
            location: l.purpose, hoardingCode: '', size: '', sqFt: 0, nos: 1,
            startDate: l.periodBeginDate || '', endDate: l.periodEndDate || '',
            days: days,
            ratePerMonth: l.rentAmount || 0, amount: l.rentAmount || 0,
            printType: '', printRate: 0,
            printingCost: 0,
            quotationLineNumber: l.quotationLineNumber,
            saved: true,
          };
        }
        const h = hoardings.find(hh => hh.hoardingID === l.hoardingID);
        const siteObj = getHoardingSite(h, siteMap, outsideSiteMap);
        const siteID = toSID(siteObj?.outsideSiteID ?? siteObj?.siteID) ?? toSID(h?.siteID) ?? null;

        const isExt = h?.isExternal === true || String(h?.isExternal).toLowerCase() === 'true' ||
          h?.is_External === true || String(h?.is_External).toLowerCase() === 'true' ||
          h?.IsExternal === true || String(h?.IsExternal).toLowerCase() === 'true' ||
          h?.Is_External === true || String(h?.Is_External).toLowerCase() === 'true';

        const { ratePerMonth, printingCost, printType, printRate } = parsePurposeMeta(l.purpose, h?.monthlyRent || 0);
        const days = calculateDays(l.periodBeginDate, l.periodEndDate) || 30;

        return {
          _id: uid(),
          rowType: 'hoarding',
          hoardingID: l.hoardingID, siteID, siteObj,
          isExternal: isExt,
          location: buildSiteAddress(siteObj, h?.hoardingCode || ''),
          hoardingCode: h?.hoardingCode || '',
          size: h ? `${h.width} X ${h.height}` : '',
          sqFt: h ? (h.width * h.height) : 0,
          nos: calcNOSFromDays(days),
          startDate: l.periodBeginDate || '', endDate: l.periodEndDate || '',
          days: days,
          ratePerMonth: ratePerMonth,
          amount: l.rentAmount || 0,
          printType: printType,
          printRate: printRate,
          printingCost: printingCost,
          quotationLineNumber: l.quotationLineNumber,
          saved: true,
        };
      });

    const mergedRows = [];
    if (myMerges.length >= 2) {
      const byLine = new Map();
      for (const m of myMerges) {
        const ln = m.quotationLineNumber;
        if (!byLine.has(ln)) byLine.set(ln, []);
        byLine.get(ln).push(m);
      }
      const usedLineNums = new Set();
      for (const [ln, records] of byLine.entries()) {
        if (records.length < 2) continue;

        const dir = records[0].mergeAlongFlag === 'S' ? 'S' : (records[0].mergeAlongFlag === 'H' ? 'H' : 'V');
        const hoardingObjs = records
          .map(r => hoardings.find(h => h.hoardingID === r.hoardingID))
          .filter(Boolean);

        const sizes = hoardingObjs.map(h => ({ w: h.width || 0, h: h.height || 0 }));
        const gaps = hoardingObjs.length - 1;
        let mw, mh;
        if (dir === 'S') {
          const singleW = sizes[0]?.w || 0;
          const singleH = sizes[0]?.h || 0;
          mw = (singleW * 2) + 1;
          mh = (singleH * 2) + 1;
        } else if (dir === 'H') {
          mw = sizes.reduce((s, sz) => s + sz.w, 0) + gaps;
          mh = Math.max(...sizes.map(s => s.h));
        } else {
          mw = Math.max(...sizes.map(s => s.w));
          mh = sizes.reduce((s, sz) => s + sz.h, 0) + gaps;
        }

        const locations = hoardingObjs.map(h => {
          const site = getHoardingSite(h, siteMap, outsideSiteMap);
          return site
            ? `${site.addressLine1 || ''}${site.city ? `, ${site.city}` : ''}`
            : h.hoardingCode || '';
        });
        const codes = hoardingObjs.map(h => h.hoardingCode || '').join(' + ');
        const locFallback = [...new Set(locations)].join(' + ');

        let savedLine = myLines.find(l => Number(l.quotationLineNumber) === Number(ln) && !usedLineNums.has(Number(l.quotationLineNumber)));
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)) && l.purpose && (
            l.purpose.includes(locFallback) ||
            locFallback.includes(l.purpose.split('|')[0]) ||
            (codes && l.purpose.includes(codes))
          ));
        }
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)) && l.purpose && hoardingObjs.some(ho => {
            const loc = buildSiteAddress(ho.siteID ? siteMap.get(ho.siteID) : null, ho.hoardingCode);
            return l.purpose.includes(ho.hoardingCode) || l.purpose.includes(loc);
          }));
        }
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)));
        }

        if (savedLine) {
          usedLineNums.add(Number(savedLine.quotationLineNumber));
        }

        const totalRent = hoardingObjs.reduce((s, ho) => s + (ho.monthlyRent || 0), 0);
        const { ratePerMonth, printingCost, printType, printRate } = parsePurposeMeta(savedLine?.purpose, totalRent);
        const days = calculateDays(savedLine?.periodBeginDate, savedLine?.periodEndDate) || 30;

        const isExt = hoardingObjs.some(ho => ho.isExternal === true || String(ho.isExternal).toLowerCase() === 'true' || ho.is_External === true || String(ho.is_External).toLowerCase() === 'true' || ho.IsExternal === true || String(ho.IsExternal).toLowerCase() === 'true');
        mergedRows.push({
          _id: uid(),
          rowType: 'merged',
          isMerged: true,
          isExternal: isExt,
          mergeDirection: dir,
          mergedHoardingIDs: hoardingObjs.map(ho => ho.hoardingID),
          hoardingID: 0, siteID: null,
          location: savedLine?.purpose?.split('|')?.[0] || locFallback,
          hoardingCode: codes,
          size: `${mw} X ${mh}`,
          sqFt: mw * mh,
          nos: calcNOSFromDays(days),
          startDate: savedLine?.periodBeginDate || '',
          endDate: savedLine?.periodEndDate || '',
          days: days,
          ratePerMonth: ratePerMonth,
          amount: savedLine?.rentAmount || 0,
          printType: printType,
          printRate: printRate,
          printingCost: printingCost,
          quotationLineNumber: savedLine?.quotationLineNumber || 0,
          saved: true,
        });
      }
    }

    const allRows = [...regularRows, ...mergedRows].sort((a, b) =>
      Number(a.quotationLineNumber || 0) - Number(b.quotationLineNumber || 0)
    );
    setRows(allRows);
    const hasPrinting = allRows.some(r => r.rowType === 'printing') || allRows.some(r => Number(r.printingCost || 0) > 0);
    setWithPrinting(hasPrinting);
    setCgstPct(quot.cGSTPercent ?? 9);
    setSgstPct(quot.sGSTPercent ?? 9);
    setStep1Error(''); setStep2Error('');
    apiService.getQuotationTerms(quot.quotationID, quot.quotationRevisionNumber)
      .then(termRes => {
        const list = normalizeList(termRes);
        const termIDs = list.map(t => Number(t.termId ?? t.TermId ?? t.termID ?? t.TermID ?? 0)).filter(id => id > 0);
        setSelectedTerms(termIDs);
      })
      .catch((err) => {
        console.warn('Failed to load terms:', err);
        setSelectedTerms([]);
      });

    // ── Derive global period from the loaded rows ──
    const datedRows = allRows.filter(r => (r.rowType === 'hoarding' || r.rowType === 'merged') && r.startDate && r.endDate);
    if (datedRows.length > 0) {
      const gStart = datedRows.reduce((min, r) => (r.startDate < min ? r.startDate : min), datedRows[0].startDate);
      const gEnd = datedRows.reduce((max, r) => (r.endDate > max ? r.endDate : max), datedRows[0].endDate);
      setGlobalStart(gStart);
      setGlobalEnd(gEnd);
      setGlobalDays(calculateDays(gStart, gEnd) || '');
    } else {
      setGlobalStart(''); setGlobalEnd('');
      setGlobalDays('');
    }

    setStep(1); setIsCreating(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  const handleReopenHistory = async (quot) => {
    const myLines = quotLines.filter(l =>
      Number(l.quotationID) === Number(quot.quotationID) &&
      Number(l.quotationRevisionNumber) === Number(quot.quotationRevisionNumber)
    );
    const myMerges = quotMerges.filter(m =>
      Number(m.quotationID) === Number(quot.quotationID) &&
      Number(m.quotationRevisionNumber) === Number(quot.quotationRevisionNumber)
    );
    const cust = customers.find(c => c.customerID === quot.customerID) || null;
    setSelectedCustomer(cust);
    setQuotNo(quot.quotationNumber);
    setQuotDate(todayISO());
    setRevisionNo((quot.quotationRevisionNumber || 1) + 1);

    setSelectedCompanyId('');
    setQuotationCompanyRecId(0);
    try {
      const res = await apiService.getQuotationCompanyByQuotation(quot.quotationID, quot.quotationRevisionNumber);
      const list = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      if (list && list.length > 0) {
        const qc = list[0];
        setSelectedCompanyId(qc.company_ID ?? qc.companyID ?? qc.CompanyID ?? 0);
      }
    } catch (err) {
      console.error("Error fetching linked company details for revision:", err);
    }

    const regularRows = myLines
      .filter(l => !l.mergeFlag)
      .map(l => {
        if (!l.hoardingID && l.purpose) {
          const isPrinting = PRINTING_TYPES.some(pt =>
            pt.toLowerCase() === (l.purpose || '').toLowerCase()
          );
          const days = calculateDays(l.periodBeginDate, l.periodEndDate) || 30;
          return {
            _id: uid(),
            rowType: isPrinting ? 'printing' : 'extra',
            hoardingID: 0,
            siteID: null,
            siteObj: null,
            location: l.purpose,
            hoardingCode: '',
            size: '',
            sqFt: 0,
            nos: 1,
            startDate: l.periodBeginDate || '',
            endDate: l.periodEndDate || '',
            days: days,
            ratePerMonth: l.rentAmount || 0,
            amount: l.rentAmount || 0,
            printType: '',
            printRate: 0,
            printingCost: 0,
            quotationLineNumber: l.quotationLineNumber,
            saved: false,
          };
        }
        const h = hoardings.find(hh => hh.hoardingID === l.hoardingID);
        const siteObj = getHoardingSite(h, siteMap, outsideSiteMap);
        const siteID = toSID(siteObj?.outsideSiteID ?? siteObj?.siteID) ?? toSID(h?.siteID) ?? null;

        const isExt = h?.isExternal === true || String(h?.isExternal).toLowerCase() === 'true' ||
          h?.is_External === true || String(h?.is_External).toLowerCase() === 'true' ||
          h?.IsExternal === true || String(h?.IsExternal).toLowerCase() === 'true' ||
          h?.Is_External === true || String(h?.Is_External).toLowerCase() === 'true';

        const { ratePerMonth, printingCost, printType, printRate } = parsePurposeMeta(l.purpose, h?.monthlyRent || 0);
        const days = calculateDays(l.periodBeginDate, l.periodEndDate) || 30;

        return {
          _id: uid(),
          rowType: 'hoarding',
          hoardingID: l.hoardingID,
          siteID,
          siteObj,
          isExternal: isExt,
          location: buildSiteAddress(siteObj, h?.hoardingCode || ''),
          hoardingCode: h?.hoardingCode || '',
          size: h ? `${h.width} X ${h.height}` : '',
          sqFt: h ? (h.width * h.height) : 0,
          nos: calcNOSFromDays(days),
          startDate: l.periodBeginDate || '',
          endDate: l.periodEndDate || '',
          days: days,
          ratePerMonth: ratePerMonth,
          amount: l.rentAmount || 0,
          printType: printType,
          printRate: printRate,
          printingCost: printingCost,
          quotationLineNumber: l.quotationLineNumber,
          saved: false,
        };
      });

    const mergedRows = [];
    if (myMerges.length >= 2) {
      const byLine = new Map();
      for (const m of myMerges) {
        const ln = m.quotationLineNumber;
        if (!byLine.has(ln)) byLine.set(ln, []);
        byLine.get(ln).push(m);
      }
      const usedLineNums = new Set();
      for (const [ln, records] of byLine.entries()) {
        if (records.length < 2) continue;

        const dir = records[0].mergeAlongFlag === 'S' ? 'S' : (records[0].mergeAlongFlag === 'H' ? 'H' : 'V');
        const hoardingObjs = records.map(r => hoardings.find(h => h.hoardingID === r.hoardingID)).filter(Boolean);

        const sizes = hoardingObjs.map(h => ({ w: h.width || 0, h: h.height || 0 }));
        const gaps = hoardingObjs.length - 1;
        let mw, mh;
        if (dir === 'S') {
          const singleW = sizes[0]?.w || 0;
          const singleH = sizes[0]?.h || 0;
          mw = (singleW * 2) + 1;
          mh = (singleH * 2) + 1;
        } else if (dir === 'H') {
          mw = sizes.reduce((s, sz) => s + sz.w, 0) + gaps;
          mh = Math.max(...sizes.map(s => s.h));
        } else {
          mw = Math.max(...sizes.map(s => s.w));
          mh = sizes.reduce((s, sz) => s + sz.h, 0) + gaps;
        }

        const locations = hoardingObjs.map(h => {
          const site = getHoardingSite(h, siteMap, outsideSiteMap);
          return site
            ? `${site.addressLine1 || ''}${site.city ? `, ${site.city}` : ''}`
            : h.hoardingCode || '';
        });
        const codes = hoardingObjs.map(h => h.hoardingCode || '').join(' + ');
        const locFallback = [...new Set(locations)].join(' + ');

        let savedLine = myLines.find(l => Number(l.quotationLineNumber) === Number(ln) && !usedLineNums.has(Number(l.quotationLineNumber)));
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)) && l.purpose && (
            l.purpose.includes(locFallback) ||
            locFallback.includes(l.purpose.split('|')[0]) ||
            (codes && l.purpose.includes(codes))
          ));
        }
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)) && l.purpose && hoardingObjs.some(ho => {
            const loc = buildSiteAddress(ho.siteID ? siteMap.get(ho.siteID) : null, ho.hoardingCode);
            return l.purpose.includes(ho.hoardingCode) || l.purpose.includes(loc);
          }));
        }
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)));
        }

        if (savedLine) {
          usedLineNums.add(Number(savedLine.quotationLineNumber));
        }

        const totalRent = hoardingObjs.reduce((s, ho) => s + (ho.monthlyRent || 0), 0);
        const { ratePerMonth, printingCost, printType, printRate } = parsePurposeMeta(savedLine?.purpose, totalRent);
        const days = calculateDays(savedLine?.periodBeginDate, savedLine?.periodEndDate) || 30;

        const isExt = hoardingObjs.some(ho => ho.isExternal === true || String(ho.isExternal).toLowerCase() === 'true' || ho.is_External === true || String(ho.is_External).toLowerCase() === 'true' || ho.IsExternal === true || String(ho.IsExternal).toLowerCase() === 'true');
        mergedRows.push({
          _id: uid(),
          rowType: 'merged',
          isMerged: true,
          isExternal: isExt,
          mergeDirection: dir,
          mergedHoardingIDs: hoardingObjs.map(h => h.hoardingID),
          hoardingID: 0, siteID: null,
          location: savedLine?.purpose?.split('|')?.[0] || locFallback,
          hoardingCode: codes,
          size: `${mw} X ${mh}`,
          sqFt: mw * mh,
          nos: calcNOSFromDays(days),
          startDate: savedLine?.periodBeginDate || '',
          endDate: savedLine?.periodEndDate || '',
          days: days,
          ratePerMonth: ratePerMonth,
          amount: savedLine?.rentAmount || 0,
          printType: printType,
          printRate: printRate,
          printingCost: printingCost,
          quotationLineNumber: savedLine?.quotationLineNumber || 0,
          saved: false,
        });
      }
    }

    const allRows = [...regularRows, ...mergedRows].sort((a, b) =>
      Number(a.quotationLineNumber || 0) - Number(b.quotationLineNumber || 0)
    );
    setRows(allRows);

    const hasPrinting = allRows.some(r => r.rowType === 'printing') || allRows.some(r => Number(r.printingCost || 0) > 0);
    setWithPrinting(hasPrinting);

    setCgstPct(quot.cGSTPercent ?? 9);
    setSgstPct(quot.sGSTPercent ?? 9);
    apiService.getQuotationTerms(quot.quotationID, quot.quotationRevisionNumber)
      .then(termRes => {
        const list = normalizeList(termRes);
        const termIDs = list.map(t => Number(t.termId ?? t.TermId ?? t.termID ?? t.TermID ?? 0)).filter(id => id > 0);
        setSelectedTerms(termIDs);
      })
      .catch((err) => {
        console.warn('Failed to load terms:', err);
        setSelectedTerms([]);
      });
    setStep1Error(''); setStep2Error('');
    setEditingQuotID(null);
    setOriginalQuotID(quot.quotationID);

    // ── Derive global period from the loaded rows ──
    const datedRows = allRows.filter(r => (r.rowType === 'hoarding' || r.rowType === 'merged') && r.startDate && r.endDate);
    if (datedRows.length > 0) {
      const gStart = datedRows.reduce((min, r) => (r.startDate < min ? r.startDate : min), datedRows[0].startDate);
      const gEnd = datedRows.reduce((max, r) => (r.endDate > max ? r.endDate : max), datedRows[0].endDate);
      setGlobalStart(gStart);
      setGlobalEnd(gEnd);
      setGlobalDays(calculateDays(gStart, gEnd) || '');
    } else {
      setGlobalStart(''); setGlobalEnd('');
      setGlobalDays('');
    }

    setStep(1); setIsCreating(true);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80);
  };

  const handleViewPDF = async (quot) => {
    const isActive = await checkSeriesActive('Quotation');
    if (!isActive) return;

    const myLines = quotLines.filter(l =>
      Number(l.quotationID) === Number(quot.quotationID) &&
      Number(l.quotationRevisionNumber) === Number(quot.quotationRevisionNumber)
    );
    const cust = customers.find(c => c.customerID === quot.customerID) || null;

    /* ── Build rows from QuotationLineDTL ── */
    const pdfRows = myLines
      .filter(l => !l.mergeFlag)   // exclude merged-line records; those come from QuotationMergeDTL below
      .map(l => {
        // ── Printing / Extra rows (hoardingID = 0, purpose set) ──
        if (!l.hoardingID && l.purpose) {
          const isPrinting = PRINTING_TYPES.some(pt =>
            pt.toLowerCase() === (l.purpose || '').toLowerCase()
          );
          return {
            rowType: isPrinting ? 'printing' : 'extra',
            hoardingID: 0,
            location: l.purpose,
            size: '',
            sqFt: 0,
            nos: 1,
            startDate: l.periodBeginDate,
            endDate: l.periodEndDate,
            ratePerMonth: l.rentAmount,
            amount: l.rentAmount,
            printingCost: 0,
          };
        }
        // ── Regular hoarding row ──
        const h = hoardings.find(hh => hh.hoardingID === l.hoardingID);
        const siteObj = getHoardingSite(h, siteMap, outsideSiteMap);

        const { ratePerMonth, printingCost, printType, printRate } = parsePurposeMeta(l.purpose, h?.monthlyRent || 0);
        const days = calculateDays(l.periodBeginDate, l.periodEndDate) || 30;

        const loc = siteObj ? `${siteObj.addressLine1 || ''}${siteObj.city ? `, ${siteObj.city}` : ''}` : h?.hoardingCode || '';

        return {
          rowType: 'hoarding',
          hoardingID: l.hoardingID,
          location: loc,
          size: h ? `${h.width} X ${h.height}` : '',
          sqFt: h ? (h.width * h.height) : 0,
          nos: calcNOSFromDays(days),
          startDate: l.periodBeginDate,
          endDate: l.periodEndDate,
          days: days,
          ratePerMonth: ratePerMonth,
          amount: l.rentAmount,
          printType: printType,
          printRate: printRate,
          printingCost: printingCost,
        };
      });

    /* ── Merged rows from QuotationMergeDTL ──
       Group by quotationLineNumber (≥2 records per merge group).
       Reconstruct merged size + location from source hoardings.
       Use the saved QuotationLineDTL merge-flag line for amount/dates.
    ── */
    const myMerges = quotMerges.filter(m =>
      Number(m.quotationID) === Number(quot.quotationID) &&
      Number(m.quotationRevisionNumber) === Number(quot.quotationRevisionNumber)
    );

    if (myMerges.length >= 2) {
      const byLine = new Map();
      for (const m of myMerges) {
        const ln = m.quotationLineNumber;
        if (!byLine.has(ln)) byLine.set(ln, []);
        byLine.get(ln).push(m);
      }

      const usedLineNums = new Set();
      const sortedKeys = [...byLine.keys()].sort((a, b) => a - b);
      for (const ln of sortedKeys) {
        const records = byLine.get(ln);
        if (records.length < 2) continue;

        const dir = records[0].mergeAlongFlag === 'S' ? 'S' : (records[0].mergeAlongFlag === 'H' ? 'H' : 'V');
        const hoardingObjs = records
          .map(r => hoardings.find(h => h.hoardingID === r.hoardingID))
          .filter(Boolean);

        const sizes = hoardingObjs.map(h => ({ w: h.width || 0, h: h.height || 0 }));
        const gaps = hoardingObjs.length - 1;
        let mw, mh;
        if (dir === 'S') {
          const singleW = sizes[0]?.w || 0;
          const singleH = sizes[0]?.h || 0;
          mw = (singleW * 2) + 1;
          mh = (singleH * 2) + 1;
        } else if (dir === 'H') {
          mw = sizes.reduce((s, sz) => s + sz.w, 0) + gaps;
          mh = Math.max(...sizes.map(s => s.h));
        } else {
          mw = Math.max(...sizes.map(s => s.w));
          mh = sizes.reduce((s, sz) => s + sz.h, 0) + gaps;
        }

        const locations = hoardingObjs.map(h => {
          const site = getHoardingSite(h, siteMap, outsideSiteMap);
          return site
            ? `${site.addressLine1 || ''}${site.city ? `, ${site.city}` : ''}`
            : h?.hoardingCode || '';
        });
        const codes = hoardingObjs.map(h => h.hoardingCode || '').join(' + ');

        const locFallback = [...new Set(locations)].join(' + ');
        let savedLine = myLines.find(l => Number(l.quotationLineNumber) === Number(ln) && !usedLineNums.has(Number(l.quotationLineNumber)));
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)) && l.purpose && (
            l.purpose.includes(locFallback) ||
            locFallback.includes(l.purpose.split('|')[0]) ||
            (codes && l.purpose.includes(codes))
          ));
        }
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)) && l.purpose && hoardingObjs.some(ho => {
            const loc = buildSiteAddress(ho.siteID ? siteMap.get(ho.siteID) : null, ho.hoardingCode);
            return l.purpose.includes(ho.hoardingCode) || l.purpose.includes(loc);
          }));
        }
        if (!savedLine) {
          savedLine = myLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)));
        }

        if (savedLine) {
          usedLineNums.add(Number(savedLine.quotationLineNumber));
        }

        const totalRent = hoardingObjs.reduce((s, ho) => s + (ho.monthlyRent || 0), 0);
        const { ratePerMonth, printingCost, printType, printRate } = parsePurposeMeta(savedLine?.purpose, totalRent);
        const days = calculateDays(savedLine?.periodBeginDate, savedLine?.periodEndDate) || 30;

        pdfRows.push({
          rowType: 'merged',
          isMerged: true,
          mergeDirection: dir,
          hoardingID: 0,
          location: locFallback,
          hoardingCode: codes,
          size: `${mw} X ${mh}`,
          sqFt: mw * mh,
          nos: calcNOSFromDays(days),
          startDate: savedLine?.periodBeginDate || '',
          endDate: savedLine?.periodEndDate || '',
          days: days,
          ratePerMonth: ratePerMonth,
          amount: savedLine ? savedLine.rentAmount : 0,
          printType: printType,
          printRate: printRate,
          printingCost: printingCost,
        });
      }
    }

    /* ── Open PDF ── */
    const storedSub = quot.totalAmount / (1 + (quot.cGSTPercent + quot.sGSTPercent) / 100);
    const storedCgst = (storedSub * quot.cGSTPercent) / 100;
    const storedSgst = (storedSub * quot.sGSTPercent) / 100;
    const storedGross = storedSub + storedCgst + storedSgst;
    const storedFinal = Math.round(storedGross);

    let dbTerms = [];
    try {
      const res = await apiService.getQuotationTerms(quot.quotationID, quot.quotationRevisionNumber);
      const list = normalizeList(res);
      dbTerms = list.map(t => Number(t.termId ?? t.TermId ?? t.termID ?? t.TermID ?? 0)).filter(id => id > 0);
    } catch (err) {
      console.warn('Failed to fetch terms:', err);
    }

    const companyDetails = await fetchCompanyDetailsForQuotation(quot.quotationID, quot.quotationRevisionNumber);

    const html = buildPrintHTML({
      rows: pdfRows,
      withPrinting: pdfRows.some(r => r.rowType === 'printing') || pdfRows.some(r => Number(r.printingCost || 0) > 0),
      selectedCustomer: cust,
      quotNo: quot.quotationNumber,
      quotDate: quot.quotationDate,
      revisionNo: quot.quotationRevisionNumber,
      cgstPct: quot.cGSTPercent,
      sgstPct: quot.sGSTPercent,
      subTotal: storedSub,
      cgstAmt: storedCgst,
      sgstAmt: storedSgst,
      roundOff: storedFinal - storedGross,
      finalTotal: storedFinal,
      selectedTerms: dbTerms,
      termsTexts: dbTerms.map(id => {
        const t = termsList.find(t => t.termID === id);
        return t?.description || '';
      }),
      companyDetails,
    });
    const win = window.open('', '_blank');
    if (win) { win.document.write(html); win.document.close(); }
  };

  const goNext = () => {
    if (step === 1) {
      if (!selectedCustomer) { setStep1Error('Please select a customer.'); return; }
      if (!globalStart) { setStep1Error('Global Period From date is required.'); return; }
      if (!globalDays) { setStep1Error('Global Period Days is required.'); return; }
      if (!globalEnd) { setStep1Error('Global Period To date is required.'); return; }
      if (globalEnd < globalStart) { setStep1Error('Global Period To date cannot be earlier than From date.'); return; }
      setStep1Error(''); setStep(2);
    } else if (step === 2) {
      if (rows.length === 0) { setStep2Error('Add at least one hoarding.'); return; }

      if (withPrinting) {
        // 1. Verify there is at least one hoarding row (or merged hoarding)
        const hasHoarding = rows.some(r => r.rowType === 'hoarding' || r.rowType === 'merged');
        if (!hasHoarding) {
          setStep2Error('Select any hoarding.');
          return;
        }

        // 2. Verify all printing rows have a size selected
        const hasUnselectedSize = rows.some(r => r.rowType === 'printing' && !r.size);
        if (hasUnselectedSize) {
          setStep2Error('Select the size from the dropdown.');
          return;
        }

        // 3. Validation for Printing Type & Print Cost/Sq.ft on hoarding/merged rows
        for (const r of rows) {
          if (r.rowType === 'hoarding' || r.rowType === 'merged') {
            const hasPrintType = !!r.printType;
            const hasPrintRate = r.printRate !== '' && r.printRate !== undefined && r.printRate !== null && Number(r.printRate) > 0;

            if (hasPrintType && !hasPrintRate) {
              setStep2Error('Print Cost/Sq.Ft is mandatory when Printing Type is selected.');
              return;
            }
            if (hasPrintRate && !hasPrintType) {
              setStep2Error('Printing Type is mandatory when Print Cost/Sq.Ft is entered.');
              return;
            }
          }
        }
      }

      // ── re-check conflicts before proceeding ──
      const conflicts = checkHoardingDateConflicts(rows, allContracts, allContractMaps, customers);
      if (conflicts.length > 0) {
        setConflictWarnings(conflicts);
        setShowConflictModal(true);
        setStep2Error(`${conflicts.length} hoarding${conflicts.length !== 1 ? 's have' : ' has'} date conflicts with existing contracts. Please resolve them before proceeding.`);
        return;
      }
      setStep2Error(''); setStep(3);
    }
  };
  const goBack = () => setStep(s => Math.max(1, s - 1));
  const handleBackToList = () => {
    // Original code:
    // setIsCreating(false); setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
    sessionStorage.removeItem('quotation_form_draft');
    setStep(1);
    resetForm();
    setIsCreating(false);
    setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 80);
  };

  const generatePDF = async (bypassTermsCheck = false) => {
    // ── Input Validation ──
    if (!selectedCustomer?.customerID) {
      showToast('Please select a customer before saving.', 'error');
      return;
    }
    if (!selectedCompanyId) {
      showToast('Please select a company before saving.', 'error');
      return;
    }
    if (rows.length === 0) {
      showToast('Please add at least one hoarding/item before saving.', 'error');
      return;
    }

    if (!editingQuotID) {
      const isActive = await checkSeriesActive('Quotation');
      if (!isActive) return;
    }

    // Validate details of each row
    for (const r of rows) {
      if (r.rowType === 'hoarding') {
        if (!r.hoardingID) {
          showToast('Invalid hoarding row detected.', 'error');
          return;
        }
        if (!r.startDate || !r.endDate) {
          showToast(`Dates are required for hoarding ${r.hoardingCode || ''}`, 'error');
          return;
        }
      } else if (r.rowType === 'merged') {
        if (!r.mergedHoardingIDs || r.mergedHoardingIDs.length === 0) {
          showToast('Invalid merged row detected.', 'error');
          return;
        }
        if (!r.startDate || !r.endDate) {
          showToast(`Dates are required for merged hoarding ${r.hoardingCode || ''}`, 'error');
          return;
        }
      } else if (r.rowType === 'printing') {
        if (!r.size) {
          showToast('Please select size for all printing rows.', 'error');
          return;
        }
      }
      if (withPrinting && (r.rowType === 'hoarding' || r.rowType === 'merged')) {
        const hasPrintType = !!r.printType;
        const hasPrintRate = r.printRate !== '' && r.printRate !== undefined && r.printRate !== null && Number(r.printRate) > 0;
        if (hasPrintType && !hasPrintRate) {
          showToast('Print Cost/Sq.Ft is mandatory when Printing Type is selected.', 'error');
          return;
        }
        if (hasPrintRate && !hasPrintType) {
          showToast('Printing Type is mandatory when Print Cost/Sq.Ft is entered.', 'error');
          return;
        }
      }
      if (Number(r.amount || 0) < 0) {
        showToast('Negative amount detected in rows.', 'error');
        return;
      }
    }

    // ✅ MODIFIED: If Terms & Conditions are not selected, prompt user via custom TermsConfirmModal.
    // Ensure we only skip check if bypassTermsCheck is explicitly boolean true (avoiding React event objects)
    const skipCheck = bypassTermsCheck === true;
    if (!skipCheck && selectedTerms.length === 0) {
      setShowTermsConfirmModal(true);
      return;
    }

    setSaving(true);
    try {
      const headerPayload = {
        quotationRevisionNumber: Number(revisionNo),
        customerID: selectedCustomer.customerID,
        quotationNumber: quotNo || `QT/${Date.now()}`,
        quotationDate: quotDate,
        cGSTPercent: Number(cgstPct),
        cGSTAmount: cgstAmt,
        sGSTPercent: Number(sgstPct),
        sGSTAmount: sgstAmt,
        totalAmount: finalTotal,
      };

      let savedHeader;
      if (editingQuotID) {
        // EDIT: same quotationID + same revision number → PUT, updates row in place
        savedHeader = await apiService.updateQuotation({ ...headerPayload, quotationID: editingQuotID });
      } else if (Number(revisionNo) > 1 && originalQuotID > 0) {
        // REVISE: pass the original quotationID so the backend recognizes this as
        // revisionNumber > 1 for that quotation family and keeps the same quotationNumber
        savedHeader = await apiService.createQuotation({ ...headerPayload, quotationID: originalQuotID });
      } else {
        // Brand new quotation (revisionNumber === 1) — backend auto-generates a fresh quotationNumber
        savedHeader = await apiService.createQuotation(headerPayload);
      }

      const savedQuotID = savedHeader?.quotationID ?? savedHeader?.QuotationID ?? editingQuotID ?? originalQuotID ?? 0;
      if (!savedQuotID || savedQuotID === 0) {
        throw new Error('Failed to retrieve a valid Quotation ID from the database.');
      }
      const savedRevNo = Number(revisionNo);

      // ── Clean up existing lines/merges from database for this quotation revision ──
      const [allLinesRaw, allMergesRaw] = await Promise.all([
        apiService.getAllQuotationLines(),
        apiService.getAllQuotationMerges(),
      ]);

      const lines = normalizeList(allLinesRaw).map(normalizeQuotLine);
      const merges = normalizeList(allMergesRaw).map(m => ({
        quotationMergeID: m.quotationMergeID ?? m.QuotationMergeID ?? 0,
        quotationLineNumber: m.quotationLineNumber ?? m.QuotationLineNumber ?? 0,
        quotationID: m.quotationID ?? m.QuotationID ?? 0,
        quotationRevisionNumber: m.quotationRevisionNumber ?? m.QuotationRevisionNumber ?? 0,
        hoardingID: m.hoardingID ?? m.HoardingID ?? 0,
      }));

      const existingLines = lines.filter(l =>
        Number(l.quotationID) === Number(savedQuotID) &&
        Number(l.quotationRevisionNumber) === Number(savedRevNo)
      );
      const existingMerges = merges.filter(m =>
        Number(m.quotationID) === Number(savedQuotID) &&
        Number(m.quotationRevisionNumber) === Number(savedRevNo)
      );

      // Delete existing merges
      await Promise.all(
        existingMerges.map(m =>
          apiService.deleteQuotationMerge(m.quotationMergeID, m.hoardingID)
        )
      );

      // Delete existing lines
      await Promise.all(
        existingLines.map(l =>
          apiService.deleteQuotationLine(
            l.quotationLineNumber,
            l.quotationID,
            l.quotationRevisionNumber,
            l.hoardingID
          )
        )
      );

      // Save lines (all treated as fresh since we cleared existing ones)
      await Promise.all(
        rows.filter(r => r.rowType === 'hoarding' || r.rowType === 'printing' || r.rowType === 'extra')
          .map((row, idx) => {
            const purpose =
              row.rowType === 'printing' ? (row.location || '') :
                row.rowType === 'extra' ? (row.location || '') :
                  `|rate:${Number(row.ratePerMonth || 0)},print:${Number(row.printingCost || 0)},printType:${row.printType || ''},printRate:${Number(row.printRate || 0)}`;

            const linePayload = {
              quotationLineNumber: idx + 1,
              quotationID: savedQuotID,
              quotationRevisionNumber: savedRevNo,
              hoardingID: row.hoardingID || 0,
              purpose,
              periodBeginDate: row.startDate || todayISO(),
              periodEndDate: row.endDate || todayISO(),
              rentAmount: Number(row.amount || 0),
              mergeFlag: false,
            };
            return apiService.createQuotationLine(linePayload);
          })
      );

      /* ── Save merge records ───────────────── */
      const mergedRows = rows.filter(r => r.rowType === 'merged');
      let nextLineNum = rows.filter(r => r.rowType === 'hoarding' || r.rowType === 'printing' || r.rowType === 'extra').length;

      for (const mergedRow of mergedRows) {
        const hIds = Array.isArray(mergedRow.mergedHoardingIDs)
          ? mergedRow.mergedHoardingIDs.map(Number).filter(id => id > 0)
          : [];
        if (!hIds.length) continue;
        const flag = mergedRow.mergeDirection === 'S' ? 'S' : (mergedRow.mergeDirection === 'H' ? 'H' : 'V');

        const purpose = (mergedRow.location || '') + `|rate:${Number(mergedRow.ratePerMonth || 0)},print:${Number(mergedRow.printingCost || 0)},printType:${mergedRow.printType || ''},printRate:${Number(mergedRow.printRate || 0)}`;

        nextLineNum += 1;
        await apiService.createQuotationLine({
          quotationLineNumber: nextLineNum,
          quotationID: savedQuotID,
          quotationRevisionNumber: savedRevNo,
          hoardingID: 0,
          purpose,
          periodBeginDate: mergedRow.startDate || todayISO(),
          periodEndDate: mergedRow.endDate || todayISO(),
          rentAmount: Number(mergedRow.amount || 0),
          mergeFlag: true,
        });

        for (const hID of hIds) {
          await apiService.createQuotationMerge({
            quotationLineNumber: nextLineNum,
            quotationID: savedQuotID,
            quotationRevisionNumber: savedRevNo,
            mergeAlongFlag: flag,
            hoardingID: hID,
          });
        }
      }

      // ── Save selected Terms & Conditions ──
      await saveQuotationTerms(savedQuotID, savedRevNo, selectedTerms);

      // ── Save selected Company Details ──
      const qcPayload = {
        quotationCompanyID: Number(quotationCompanyRecId || 0),
        companyID: Number(selectedCompanyId),
        quotationID: Number(savedQuotID),
        quotationRevisionNumber: Number(savedRevNo)
      };

      if (editingQuotID) {
        await apiService.updateQuotationCompany(qcPayload);
      } else {
        const qcRes = await apiService.createQuotationCompany({ ...qcPayload, quotationCompanyID: 0 });
        const qcRaw = qcRes?.data ?? qcRes;
        const newQcId = qcRaw?.quotationCompanyID ?? qcRaw?.QuotationCompanyID ?? 0;
        if (newQcId > 0) {
          setQuotationCompanyRecId(newQcId);
        }
      }

      // ── Fetch saved data from Database to Print ──
      const [allQuotsRaw, printLinesRaw, printMergesRaw] = await Promise.all([
        apiService.getAllQuotations(),
        apiService.getAllQuotationLines(),
        apiService.getAllQuotationMerges(),
      ]);

      const quots = normalizeList(allQuotsRaw).map(normalizeQuotation);
      const printLines = normalizeList(printLinesRaw).map(normalizeQuotLine);
      const printMerges = normalizeList(printMergesRaw).map(m => ({
        quotationMergeID: m.quotationMergeID ?? m.QuotationMergeID ?? 0,
        quotationLineNumber: m.quotationLineNumber ?? m.QuotationLineNumber ?? 0,
        quotationID: m.quotationID ?? m.QuotationID ?? 0,
        quotationRevisionNumber: m.quotationRevisionNumber ?? m.QuotationRevisionNumber ?? 0,
        hoardingID: m.hoardingID ?? m.HoardingID ?? 0,
        mergeAlongFlag: m.mergeAlongFlag ?? m.MergeAlongFlag ?? 'H',
      }));

      const dbQuot = quots.find(q =>
        Number(q.quotationID) === Number(savedQuotID) &&
        Number(q.quotationRevisionNumber) === Number(savedRevNo)
      ) || headerPayload;

      const dbLines = printLines.filter(l =>
        Number(l.quotationID) === Number(savedQuotID) &&
        Number(l.quotationRevisionNumber) === Number(savedRevNo)
      );

      const dbMerges = printMerges.filter(m =>
        Number(m.quotationID) === Number(savedQuotID) &&
        Number(m.quotationRevisionNumber) === Number(savedRevNo)
      );

      /* ── Build rows from DB fetched QuotationLineDTL ── */
      const pdfRows = dbLines
        .filter(l => !l.mergeFlag)
        .map(l => {
          if (!l.hoardingID && l.purpose) {
            const isPrinting = PRINTING_TYPES.some(pt =>
              pt.toLowerCase() === (l.purpose || '').toLowerCase()
            );
            return {
              rowType: isPrinting ? 'printing' : 'extra',
              hoardingID: 0,
              location: l.purpose,
              size: '',
              sqFt: 0,
              nos: 1,
              startDate: l.periodBeginDate,
              endDate: l.periodEndDate,
              ratePerMonth: l.rentAmount,
              amount: l.rentAmount,
              printingCost: 0,
            };
          }
          const h = hoardings.find(hh => hh.hoardingID === l.hoardingID);
          const siteObj = getHoardingSite(h, siteMap, outsideSiteMap);

          const { ratePerMonth, printingCost, printType, printRate } = parsePurposeMeta(l.purpose, h?.monthlyRent || 0);
          const days = calculateDays(l.periodBeginDate, l.periodEndDate) || 30;

          const loc = siteObj ? `${siteObj.addressLine1 || ''}${siteObj.city ? `, ${siteObj.city}` : ''}` : h?.hoardingCode || '';

          return {
            rowType: 'hoarding',
            hoardingID: l.hoardingID,
            location: loc,
            size: h ? `${h.width} X ${h.height}` : '',
            sqFt: h ? (h.width * h.height) : 0,
            nos: calcNOSFromDays(days),
            startDate: l.periodBeginDate,
            endDate: l.periodEndDate,
            days: days,
            ratePerMonth: ratePerMonth,
            amount: l.rentAmount,
            printType: printType,
            printRate: printRate,
            printingCost: printingCost,
          };
        });

      const usedLineNums = new Set();
      if (dbMerges.length >= 2) {
        const byLine = new Map();
        for (const m of dbMerges) {
          const ln = m.quotationLineNumber;
          if (!byLine.has(ln)) byLine.set(ln, []);
          byLine.get(ln).push(m);
        }

        const sortedKeys = [...byLine.keys()].sort((a, b) => a - b);
        for (const ln of sortedKeys) {
          const records = byLine.get(ln);
          if (records.length < 2) continue;

          const dir = records[0].mergeAlongFlag === 'S' ? 'S' : (records[0].mergeAlongFlag === 'H' ? 'H' : 'V');
          const hoardingObjs = records
            .map(r => hoardings.find(h => h.hoardingID === r.hoardingID))
            .filter(Boolean);

          const sizes = hoardingObjs.map(h => ({ w: h.width || 0, h: h.height || 0 }));
          const gaps = hoardingObjs.length - 1;
          let mw, mh;
          if (dir === 'S') {
            const singleW = sizes[0]?.w || 0;
            const singleH = sizes[0]?.h || 0;
            mw = (singleW * 2) + 1;
            mh = (singleH * 2) + 1;
          } else if (dir === 'H') {
            mw = sizes.reduce((s, sz) => s + sz.w, 0) + gaps;
            mh = Math.max(...sizes.map(s => s.h));
          } else {
            mw = Math.max(...sizes.map(s => s.w));
            mh = sizes.reduce((s, sz) => s + sz.h, 0) + gaps;
          }

          const locations = hoardingObjs.map(h => {
            const site = getHoardingSite(h, siteMap, outsideSiteMap);
            return site
              ? `${site.addressLine1 || ''}${site.city ? `, ${site.city}` : ''}`
              : h?.hoardingCode || '';
          });
          const codes = hoardingObjs.map(h => h.hoardingCode || '').join(' + ');

          const locFallback = [...new Set(locations)].join(' + ');
          let savedLine = dbLines.find(l => Number(l.quotationLineNumber) === Number(ln) && !usedLineNums.has(Number(l.quotationLineNumber)));
          if (!savedLine) {
            savedLine = dbLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)) && l.purpose && (
              l.purpose.includes(locFallback) ||
              locFallback.includes(l.purpose.split('|')[0]) ||
              (codes && l.purpose.includes(codes))
            ));
          }
          if (!savedLine) {
            savedLine = dbLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)) && l.purpose && hoardingObjs.some(ho => {
              const loc = buildSiteAddress(ho.siteID ? siteMap.get(ho.siteID) : null, ho.hoardingCode);
              return l.purpose.includes(ho.hoardingCode) || l.purpose.includes(loc);
            }));
          }
          if (!savedLine) {
            savedLine = dbLines.find(l => l.mergeFlag && !usedLineNums.has(Number(l.quotationLineNumber)));
          }

          if (savedLine) {
            usedLineNums.add(Number(savedLine.quotationLineNumber));
          }

          const totalRent = hoardingObjs.reduce((s, ho) => s + (ho.monthlyRent || 0), 0);
          const { ratePerMonth, printingCost, printType, printRate } = parsePurposeMeta(savedLine?.purpose, totalRent);
          const days = calculateDays(savedLine?.periodBeginDate, savedLine?.periodEndDate) || 30;

          pdfRows.push({
            rowType: 'merged',
            isMerged: true,
            mergeDirection: dir,
            hoardingID: 0,
            location: locFallback,
            hoardingCode: codes,
            size: `${mw} X ${mh}`,
            sqFt: mw * mh,
            nos: calcNOSFromDays(days),
            startDate: savedLine?.periodBeginDate || '',
            endDate: savedLine?.periodEndDate || '',
            days: days,
            ratePerMonth: ratePerMonth,
            amount: savedLine ? savedLine.rentAmount : 0,
            printType: printType,
            printRate: printRate,
            printingCost: printingCost,
          });
        }
      }

      /* ── Calculate DB Totals ── */
      const totalAmountVal = Number(dbQuot.totalAmount ?? dbQuot.TotalAmount ?? finalTotal);
      const storedSub = totalAmountVal / (1 + (dbQuot.cGSTPercent + dbQuot.sGSTPercent) / 100);
      const storedCgst = (storedSub * dbQuot.cGSTPercent) / 100;
      const storedSgst = (storedSub * dbQuot.sGSTPercent) / 100;
      const storedGross = storedSub + storedCgst + storedSgst;
      const storedFinal = Math.round(storedGross);

      const companyDetails = await fetchCompanyDetailsForQuotation(savedQuotID, savedRevNo);

      const html = buildPrintHTML({
        rows: pdfRows,
        withPrinting: withPrinting || pdfRows.some(r => r.rowType === 'printing') || pdfRows.some(r => Number(r.printingCost || 0) > 0),
        selectedCustomer: selectedCustomer,
        quotNo: dbQuot.quotationNumber,
        quotDate: dbQuot.quotationDate,
        revisionNo: dbQuot.quotationRevisionNumber,
        cgstPct: dbQuot.cGSTPercent,
        sgstPct: dbQuot.sGSTPercent,
        subTotal: storedSub,
        cgstAmt: storedCgst,
        sgstAmt: storedSgst,
        roundOff: storedFinal - storedGross,
        finalTotal: storedFinal,
        selectedTerms,
        termsTexts: selectedTerms.map(id => {
          const t = termsList.find(t => t.termID === id);
          return t?.description || '';
        }),
        companyDetails,
      });

      const win = window.open('', '_blank');
      if (win) { win.document.write(html); win.document.close(); }
      showToast('Quotation saved successfully!', 'success');
      // Original code:
      // await refreshQuotations();
      // setEditingQuotID(null);
      // setOriginalQuotID(0);
      // setIsCreating(false);
      sessionStorage.removeItem('quotation_form_draft');
      setStep(1);
      resetForm();
      await refreshQuotations();
      setEditingQuotID(null);
      setOriginalQuotID(0);
      setIsCreating(false);
    } catch (err) {
      console.error(err);
      let errorMsg = 'Failed to save quotation.';
      if (err?.response?.data) {
        const data = err.response.data;
        if (typeof data === 'string') {
          errorMsg = data;
        } else if (data.message) {
          errorMsg = data.message;
        } else if (data.title) {
          errorMsg = data.title;
        } else if (data.errors) {
          errorMsg = typeof data.errors === 'object' ? JSON.stringify(data.errors) : String(data.errors);
        } else {
          errorMsg = JSON.stringify(data);
        }
      } else if (err?.message) {
        errorMsg = err.message;
      }
      showToast(errorMsg, 'error');
    } finally { setSaving(false); }
  };

  const handleDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    setSaving(true);
    try {
      const ids = Array.from(selectedQuotIds).filter(id => !contractedQuotIds.has(Number(id)));
      if (ids.length === 0) {
        showToast('No eligible quotations to delete (some were skipped because they have contracts created).', 'warning');
        return;
      }
      await Promise.all(
        ids.map(async (id) => {
          const qObj = quotations.find(q => Number(q.quotationID) === Number(id));
          if (qObj) {
            const revNo = qObj.quotationRevisionNumber ?? 0;
            const custId = qObj.customerID ?? 0;
            await apiService.deleteQuotation(id, revNo, custId);
          }
        })
      );
      showToast(`${ids.length} quotation(s) deleted successfully.`, 'success');
      setSelectedQuotIds(new Set());
      setDeleteMode(false);
      await refreshQuotations();
    } catch (err) {
      console.error('[Delete Confirm] Error deleting quotations:', err);
      showToast('Deletion failed: ' + (err?.response?.data?.message || err?.message || 'Unknown error'), 'error');
    } finally {
      setSaving(false);
    }
  };

  /* ── History grouped table ── */
  const allGrouped = useMemo(() => {
    const map = new Map();
    for (const q of quotations) {
      const key = (q.quotationNumber || '').trim() || String(q.quotationID);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(q);
    }
    const groups = [];
    for (const revs of map.values()) {
      groups.push([...revs].sort((a, b) =>
        Number(b.quotationRevisionNumber || 0) - Number(a.quotationRevisionNumber || 0)
      ));
    }
    return groups;
  }, [quotations]);

  const filteredGroups = useMemo(() => {
    const q = histSearch.toLowerCase();
    if (!q) return allGrouped;
    return allGrouped.filter(group => {
      const l = group[0];
      const cust = customers.find(c => c.customerID === l.customerID);
      return (l.quotationNumber || '').toLowerCase().includes(q) ||
        (cust?.customerName || '').toLowerCase().includes(q);
    });
  }, [allGrouped, histSearch, customers]);

  const sortedGroups = useMemo(() => [...filteredGroups].sort((a, b) => {
    const av = String(a[0][histSortKey] || '').toLowerCase();
    const bv = String(b[0][histSortKey] || '').toLowerCase();
    return histSortDir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
  }), [filteredGroups, histSortKey, histSortDir]);

  const histTotalPages = Math.max(1, Math.ceil(sortedGroups.length / histPageSize));
  const histPaginated = sortedGroups.slice((histPage - 1) * histPageSize, histPage * histPageSize);

  const handleHistSort = (key) => {
    if (histSortKey === key) setHistSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setHistSortKey(key); setHistSortDir('asc'); }
    setHistPage(1);
  };

  const histPageNums = Array.from({ length: histTotalPages }, (_, i) => i + 1)
    .filter(p => p === 1 || p === histTotalPages || Math.abs(p - histPage) <= 1)
    .reduce((acc, p, i, arr) => { if (i > 0 && arr[i] - arr[i - 1] > 1) acc.push('…'); acc.push(p); return acc; }, []);

  const HIST_COLS = [
    { key: 'quotationNumber', label: 'Quotation No.', w: '14%' },
    { key: 'customerID', label: 'Customer', w: '20%' },
    { key: 'quotationDate', label: 'Date', w: '11%' },
    { key: '_version', label: 'Latest Version', w: '11%', noSort: true },
    { key: 'totalAmount', label: 'Grand Total', w: '12%' },
    { key: '_action', label: 'Actions', w: '22%', noSort: true },
  ];

  const custName = (id) => customers.find(c => c.customerID === id)?.customerName || '—';

  const mergedCount = rows.filter(r => r.rowType === 'merged').length;

  /* ════════════════ RENDER ════════════════ */
  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 14, flexDirection: 'column' }}>
      <Loader2 size={32} color="#049edf" className="pg-spin" />
      <span style={{ fontFamily: 'Nunito,sans-serif', color: '#9090a8', fontSize: 14 }}>Loading quotation data…</span>
    </div>
  );

  return (
    <>
      {saving && (
        <div className="qt-saving-overlay">
          <Loader2 size={32} color="#049edf" className="pg-spin" />
          <div className="qt-saving-overlay__text">Saving quotation…</div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}

      <div className="pg-page">

        {/* ── Page Header ── */}
        <div className="pg-header">
          <div>
            <h1 className="pg-header__title">Quotations</h1>
            <p className="pg-header__subtitle">Generate and manage hoarding <strong>quotations</strong> for customers.</p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {isCreating && (
              <button className="pg-btn-cancel" onClick={handleBackToList} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <LayoutGrid size={13} /> Back to List
              </button>
            )}
            {!isCreating && (
              <button className="pg-btn-add" onClick={handleStartNew}>
                <Plus size={14} /> New Quotation
              </button>
            )}
          </div>
        </div>

        {apiError && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 11, marginBottom: 16, color: '#dc2626', fontSize: 13, fontWeight: 600, fontFamily: 'Nunito,sans-serif' }}>
            <AlertCircle size={14} /> {apiError}
          </div>
        )}

        {/* ══════════════ STEP-BASED FORM ══════════════ */}
        {isCreating && (
          <div ref={formRef} className="pg-container qt-form-container" style={{ marginBottom: 20 }}>

            {/* Step Indicator */}
            <div className="qt-step-bar">
              {STEPS.map((s, i) => {
                const done = step > s.n;
                const active = step === s.n;
                return (
                  <React.Fragment key={s.n}>
                    <div className={`qt-step${active ? ' qt-step--active' : ''}${done ? ' qt-step--done' : ''}`}>
                      <div className="qt-step__circle">
                        {done ? <Check size={14} color="#fff" /> : <s.Icon size={13} color={active ? '#fff' : '#b0b0c8'} />}
                      </div>
                      <div className="qt-step__label">{s.label}</div>
                    </div>
                    {i < STEPS.length - 1 && <div className={`qt-step__connector${done ? ' qt-step__connector--done' : ''}`} />}
                  </React.Fragment>
                );
              })}
            </div>

            {/* ─── STEP 1 ─── */}
            {step === 1 && (
              <div className="qt-step-body">
                <div className="qt-form-grid">

                  {/* Customer */}
                  <div className="qt-field-full">
                    <label className="qt-label">
                      Customer <span className="qt-label--req">*</span>
                      {revisionNo > 1 && (
                        <span style={{ marginLeft: 6, fontFamily: 'Nunito,sans-serif', fontSize: 11, fontWeight: 700, color: '#7c7c7c' }}>
                          · locked for revision
                        </span>
                      )}
                    </label>
                    {revisionNo > 1 ? (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '9px 12px', borderRadius: 10,
                        background: 'rgba(114, 114, 114, 0.04)',
                        border: '1.5px solid rgba(88, 88, 88, 0.25)',
                        cursor: 'not-allowed',
                      }}>
                        <User size={14} color="#7c7c7c" style={{ flexShrink: 0 }} />
                        <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 700, color: '#7c7c7c', flex: 1 }}>
                          {selectedCustomer?.customerName || '—'}
                        </span>
                        <span style={{ fontSize: 11, color: '#7c7c7c', flexShrink: 0 }}>🔒</span>
                      </div>
                    ) : (
                      <CustomerCombo
                        value={selectedCustomer?.customerID ?? null}
                        onChange={(c) => { setSelectedCustomer(c); setStep1Error(''); }}
                        customers={customers}
                      />
                    )}

                    {/* ── Customer info strip with Edit button ── */}
                    {selectedCustomer && (
                      <div className="qt-customer-info" style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <strong>{selectedCustomer.customerName}</strong>
                          {selectedCustomer.addressLine1 && <span> · {selectedCustomer.addressLine1}</span>}
                          {selectedCustomer.city && (
                            <span>, {[selectedCustomer.city, selectedCustomer.district].filter(Boolean).join(', ')}</span>
                          )}
                          {selectedCustomer.phone1 && <span> · 📞 {selectedCustomer.phone1}</span>}
                          {selectedCustomer.gstNumber && <span> · GST: {selectedCustomer.gstNumber}</span>}
                        </div>
                        {/* ← NEW: Edit customer button */}
                        <button
                          onClick={() => setShowCustomerEditModal(true)}
                          title="Edit customer details"
                          style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '4px 11px', borderRadius: 7,
                            border: '1.5px solid rgba(4,158,223,0.30)',
                            background: 'rgba(4,158,223,0.06)',
                            cursor: 'pointer', color: '#049edf',
                            fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 800,
                            flexShrink: 0, whiteSpace: 'nowrap',
                          }}
                        >
                          <Edit2 size={11} /> Edit
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Quotation Type */}
                  <div className="qt-field-full">
                    <label className="qt-label">Quotation Type <span className="qt-label--req">*</span></label>
                    <div className="qt-type-grid">
                      {[
                        { val: false, label: 'Standard Quotation', sub: 'SR, Location, Size, Period, Rate, Amount' },
                        { val: true, label: 'With Printing Cost', sub: 'Includes Printing Cost column + Flex Banner rows' },
                      ].map(({ val, label, sub }) => (
                        <button key={String(val)} onClick={() => setWithPrinting(val)}
                          className={`qt-type-card${withPrinting === val ? ' qt-type-card--active' : ''}`}>
                          <div className="qt-type-card__check">{withPrinting === val && <Check size={11} color="#fff" />}</div>
                          <div className="qt-type-card__label">{label}</div>
                          <div className="qt-type-card__sub">{sub}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Quotation No + Date */}
                  <div>
                    <label className="qt-label">
                      Quotation No.
                      {revisionNo > 1 && (
                        <span style={{ marginLeft: 6, fontFamily: 'Nunito,sans-serif', fontSize: 11, fontWeight: 700, color: '#7c7c7c' }}>
                          · locked for revision
                        </span>
                      )}
                      {revisionNo <= 1 && (
                        <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#16a34a', background: 'rgba(22,163,74,0.08)', padding: '1px 6px', borderRadius: 4 }}>
                          Auto Generated
                        </span>
                      )}
                    </label>
                    <div className="qt-input-wrap" style={{
                      background: 'rgba(4,158,223,0.03)',
                      borderBottomColor: revisionNo > 1 ? '#7c7c7c' : '#049edf',
                      cursor: 'not-allowed',
                    }}>
                      <Hash size={14} color={revisionNo > 1 ? '#7c7c7c' : '#049edf'} style={{ flexShrink: 0 }} />
                      <input
                        className="qt-input"
                        value={quotNo || 'Generating…'}
                        readOnly
                        style={{
                          color: revisionNo > 1 ? '#7c7c7c' : '#049edf',
                          fontWeight: 800,
                          cursor: 'not-allowed',
                          pointerEvents: 'none',
                        }}
                      />
                      <span title={revisionNo > 1 ? 'Quotation number is fixed for revisions' : 'Auto-generated by system'} style={{ fontSize: 11, color: revisionNo > 1 ? '#7c7c7c' : '#049edf', flexShrink: 0 }}>🔒</span>
                    </div>
                  </div>
                  <div>
                    <label className="qt-label">Date</label>
                    <div className="qt-input-wrap">
                      <Calendar size={14} color="#c0c0d8" style={{ flexShrink: 0 }} />
                      <input className="qt-input" type="date" value={quotDate} onChange={e => setQuotDate(e.target.value)} />
                    </div>
                  </div>

                  {/* Revision No */}
                  <div>
                    <label className="qt-label">
                      Revision No. <span className="qt-label--opt">(1 = original)</span>
                    </label>
                    <div className="qt-input-wrap" style={{ background: 'rgba(4,158,223,0.03)', borderBottomColor: revisionNo > 1 ? '#7c7c7c' : '#049edf', cursor: 'default' }}>
                      <Edit2 size={14} color={revisionNo > 1 ? '#7c7c7c' : '#c0c0d8'} style={{ flexShrink: 0 }} />
                      <input className="qt-input" type="number" value={revisionNo} readOnly
                        style={{ color: revisionNo > 1 ? '#7c7c7c' : '#1a1a2e', fontWeight: 900, cursor: 'default', pointerEvents: 'none' }} />
                      <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, fontWeight: 700, color: revisionNo > 1 ? '#7c7c7c' : '#16a34a', flexShrink: 0, whiteSpace: 'nowrap' }}>
                        {revisionNo <= 1 ? 'Original' : `Revision ${revisionNo}`}
                      </span>
                    </div>
                  </div>

                  {/* Global Period Dates */}
                  <div className="qt-field-full">
                    <label className="qt-label">
                      Global Period Dates <span className="qt-label--req">*</span>
                      <span className="qt-label--opt"> — applied to all hoardings; editable per row in step 2</span>
                    </label>
                    <div className="qt-date-banner">
                      <span className="qt-date-banner__label"><Calendar size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />Period:</span>
                      <div className="qt-date-banner__field">
                        <span className="qt-date-banner__sep">From</span>
                        <input type="date" className="qt-date-banner__input" value={globalStart} onChange={e => {
                          const val = e.target.value;
                          setGlobalStart(val);
                          if (val && globalDays) {
                            setGlobalEnd(calculateEndDate(val, globalDays));
                          }
                        }} />
                        <span className="qt-date-banner__sep">Days</span>
                        <input type="number" min="1" className="qt-date-banner__input" style={{ width: 80 }} value={globalDays} onChange={e => {
                          const val = e.target.value;
                          setGlobalDays(val);
                          if (globalStart && val) {
                            setGlobalEnd(calculateEndDate(globalStart, val));
                          }
                        }} />
                        <span className="qt-date-banner__sep">To</span>
                        <input type="date" className="qt-date-banner__input" value={globalEnd} readOnly disabled style={{ cursor: 'not-allowed', background: '#f0f0f8' }} />
                      </div>
                    </div>
                  </div>

                </div>

                {step1Error && (
                  <div className="qt-error-banner">
                    <AlertCircle size={14} /> {step1Error}
                  </div>
                )}

                <div className="qt-step-foot">
                  <button className="pg-btn-cancel" onClick={handleBackToList} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <LayoutGrid size={13} /> Back to List
                  </button>
                  <button className="pg-btn-save" onClick={goNext}>
                    Next: Add Hoardings <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* ─── STEP 2 ─── */}
            {step === 2 && (
              <div className="qt-step-body">
                {(globalStart || globalEnd) && (
                  <div className="qt-date-banner" style={{ marginBottom: 14 }}>
                    <span className="qt-date-banner__label"><Calendar size={13} style={{ marginRight: 4, verticalAlign: 'middle' }} />Global Period:</span>
                    <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 700, color: '#1a1a2e' }}>
                      {globalStart ? fmtDateDisplay(globalStart) : '—'} ({globalDays ? `${globalDays} Days` : '—'}) → {globalEnd ? fmtDateDisplay(globalEnd) : 'Auto'}
                    </span>
                    <button className="qt-date-banner__apply" onClick={applyGlobalDates}>
                      <RefreshCw size={12} /> Apply to All
                    </button>
                  </div>
                )}

                {/* Site colour legend */}
                {rows.length > 0 && (() => {
                  const siteIds = [...new Set(rows.map(r => r.siteID).filter(sid => sid != null))];
                  if (siteIds.length === 0) return null;
                  return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap', padding: '8px 12px', background: '#f8f8fd', borderRadius: 10, border: '1px solid #f0f0f8' }}>
                      <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 700, color: '#5a5a78' }}>Sites:</span>
                      {siteIds.map(sid => {
                        const color = siteColorMap.get(toSID(sid));
                        const h = hoardings.find(hh => (hh.siteID ?? hh.site?.siteID) === sid);
                        const label = h?.site?.addressLine1 || h?.site?.city || `Site ${sid}`;
                        return color ? (
                          <div key={sid} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: color.bg, border: `1px solid ${color.border}` }}>
                            <div style={{ width: 8, height: 8, borderRadius: '50%', background: color.dot }} />
                            <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, fontWeight: 700, color: '#1a1a2e', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
                          </div>
                        ) : null;
                      })}
                      {mergedCount > 0 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '3px 10px', borderRadius: 20, background: 'rgba(124,58,237,0.08)', border: '1px solid rgba(124,58,237,0.25)' }}>
                          <Link2 size={10} color="#7c3aed" />
                          <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, fontWeight: 700, color: '#7c3aed' }}>{mergedCount} Merged</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                <div className="qt-step2-head">
                  <div>
                    <div className="qt-step2-title">Hoarding Items</div>
                    <div className="qt-step2-sub">
                      {rows.length} row{rows.length !== 1 ? 's' : ''} · {Math.max(1, Math.ceil(rows.length / ROWS_PER_PRINT_PAGE))} page{Math.max(1, Math.ceil(rows.length / ROWS_PER_PRINT_PAGE)) !== 1 ? 's' : ''} in PDF
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      className="pg-btn-cancel"
                      onClick={() => setShowMergeModal(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, borderColor: 'rgba(124,58,237,0.35)', color: '#7c3aed', background: 'rgba(124,58,237,0.05)' }}
                      title="Merge two hoardings into one large display"
                    >
                      <Link2 size={13} /> Merge
                    </button>
                    <button className="pg-btn-cancel" onClick={() => setShowManualModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Building2 size={13} /> Add Manual
                    </button>
                    <button
                      className="pg-btn-cancel"
                      onClick={() => setShowExternalModal(true)}
                      style={{ display: 'flex', alignItems: 'center', gap: 6, borderColor: 'rgba(22,163,74,0.35)', color: '#16a34a', background: 'rgba(22,163,74,0.05)' }}
                      title="Add external hoardings"
                    >
                      <Building2 size={13} /> Add External Hoarding
                    </button>
                    <button className="pg-btn-save" onClick={() => setShowHoardModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Plus size={13} /> Add Hoardings
                    </button>
                  </div>
                </div>

                <div style={{ overflowX: 'auto', border: '1px solid #f0f0f8', borderRadius: 12, marginBottom: 12 }}>
                  {rows.length === 0 ? (
                    <div className="pg-empty__inner" style={{ padding: '44px 20px' }}>
                      <Building2 size={38} color="#d0d0e8" />
                      <span className="pg-empty__label">No hoardings added yet</span>
                    </div>
                  ) : (
                    <table className="pg-table" ref={step2TableRef} style={{ minWidth: withPrinting ? 1420 : 1070 }}>
                      <thead>
                        <tr>
                          <th className="pg-th">#</th>
                          <th className="pg-th" style={{ textAlign: 'left' }}>Site Address / Product</th>
                          <th className="pg-th">Size</th>
                          <th className="pg-th">Sq.Ft</th>
                          <th className="pg-th">NOS</th>
                          <th className="pg-th">Start Date</th>
                          <th className="pg-th">Days</th>
                          <th className="pg-th">End Date</th>
                          <th className="pg-th">Rate/Mo</th>
                          {withPrinting && <>
                            <th className="pg-th" style={{ color: '#7c3aed' }}>Printing Type</th>
                            <th className="pg-th" style={{ color: '#7c3aed' }}>Print Cost/Sq.Ft</th>
                            <th className="pg-th" style={{ color: '#7c3aed' }}>Print Cost</th>
                          </>}
                          <th className="pg-th">Amount</th>
                          <th className="pg-th"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          // Compute sizeMap once — used by printing row SIZE dropdown
                          const sizeMap = {};
                          rows.forEach(r2 => {
                            if (r2.rowType === 'hoarding' && r2.size) {
                              if (!sizeMap[r2.size]) sizeMap[r2.size] = { sqFt: r2.sqFt || 0, count: 0, isMerged: false };
                              sizeMap[r2.size].count++;
                            } else if (r2.rowType === 'merged' && r2.size) {
                              // unique key so merged sizes don't collide/combine with regular hoarding sizes
                              const key = `${r2.size} (Merged)`;
                              if (!sizeMap[key]) sizeMap[key] = { sqFt: r2.sqFt || 0, count: 0, isMerged: true };
                              sizeMap[key].count++;   // ← CHANGED: increment instead of staying fixed at 1
                            }
                          });
                          const sizeOptions = Object.entries(sizeMap);

                          return rows.map((row, i) => {
                            const siteColor = getRowSiteColor(row);
                            const isMerged = row.rowType === 'merged';
                            const isPrint = row.rowType === 'printing';
                            const isExtra = row.rowType === 'extra';

                            const rowBg = isMerged ? 'rgba(124,58,237,0.05)'
                              : isPrint ? 'rgba(124,58,237,0.03)'
                                : siteColor ? siteColor.bg
                                  : '';
                            const rowBorderLeft = isMerged ? '4px solid rgba(124,58,237,0.40)'
                              : siteColor ? `4px solid ${siteColor.border}`
                                : '4px solid transparent';

                            return (
                              <tr key={row._id} className="pg-tr" style={{ background: rowBg, borderLeft: rowBorderLeft }}>
                                <td className="pg-td" style={{ textAlign: 'center' }}>
                                  {isMerged ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                      <Link2 size={12} color="#7c3aed" />
                                      <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 10, fontWeight: 800, color: '#7c3aed' }}>{i + 1}</span>
                                    </div>
                                  ) : (
                                    <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 800, color: isPrint ? '#7c3aed' : '#9090a8' }}>{i + 1}</span>
                                  )}
                                </td>
                                <td className="pg-td" style={{ minWidth: 160 }}>
                                  {isMerged && (
                                    <div style={{ marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                                      <span style={{
                                        fontFamily: 'Nunito,sans-serif', fontSize: 10.5, fontWeight: 800,
                                        padding: '2px 7px', borderRadius: 4,
                                        background: 'rgba(124,58,237,0.12)', color: '#7c3aed',
                                        border: '1px solid rgba(124,58,237,0.25)',
                                      }}>
                                        {row.mergeDirection === 'S'
                                          ? '⊞ Square Merge'
                                          : (row.mergeDirection === 'H' ? '↔ Horizontal Merge' : '↕ Vertical Merge')}
                                      </span>

                                      {/* Direction toggle button — only for H / V merges */}
                                      {row.mergeDirection !== 'S' && (
                                        <button
                                          onClick={e => { e.stopPropagation(); toggleMergeDirection(row._id); }}
                                          title={`Switch to ${row.mergeDirection === 'H' ? 'Vertical' : 'Horizontal'}`}
                                          style={{
                                            display: 'flex', alignItems: 'center', gap: 3,
                                            padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(124,58,237,0.30)',
                                            background: 'rgba(124,58,237,0.06)', color: '#7c3aed',
                                            cursor: 'pointer', fontFamily: 'Nunito,sans-serif',
                                            fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap',
                                          }}
                                        >
                                          <RefreshCw size={9} />
                                          {row.mergeDirection === 'H' ? '↕ Switch V' : '↔ Switch H'}
                                        </button>
                                      )}

                                      {/* Info button to see merged hoardings */}
                                      <button
                                        onClick={e => { e.stopPropagation(); setViewMergedRow(row); }}
                                        title="View Merged Hoardings List"
                                        style={{
                                          display: 'flex', alignItems: 'center', gap: 4,
                                          padding: '2px 8px', borderRadius: 4, border: '1px solid rgba(124,58,237,0.30)',
                                          background: 'rgba(124,58,237,0.06)', color: '#7c3aed',
                                          cursor: 'pointer', fontFamily: 'Nunito,sans-serif',
                                          fontSize: 10, fontWeight: 800, whiteSpace: 'nowrap',
                                        }}
                                      >
                                        <Info size={10} />
                                        Info ({row.mergedHoardingIDs?.length ?? 0})
                                      </button>
                                    </div>
                                  )}
                                  {!isMerged && siteColor && (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 2 }}>
                                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: siteColor.dot, flexShrink: 0 }} />
                                      <MapPin size={10} color={siteColor.dot} />
                                    </div>
                                  )}
                                  {isExtra ? (
                                    <input
                                      value={row.location}
                                      onChange={e => updateRow(row._id, 'location', e.target.value)}
                                      style={{
                                        width: '100%', border: 'none',
                                        borderBottom: '1.5px dashed #e8e8f4',
                                        fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 700,
                                        color: '#1a1a2e', background: 'transparent',
                                        outline: 'none', padding: '2px 0',
                                      }}
                                      placeholder="Charge description…"
                                    />
                                  ) : (
                                    <div style={{
                                      fontFamily: 'Nunito,sans-serif', fontSize: 12.5, fontWeight: 600,
                                      color: isMerged ? '#7c3aed' : '#1a1a2e',
                                      fontStyle: isPrint ? 'italic' : 'normal',
                                      lineHeight: 1.4, paddingTop: 1,
                                    }}>
                                      {row.rowType === 'hoarding' && row.siteObj ? (
                                        `${row.siteObj.addressLine1 || ''}${row.siteObj.city ? `, ${row.siteObj.city}` : ''}`
                                      ) : row.location}
                                    </div>
                                  )}
                                </td>
                                <td className="pg-td" style={{ textAlign: 'center' }}>
                                  {withPrinting && isPrint && !isExtra ? (
                                    // ── Printing row: size dropdown ──
                                    <div>
                                      <select
                                        value={row.size || ''}
                                        onChange={e => {
                                          const sel = e.target.value;
                                          if (!sel) { updateRowMultiple(row._id, { size: '', sqFt: 0, nos: 0, amount: 0 }); return; }
                                          const info = sizeMap[sel];
                                          if (!info) return;
                                          const totalSqFt = info.count * info.sqFt;
                                          updateRowMultiple(row._id, { size: sel, sqFt: totalSqFt, nos: info.count });
                                        }}
                                        style={{
                                          width: '100%', padding: '5px 6px',
                                          border: '1.5px solid #e8e8f4', borderRadius: 8,
                                          fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 700,
                                          color: '#7c3aed', background: '#fff', outline: 'none', cursor: 'pointer',
                                        }}
                                      >
                                        <option value="">Size…</option>
                                        {sizeOptions.map(([size, info]) => (
                                          <option key={size} value={size}>
                                            {`${size} (${info.count} × ${info.sqFt} = ${info.count * info.sqFt} sqft)`}
                                          </option>
                                        ))}
                                      </select>
                                      {row.sqFt > 0 && (
                                        <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 10, color: '#7c3aed', fontWeight: 700, marginTop: 2 }}>
                                          {row.sqFt} sq.ft
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    // ── Regular row: plain size text ──
                                    <span style={{
                                      fontFamily: 'Nunito,sans-serif', fontSize: 12.5, fontWeight: 700, color: '#4a5568',
                                    }}>
                                      {row.size || '—'}
                                    </span>
                                  )}
                                </td>
                                <td className="pg-td" style={{ textAlign: 'center' }}>
                                  <span style={{
                                    fontFamily: 'Nunito,sans-serif', fontSize: 12.5, fontWeight: 700,
                                    color: '#4a5568',
                                  }}>
                                    {isExtra ? '—' : (row.sqFt || '—')}
                                  </span>
                                </td>
                                <td className="pg-td" style={{ textAlign: 'center' }}>
                                  {isExtra ? (
                                    <span style={{ color: '#c0c0d0' }}>—</span>
                                  ) : isPrint ? (
                                    <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 800, color: '#7c3aed' }}>
                                      {row.nos > 0 ? row.nos : '—'}
                                    </span>
                                  ) : (
                                    <input
                                      className="qt-inline-input" type="number"
                                      value={row.nos}
                                      readOnly
                                      style={{ width: 64, textAlign: 'center', cursor: 'not-allowed', background: 'rgba(0,0,0,0.03)' }}
                                    />
                                  )}
                                </td>
                                <td className="pg-td">
                                  {isExtra || isPrint ? (
                                    <span style={{ color: '#c0c0d0', paddingLeft: 8 }}>—</span>
                                  ) : (
                                    <input className="qt-inline-input qt-date-input" type="date" value={row.startDate} onChange={e => updateRow(row._id, 'startDate', e.target.value)} />
                                  )}
                                </td>
                                <td className="pg-td">
                                  {isExtra || isPrint ? (
                                    <span style={{ color: '#c0c0d0', paddingLeft: 8 }}>—</span>
                                  ) : (
                                    <input className="qt-inline-input" type="number" min="1" value={row.days || ''} onChange={e => updateRow(row._id, 'days', e.target.value)} style={{ width: 64, textAlign: 'center' }} />
                                  )}
                                </td>
                                <td className="pg-td">
                                  {isExtra || isPrint ? (
                                    <span style={{ color: '#c0c0d0', paddingLeft: 8 }}>—</span>
                                  ) : (
                                    <input className="qt-inline-input qt-date-input" type="date" value={row.endDate} readOnly disabled style={{ cursor: 'not-allowed', background: 'rgba(0,0,0,0.03)' }} />
                                  )}
                                </td>
                                <td className="pg-td">
                                  {isExtra ? (
                                    <span style={{ color: '#c0c0d0', paddingLeft: 8 }}>—</span>
                                  ) : (
                                    <input className="qt-inline-input" type="number" min="0" value={row.ratePerMonth || ''} onChange={e => updateRow(row._id, 'ratePerMonth', e.target.value)} style={{ width: 86 }} />
                                  )}
                                </td>
                                {withPrinting && (
                                  <>
                                    <td className="pg-td">
                                      {isExtra || isPrint ? (
                                        <span style={{ color: '#c0c0d0', paddingLeft: 8 }}>—</span>
                                      ) : (
                                        <select
                                          value={row.printType || ''}
                                          onChange={e => updateRow(row._id, 'printType', e.target.value)}
                                          style={{
                                            width: '100%', padding: '5px 6px',
                                            border: '1.5px solid #e8e8f4', borderRadius: 8,
                                            fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 700,
                                            color: '#1a1a2e', background: '#fff', outline: 'none', cursor: 'pointer',
                                          }}
                                        >
                                          <option value="">Select Type…</option>
                                          {PRINTING_TYPES.map(type => (
                                            <option key={type} value={type}>{type}</option>
                                          ))}
                                        </select>
                                      )}
                                    </td>
                                    <td className="pg-td">
                                      {isExtra || isPrint ? (
                                        <span style={{ color: '#c0c0d0', paddingLeft: 8 }}>—</span>
                                      ) : (
                                        <input
                                          className="qt-inline-input" type="number" min="0"
                                          value={row.printRate || ''}
                                          onChange={e => updateRow(row._id, 'printRate', e.target.value)}
                                          style={{ width: 80, fontWeight: 700, color: '#7c3aed' }}
                                        />
                                      )}
                                    </td>
                                    <td className="pg-td">
                                      {isPrint ? (
                                        <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 900, color: '#7c3aed' }}>
                                          {row.amount > 0 ? Number(row.amount).toLocaleString('en-IN') : '—'}
                                        </span>
                                      ) : isExtra ? (
                                        <span style={{ color: '#c0c0d0', paddingLeft: 8 }}>—</span>
                                      ) : (
                                        <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 900, color: '#7c3aed' }}>
                                          {row.printingCost > 0 ? Number(row.printingCost).toLocaleString('en-IN') : '—'}
                                        </span>
                                      )}
                                    </td>
                                  </>
                                )}
                                <td className="pg-td">
                                  {isExtra ? (
                                    <input className="qt-inline-input" type="number" min="0" value={row.amount || ''} onChange={e => updateRow(row._id, 'amount', e.target.value)} style={{ width: 86, fontWeight: 700 }} />
                                  ) : (
                                    <input className="qt-inline-input" type="number" value={row.amount || ''} readOnly disabled style={{ width: 86, fontWeight: 700, cursor: 'not-allowed', background: 'rgba(0,0,0,0.03)' }} />
                                  )}
                                </td>
                                <td className="pg-td">
                                  <div className="pg-action-wrap">
                                    <button className="pg-btn-view" onClick={() => setRowToDelete(row)} title="Remove">
                                      <Trash2 size={13} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          });
                        })()}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#f8f8fd', borderTop: '2px solid #ececf8', fontWeight: 'bold' }}>
                          <td className="pg-td" style={{ textAlign: 'center' }}></td>
                          <td className="pg-td" style={{ textAlign: 'left', fontWeight: 900, fontFamily: 'Nunito,sans-serif', fontSize: 13, color: '#1a1a2e' }}>Total</td>
                          <td className="pg-td"></td>
                          <td className="pg-td"></td>
                          <td className="pg-td" style={{ textAlign: 'center', fontWeight: 900, fontFamily: 'Nunito,sans-serif', fontSize: 13, color: '#1a1a2e' }}>
                            {tableTotals.nos}
                          </td>
                          <td className="pg-td"></td>
                          <td className="pg-td"></td>
                          <td className="pg-td"></td>
                          <td className="pg-td" style={{ fontWeight: 900, fontFamily: 'Nunito,sans-serif', fontSize: 13, color: '#1a1a2e' }}>
                            ₹ {fmtCurrency(tableTotals.rate)}
                          </td>
                          {withPrinting && (
                            <>
                              <td className="pg-td"></td>
                              <td className="pg-td"></td>
                              <td className="pg-td" style={{ fontWeight: 900, fontFamily: 'Nunito,sans-serif', fontSize: 13, color: '#7c3aed' }}>
                                ₹ {fmtCurrency(tableTotals.printCost)}
                              </td>
                            </>
                          )}
                          <td className="pg-td" style={{ fontWeight: 900, fontFamily: 'Nunito,sans-serif', fontSize: 13, color: '#049edf' }}>
                            ₹ {fmtCurrency(tableTotals.amount)}
                          </td>
                          <td className="pg-td"></td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>

                <div style={{ display: 'flex', gap: 10, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>

                  {/* Printing type dropdown — withPrinting mode only */}
                  {/* {withPrinting && (
                    <div style={{ position: 'relative' }}>
                      <button
                        ref={printTypeBtnRef}
                        onClick={() => setShowPrintTypeDD(p => !p)}
                        className="qt-add-print-row"
                        style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                      >
                        <Plus size={13} /> Add Printing Row <ChevronDown size={12} />
                      </button>

                      <PortalDropdown
                        open={showPrintTypeDD}
                        triggerRef={printTypeBtnRef}
                        panelRef={printTypePanelRef}
                      >
                        <div
                          ref={printTypePanelRef}
                          style={{
                            background: '#fff', border: '1.5px solid #e8e8f4', borderRadius: 12,
                            boxShadow: '0 8px 28px rgba(0,0,0,0.12)', overflow: 'hidden',
                          }}
                        >
                          {PRINTING_TYPES.map(type => (
                            <div
                              key={type}
                              onClick={() => { setRows(p => [...p, newPrintingRow(type, globalStart, globalEnd)]); setShowPrintTypeDD(false); }}
                              style={{
                                padding: '10px 16px', cursor: 'pointer',
                                fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 700,
                                color: '#7c3aed', borderBottom: '1px solid #f4f4fb',
                                display: 'flex', alignItems: 'center', gap: 8,
                              }}
                              onMouseEnter={e => e.currentTarget.style.background = 'rgba(124,58,237,0.05)'}
                              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                            >
                              <Printer size={13} color="#7c3aed" /> {type}
                            </div>
                          ))}
                        </div>
                      </PortalDropdown>
                    </div>
                  )} */}

                  {/* Add Extra Charge — both modes */}
                  <button
                    onClick={() => setRows(p => [...p, newExtraChargeRow()])}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 16px', borderRadius: 9, cursor: 'pointer',
                      border: '1.5px dashed #e8e8f4', background: '#fafafe',
                      fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 700,
                      color: '#5a5a78',
                    }}
                  >
                    <Plus size={13} /> Add Extra Charge
                  </button>

                </div>

                {step2Error && <div className="qt-error-banner"><AlertCircle size={14} /> {step2Error}</div>}

                <div className="qt-step-foot">
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button className="pg-btn-cancel" onClick={handleBackToList} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <LayoutGrid size={13} /> Back to List
                    </button>
                    <button className="pg-btn-cancel" onClick={goBack} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ArrowLeft size={13} /> Back
                    </button>
                  </div>
                  <button className="pg-btn-save" onClick={goNext}>
                    Next: GST &amp; Generate <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            )}

            {/* ─── STEP 3 ─── */}
            {step === 3 && (
              <div className="qt-step-body">
                <div className="qt-step3-grid">

                  <div>
                    <div className="qt-section-head">GST Configuration</div>
                    <div className="qt-gst-row">
                      <div>
                        <label className="qt-label">CGST %</label>
                        <div className="qt-input-wrap">
                          <Settings size={14} color="#c0c0d8" style={{ flexShrink: 0 }} />
                          <input className="qt-input" type="number" min="0" max="28" value={cgstPct} onChange={e => setCgstPct(e.target.value)} />
                        </div>
                      </div>
                      <div>
                        <label className="qt-label">SGST %</label>
                        <div className="qt-input-wrap">
                          <Settings size={14} color="#c0c0d8" style={{ flexShrink: 0 }} />
                          <input className="qt-input" type="number" min="0" max="28" value={sgstPct} onChange={e => setSgstPct(e.target.value)} />
                        </div>
                      </div>
                    </div>

                    <div className="qt-section-head" style={{ marginTop: 24 }}>Company Details</div>
                    <div className="qt-company-row" style={{ marginBottom: 20 }}>
                      <label className="qt-label" style={{ marginBottom: 6, display: 'block' }}>Select Company <span style={{ color: '#ef4444' }}>*</span></label>
                      <CompanyCombo
                        value={selectedCompanyId}
                        onChange={(comp) => { setSelectedCompanyId(comp ? comp.companyID : ''); }}
                        companies={companyDetailsList.filter(c => c.isActive)}
                      />
                    </div>

                    {/* ✅ MODIFIED: Wrap terms section in a container that highlights (with dashed red border and background tint) if user proceeds without selecting terms and cancels confirmation */}
                    <div
                      id="terms-section-container"
                      style={highlightTerms ? {
                        border: '2px dashed #ef4444',
                        borderRadius: '12px',
                        padding: '12px 14px',
                        background: 'rgba(239, 68, 68, 0.03)',
                        marginBottom: 16,
                        animation: 'fadeIn 0.2s'
                      } : {}}
                    >
                      <div className="qt-terms-head">
                        <div className="qt-section-head" style={{ margin: 0 }}>
                          Terms &amp; Conditions
                        </div>
                        <button className="pg-btn-cancel" onClick={() => setShowTermsModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
                          <List size={12} /> Choose
                        </button>
                      </div>
                      {selectedTerms.length === 0
                        ? <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12.5, color: '#9090a8', fontStyle: 'italic' }}>None selected (optional)</div>
                        : selectedTerms.map((termID, i) => {
                          const t = termsList.find(t => t.termID === termID);
                          return (
                            <div key={termID} className="qt-term-chip">
                              <span className="qt-term-chip__num">{i + 1}.</span>
                              <span>{t?.description || '—'}</span>
                            </div>
                          );
                        })}
                    </div>

                    {mergedCount > 0 && (
                      <div style={{ marginTop: 16, padding: '10px 14px', borderRadius: 11, background: 'rgba(124,58,237,0.05)', border: '1px solid rgba(124,58,237,0.18)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6 }}>
                          <Link2 size={13} color="#7c3aed" />
                          <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12.5, fontWeight: 800, color: '#7c3aed' }}>
                            {mergedCount} Merged Hoarding{mergedCount !== 1 ? 's' : ''} in this Quotation
                          </span>
                        </div>
                        {rows.filter(r => r.rowType === 'merged').map(r => (
                          <div key={r._id} style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11.5, color: '#5a5a78', marginBottom: 3 }}>
                            {r.mergeDirection === 'S' ? '⊞' : (r.mergeDirection === 'H' ? '↔' : '↕')} {r.location} · <strong>{r.size}</strong> · {r.sqFt} sq.ft
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <div className="qt-section-head">Summary</div>
                    <div className="qt-summary-box">
                      <div className="qt-summary-customer">
                        <span>Customer</span>
                        <span>{selectedCustomer?.customerName || '—'}</span>
                      </div>
                      {[
                        { label: 'Sub Total', val: subTotal },
                        { label: `CGST ${cgstPct}%`, val: cgstAmt },
                        { label: `SGST ${sgstPct}%`, val: sgstAmt },
                        { label: 'Round Off', val: roundOff },
                      ].map(({ label, val }) => (
                        <div key={label} className="qt-summary-row">
                          <span>{label}</span><span>₹ {fmtCurrency(val)}</span>
                        </div>
                      ))}
                      <div className="qt-summary-total">
                        <span>Grand Total</span>
                        <span>₹ {fmtCurrency(finalTotal)}</span>
                      </div>
                      <div className="qt-summary-words">{numberToWords(Math.round(finalTotal))}</div>
                    </div>
                  </div>
                </div>

                <div className="qt-step-foot">
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button className="pg-btn-cancel" onClick={handleBackToList} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <LayoutGrid size={13} /> Back to List
                    </button>
                    <button className="pg-btn-cancel" onClick={goBack} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <ArrowLeft size={13} /> Back
                    </button>
                  </div>
                  <button className="pg-btn-save" onClick={generatePDF} disabled={rows.length === 0 || saving} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', fontSize: 14 }}>
                    <Printer size={15} /> Generate &amp; Print PDF
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ══════════ PREVIOUS REVISIONS PANEL ══════════ */}
        {isCreating && quotNo && (() => {
          const prevRevisions = quotations
            .filter(q => (q.quotationNumber || '').trim() === (quotNo || '').trim())
            .sort((a, b) => Number(b.quotationRevisionNumber || 0) - Number(a.quotationRevisionNumber || 0));
          if (prevRevisions.length === 0) return null;
          return (
            <div className="pg-container" style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 20px', borderBottom: '1px solid #f0f0f8' }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {/* <div style={{ width: 28, height: 28, borderRadius: 8, background: 'rgba(217,119,6,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <RefreshCw size={14} color="#7c7c7c" />
                    </div> */}
                    <span style={{ fontFamily: 'Nunito,sans-serif', fontSize: 14, fontWeight: 900, color: '#1a1a2e' }}>
                      Previous Revisions — {quotNo}
                    </span>
                  </div>
                  <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 12, color: '#9090a8', fontWeight: 600, marginTop: 4 }}>
                    {prevRevisions.length} revision{prevRevisions.length !== 1 ? 's' : ''} saved · You are creating Rev. {revisionNo}
                  </div>
                </div>
              </div>
              <div className="pg-desktop-table qt-mobile-table-show">
                <table className="pg-table" style={{ minWidth: 800 }}>
                  <thead>
                    <tr>
                      <th className="pg-th" style={{ width: '14%' }}>Version</th>
                      <th className="pg-th" style={{ width: '14%' }}>Date</th>
                      <th className="pg-th" style={{ width: '22%' }}>Customer</th>
                      <th className="pg-th" style={{ width: '18%' }}>Grand Total</th>
                      <th className="pg-th" style={{ width: '16%', textAlign: 'center' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {prevRevisions.map(rev => (
                      <tr key={`prev-${rev.quotationID}-${rev.quotationRevisionNumber}`} className="pg-tr">
                        <td className="pg-td">
                          {Number(rev.quotationRevisionNumber) > 1
                            ? <span className="qt-rev-badge"><RefreshCw size={9} /> Rev. {rev.quotationRevisionNumber}</span>
                            : <span className="qt-orig-badge">Original</span>}
                        </td>
                        <td className="pg-td">
                          <span style={{ color: '#4a5568', fontFamily: 'Nunito,sans-serif', fontSize: 12.5, fontWeight: 600 }}>
                            {fmtDateDisplay(rev.quotationDate)}
                          </span>
                        </td>
                        <td className="pg-td pg-td--overflow">
                          <span className="pg-td__ellipsis">{custName(rev.customerID)}</span>
                        </td>
                        <td className="pg-td">
                          <span style={{ fontFamily: 'Nunito,sans-serif', fontWeight: 800, fontSize: 13, color: '#049edf' }}>
                            ₹ {fmtCurrency(rev.totalAmount)}
                          </span>
                        </td>
                        <td className="pg-td" style={{ textAlign: 'center' }}>
                          <button onClick={() => handleViewPDF(rev)}
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 13px', borderRadius: 8, border: '1.5px solid #049edf', color: '#049edf', background: 'rgba(4,158,223,0.06)', cursor: 'pointer', fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 800 }}>
                            <Printer size={12} /> View PDF
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* ══════════ QUOTATION HISTORY ══════════ */}
        {!isCreating && <div className="pg-container">

          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '16px 20px', borderBottom: '1px solid #f0f0f8', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexShrink: 0 }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: 'rgba(4,158,223,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={15} color="#049edf" />
              </div>
              <div>
                <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 16, fontWeight: 900, color: '#1a1a2e', lineHeight: 1 }}>{sortedGroups.length}</div>
                <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, fontWeight: 600, color: '#9090a8', lineHeight: 1, marginTop: 2 }}>Quotation{sortedGroups.length !== 1 ? 's' : ''}</div>
              </div>
            </div>
            <div style={{ flex: 1, minWidth: 220, display: 'flex', alignItems: 'center', gap: 9, padding: '9px 14px', background: '#f4f4fb', borderRadius: 10, border: '1.5px solid #ececf8' }}>
              <Search size={14} color="#9090a8" style={{ flexShrink: 0 }} />
              <input
                style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontFamily: 'Nunito,sans-serif', fontSize: 13, fontWeight: 600, color: '#1a1a2e' }}
                placeholder="Search by quotation no. or customer…"
                value={histSearch}
                onChange={e => { setHistSearch(e.target.value); setHistPage(1); }}
              />
              {histSearch && <X size={13} style={{ cursor: 'pointer', color: '#9090a8', flexShrink: 0 }} onClick={() => setHistSearch('')} />}
            </div>
            {deleteMode && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '6px 12px', borderRadius: 8,
                background: '#fef2f2', border: '1px solid #fee2e2',
                color: '#991b1b', fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 700,
                flexShrink: 0,
              }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
                Delete Mode Active
              </div>
            )}
            {deleteMode && (
              <button
                onClick={() => {
                  if (selectedQuotIds.size === 0) {
                    showToast('Please select at least one quotation to delete', 'warning');
                    return;
                  }
                  setShowDeleteConfirm(true);
                }}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '8px 14px', borderRadius: 9,
                  border: 'none',
                  background: selectedQuotIds.size > 0 ? '#ef4444' : '#fee2e2',
                  color: selectedQuotIds.size > 0 ? '#fff' : '#f87171',
                  cursor: selectedQuotIds.size > 0 ? 'pointer' : 'not-allowed',
                  fontFamily: 'Nunito,sans-serif', fontSize: 12.5, fontWeight: 700,
                  flexShrink: 0,
                  boxShadow: selectedQuotIds.size > 0 ? '0 2px 6px rgba(239,68,68,0.25)' : 'none',
                  transition: 'all 0.2s',
                }}
                disabled={selectedQuotIds.size === 0}
              >
                <Trash2 size={13} /> Delete Selected ({selectedQuotIds.size})
              </button>
            )}
            <button onClick={refreshQuotations}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 9, border: '1.5px solid #e8e8f4', background: '#fff', color: '#5a5a78', cursor: 'pointer', fontFamily: 'Nunito,sans-serif', fontSize: 12.5, fontWeight: 700, flexShrink: 0 }}>
              <RefreshCw size={13} /> Refresh
            </button>
          </div>

          <div className="pg-desktop-table qt-mobile-table-show">
            <table className="pg-table" ref={histTableRef} style={{ minWidth: 950 }}>
              <thead>
                <tr>
                  <th style={{ width: 44 }} className="pg-th"></th>
                  {deleteMode && (
                    <th style={{ width: 40, textAlign: 'center' }} className="pg-th">
                      {(() => {
                        const deletableQuotations = [];
                        histPaginated.forEach(group => {
                          group.forEach(q => {
                            if (!contractedQuotIds.has(q.quotationID)) {
                              deletableQuotations.push(q);
                            }
                          });
                        });
                        const allDeletableSelected = deletableQuotations.length > 0 && deletableQuotations.every(q => selectedQuotIds.has(q.quotationID));
                        return (
                          <input
                            type="checkbox"
                            checked={allDeletableSelected}
                            disabled={deletableQuotations.length === 0}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setSelectedQuotIds(prev => {
                                const next = new Set(prev);
                                deletableQuotations.forEach(q => {
                                  if (checked) {
                                    next.add(q.quotationID);
                                  } else {
                                    next.delete(q.quotationID);
                                  }
                                });
                                return next;
                              });
                            }}
                            style={{ cursor: deletableQuotations.length === 0 ? 'not-allowed' : 'pointer', verticalAlign: 'middle' }}
                          />
                        );
                      })()}
                    </th>
                  )}
                  {HIST_COLS.map(col => (
                    <th key={col.key} style={{ width: col.w }}
                      className={['pg-th', col.noSort ? '' : 'pg-th--sort'].filter(Boolean).join(' ')}
                      onClick={() => !col.noSort && handleHistSort(col.key)}
                    >
                      <div className="pg-th__inner">
                        {col.label}
                        {!col.noSort
                          ? <SortIcon col={col.key} sortKey={histSortKey} sortDir={histSortDir} />
                          : <Filter size={10} color="#d0d0e4" style={{ marginLeft: 5 }} />}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {histPaginated.length === 0 ? (
                  <tr>
                    <td colSpan={HIST_COLS.length + 1 + (deleteMode ? 1 : 0)} className="pg-td pg-empty" style={{ maxWidth: 'none' }}>
                      <div className="pg-empty__inner">
                        <FileText size={36} color="#d0d0e8" />
                        <span className="pg-empty__label">No quotations found</span>
                      </div>
                    </td>
                  </tr>
                ) : histPaginated.map(group => {
                  const latest = group[0];
                  const groupKey = (latest.quotationNumber || '').trim() || String(latest.quotationID);
                  const isExp = expandedGroups.has(groupKey);
                  const hasRevs = group.length > 1;

                  return (
                    <React.Fragment key={groupKey}>
                      <tr className="pg-tr qt-group-row">
                        <td className="pg-td" style={{ textAlign: 'center' }}>
                          {hasRevs ? (
                            <button className="qt-expand-btn" onClick={() => toggleGroup(groupKey)}
                              title={isExp ? 'Hide revisions' : `Show ${group.length - 1} older revision${group.length - 1 !== 1 ? 's' : ''}`}>
                              {isExp ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                            </button>
                          ) : <span style={{ width: 28, display: 'inline-block' }} />}
                        </td>
                        {deleteMode && (
                          <td className="pg-td" style={{ textAlign: 'center' }}>
                            <input
                              type="checkbox"
                              checked={selectedQuotIds.has(latest.quotationID)}
                              disabled={contractedQuotIds.has(latest.quotationID)}
                              onChange={(e) => {
                                const checked = e.target.checked;
                                setSelectedQuotIds(prev => {
                                  const next = new Set(prev);
                                  if (checked) {
                                    next.add(latest.quotationID);
                                    // Automatically check all revisions in this group that are not contracted
                                    group.forEach(q => {
                                      if (!contractedQuotIds.has(q.quotationID)) {
                                        next.add(q.quotationID);
                                      }
                                    });
                                  } else {
                                    next.delete(latest.quotationID);
                                    // Automatically uncheck all revisions in this group
                                    group.forEach(q => next.delete(q.quotationID));
                                  }
                                  return next;
                                });
                              }}
                              style={{ cursor: contractedQuotIds.has(latest.quotationID) ? 'not-allowed' : 'pointer', width: 15, height: 15 }}
                            />
                          </td>
                        )}
                        <td className="pg-td">
                          <div className="pg-td__primary">{latest.quotationNumber}</div>
                          {hasRevs && (
                            <div style={{ fontFamily: 'Nunito,sans-serif', fontSize: 11, color: '#9090a8', fontWeight: 600, marginTop: 2 }}>
                              {group.length - 1} older revision{group.length - 1 !== 1 ? 's' : ''}
                            </div>
                          )}
                        </td>
                        <td className="pg-td pg-td--overflow">
                          <span className="pg-td__ellipsis" title={custName(latest.customerID)}>{custName(latest.customerID)}</span>
                        </td>
                        <td className="pg-td">
                          <span style={{ color: '#4a5568' }}>{fmtDateDisplay(latest.quotationDate)}</span>
                        </td>
                        <td className="pg-td" style={{ textAlign: 'center' }}>
                          {Number(latest.quotationRevisionNumber) > 1
                            ? <span className="qt-rev-badge"><RefreshCw size={9} />Rev. {latest.quotationRevisionNumber}</span>
                            : <span className="qt-orig-badge">Original</span>}
                        </td>
                        <td className="pg-td">
                          <span style={{ fontFamily: 'Nunito,sans-serif', fontWeight: 800, fontSize: 13, color: '#049edf' }}>₹ {fmtCurrency(latest.totalAmount)}</span>
                        </td>
                        {/* ── Actions: PDF · Contract · Revise ── */}
                        <td className="pg-td">
                          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                            {/* PDF */}
                            <button onClick={() => handleViewPDF(latest)} title="View / Print PDF"
                              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 8, border: '1.5px solid #049edf', color: '#049edf', background: 'rgba(4,158,223,0.06)', cursor: 'pointer', fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' }}>
                              <Printer size={13} /> PDF
                            </button>
                            {/* Proforma Invoice */}
                            <button onClick={() => handleViewProforma(latest)} title="Download Proforma Invoice"
                              style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 11px', borderRadius: 8, border: '1.5px solid #16a34a', color: '#16a34a', background: 'rgba(22,163,74,0.06)', cursor: 'pointer', fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 800, whiteSpace: 'nowrap' }}>
                              <FileText size={13} /> Proforma
                            </button>
                            {/* ← NEW: Create Contract button */}
                            {(() => {
                              const done = contractedQuotIds.has(latest.quotationID);
                              return (
                                <button
                                  onClick={() => {
                                    if (done) return;
                                    setContractQuot(latest);
                                    setShowContractModal(true);
                                  }}
                                  disabled={done}
                                  title={done ? 'Contract already created for this quotation' : 'Create customer contract'}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 5,
                                    padding: '5px 11px', borderRadius: 8, border: 'none',
                                    color: '#fff',
                                    background: done
                                      ? '#16a34a'
                                      : 'linear-gradient(135deg, #049edf, #6c63ff)',
                                    cursor: done ? 'not-allowed' : 'pointer',
                                    fontFamily: 'Nunito,sans-serif', fontSize: 12, fontWeight: 800,
                                    whiteSpace: 'nowrap',
                                    boxShadow: done ? 'none' : '0 2px 6px rgba(124,58,237,0.25)',
                                    opacity: done ? 0.75 : 1,
                                    pointerEvents: done ? 'none' : 'auto',
                                  }}
                                >
                                  {done
                                    ? <><Check size={13} /> Created</>
                                    : <><FileCheck size={13} /> Contract</>}
                                </button>
                              );
                            })()}
                            {/* Edit — edits this exact revision in place */}
                            {(() => {
                              const doneProforma = proformaInvoices.some(inv =>
                                Number(inv.quotationID ?? inv.QuotationID) === Number(latest.quotationID) &&
                                Number(inv.quotationRevisionNumber ?? inv.QuotationRevisionNumber) === Number(latest.quotationRevisionNumber)
                              );
                              const doneContract = contractedQuotIds.has(latest.quotationID);
                              return (
                                <button
                                  onClick={() => handleEditQuotation(latest)}
                                  disabled={doneContract || doneProforma}
                                  title={
                                    doneContract
                                      ? 'Contract has been created. Cannot edit.'
                                      : doneProforma
                                        ? 'Proforma invoice has been created. Cannot edit.'
                                        : 'Edit this quotation (same number & revision)'
                                  }
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    padding: '5px 11px',
                                    borderRadius: 8,
                                    border: '1.5px solid #f59e0b',
                                    color: '#f59e0b',
                                    background: 'rgba(245,158,11,0.06)',
                                    cursor: (doneContract || doneProforma) ? 'not-allowed' : 'pointer',
                                    fontFamily: 'Nunito,sans-serif',
                                    fontSize: 12,
                                    fontWeight: 800,
                                    whiteSpace: 'nowrap',
                                    opacity: (doneContract || doneProforma) ? 0.45 : 1,
                                  }}
                                >
                                  <Edit2 size={13} /> Edit
                                </button>
                              );
                            })()}
                            {/* Revise */}
                            {(() => {
                              const doneContract = contractedQuotIds.has(latest.quotationID);
                              return (
                                <button
                                  onClick={() => handleReopenHistory(latest)}
                                  disabled={doneContract}
                                  title={
                                    doneContract
                                      ? 'Contract has been created. Cannot revise.'
                                      : 'Create Revision'
                                  }
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 5,
                                    padding: '5px 11px',
                                    borderRadius: 8,
                                    border: '1.5px solid #e8e8f4',
                                    color: '#5a5a78',
                                    background: '#fff',
                                    cursor: doneContract ? 'not-allowed' : 'pointer',
                                    fontFamily: 'Nunito,sans-serif',
                                    fontSize: 12,
                                    fontWeight: 800,
                                    whiteSpace: 'nowrap',
                                    opacity: doneContract ? 0.45 : 1,
                                  }}
                                >
                                  <Edit2 size={13} /> Revise
                                </button>
                              );
                            })()}
                          </div>
                        </td>
                      </tr>

                      {isExp && group.slice(1).map((rev) => (
                        <tr key={`${rev.quotationID}-${rev.quotationRevisionNumber}`} className="pg-tr qt-rev-row">
                          <td className="pg-td"></td>
                          {deleteMode && (
                            <td className="pg-td" style={{ textAlign: 'center' }}>
                              <input
                                type="checkbox"
                                checked={selectedQuotIds.has(rev.quotationID)}
                                disabled={contractedQuotIds.has(rev.quotationID)}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setSelectedQuotIds(prev => {
                                    const next = new Set(prev);
                                    if (checked) {
                                      next.add(rev.quotationID);
                                    } else {
                                      next.delete(rev.quotationID);
                                      next.delete(latest.quotationID);
                                    }
                                    return next;
                                  });
                                }}
                                style={{ cursor: contractedQuotIds.has(rev.quotationID) ? 'not-allowed' : 'pointer', width: 14, height: 14 }}
                              />
                            </td>
                          )}
                          <td className="pg-td">
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingLeft: 12 }}>
                              <div className="qt-rev-tree-line" />
                              {Number(rev.quotationRevisionNumber) > 1
                                ? <span className="qt-rev-badge" style={{ fontSize: 11 }}><RefreshCw size={9} />Rev. {rev.quotationRevisionNumber}</span>
                                : <span className="qt-orig-badge">Original</span>}
                            </div>
                          </td>
                          <td className="pg-td pg-td--overflow">
                            <span className="pg-td__ellipsis" style={{ color: '#9090a8' }}>{custName(rev.customerID)}</span>
                          </td>
                          <td className="pg-td">
                            <span style={{ color: '#b0b0c8', fontSize: 12 }}>{fmtDateDisplay(rev.quotationDate)}</span>
                          </td>
                          <td className="pg-td" style={{ textAlign: 'center' }}>
                            {Number(rev.quotationRevisionNumber) > 1
                              ? <span className="qt-rev-badge"><RefreshCw size={9} />Rev. {rev.quotationRevisionNumber}</span>
                              : <span className="qt-orig-badge">Original</span>}
                          </td>
                          <td className="pg-td">
                            <span style={{ fontFamily: 'Nunito,sans-serif', fontWeight: 700, fontSize: 12.5, color: '#9090a8' }}>₹ {fmtCurrency(rev.totalAmount)}</span>
                          </td>
                          <td className="pg-td">
                            <div style={{ display: 'flex', gap: 6 }}>
                              <button onClick={() => handleViewPDF(rev)} title="View PDF"
                                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 8, border: '1.5px solid #049edf', color: '#049edf', background: 'rgba(4,158,223,0.06)', cursor: 'pointer', fontFamily: 'Nunito,sans-serif', fontSize: 11.5, fontWeight: 800, whiteSpace: 'nowrap' }}>
                                <Printer size={12} /> PDF
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {sortedGroups.length > histPageSize && (
            <div className="pg-pagination">
              <div className="pg-pagination__left">
                <button className="pg-pg-btn" disabled={histPage === 1} onClick={() => setHistPage(1)}><ChevronsLeft size={13} /></button>
                <button className="pg-pg-btn" disabled={histPage === 1} onClick={() => setHistPage(p => p - 1)}><ChevronLeft size={13} /></button>
                {histPageNums.map((p, i) => p === '…'
                  ? <span key={`e${i}`} className="pg-pg-ellipsis">…</span>
                  : <button key={p} className={`pg-pg-btn${histPage === p ? ' pg-pg-btn--active' : ''}`} onClick={() => setHistPage(p)}>{p}</button>
                )}
                <button className="pg-pg-btn" disabled={histPage === histTotalPages} onClick={() => setHistPage(p => p + 1)}><ChevronRight size={13} /></button>
                <button className="pg-pg-btn" disabled={histPage === histTotalPages} onClick={() => setHistPage(histTotalPages)}><ChevronsRight size={13} /></button>
              </div>
              <div className="pg-pagination__right">
                <select className="pg-pagesize-select" value={histPageSize} onChange={e => { setHistPageSize(Number(e.target.value)); setHistPage(1); }}>
                  {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                </select>
                <span className="pg-pagination__text">Items per page</span>
                <span className="pg-pagination__text">{histPage} of {histTotalPages} pages ({sortedGroups.length} items)</span>
              </div>
            </div>
          )}

        </div>}
      </div>

      {/* ════════════ MODALS ════════════ */}
      {showHoardModal && (
        <HoardingSelectModal
          allHoardings={hoardings}
          existingIds={existingHoardingIds}
          onAdd={handleAddSelected}
          onClose={() => setShowHoardModal(false)}
          siteColorMap={siteColorMap}
          siteMap={siteMap}
          startDate={globalStart}
          endDate={globalEnd}
        />
      )}
      {showManualModal && (
        <ManualHoardingModal
          allHoardings={hoardings}
          existingIds={existingHoardingIds}
          onAdd={handleAddManual}
          onClose={() => setShowManualModal(false)}
          siteColorMap={siteColorMap}
          siteMap={siteMap}
          startDate={globalStart}
          endDate={globalEnd}
        />
      )}
      {showExternalModal && (
        <ExternalHoardingSelectModal
          allHoardings={hoardings}
          existingIds={existingHoardingIds}
          onAdd={handleAddExternal}
          onClose={() => {
            setShowExternalModal(false);
            sessionStorage.removeItem('reopen_quotation_external_hoarding_modal');
            sessionStorage.removeItem('unsaved_add_external_hoarding_form');
            sessionStorage.removeItem('newly_created_external_site_id');
          }}
          siteColorMap={siteColorMap}
          siteMap={siteMap}
          outsideSiteMap={outsideSiteMap}
          startDate={globalStart}
          endDate={globalEnd}
          showToast={showToast} // ✅ MODIFIED: Pass showToast function
          changeTab={changeTab}
        />
      )}
      {/* Hoarding date conflict modal */}
      {showConflictModal && conflictWarnings.length > 0 && (
        <HoardingConflictModal
          conflicts={conflictWarnings}
          onClose={() => setShowConflictModal(false)}
        />
      )}
      {showTermsModal && (
        <TermsModal
          selected={selectedTerms}
          onSelect={toggleTerm}
          onSelectAll={(allIds) => setSelectedTerms(allIds)}
          termsList={termsList}
          onClose={() => setShowTermsModal(false)}
        />
      )}
      {showTermsConfirmModal && (
        <TermsConfirmModal
          onConfirm={() => {
            setShowTermsConfirmModal(false);
            generatePDF(true);
          }}
          onCancel={() => {
            setShowTermsConfirmModal(false);
            setHighlightTerms(true);
            setTimeout(() => {
              const el = document.getElementById('terms-section-container');
              if (el) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
              }
            }, 100);
          }}
        />
      )}

      {showMergeModal && (
        <MergeModal
          rows={rows}
          onMerge={handleMerge}
          onClose={() => setShowMergeModal(false)}
          siteColorMap={siteColorMap}
        />
      )}
      {viewMergedRow && (
        <MergedHoardingsViewModal
          row={viewMergedRow}
          hoardings={hoardings}
          siteMap={siteMap}
          outsideSiteMap={outsideSiteMap}
          onClose={() => setViewMergedRow(null)}
        />
      )}

      {/* ← NEW: Customer Edit Modal (from Step 1) */}
      {showCustomerEditModal && selectedCustomer && (
        <CustomerEditModal
          customer={selectedCustomer}
          onSave={handleCustomerSaved}
          onClose={() => setShowCustomerEditModal(false)}
        />
      )}

      {showContractModal && contractQuot && (
        <CreateContractFromQuotModal
          quot={contractQuot}
          quotLines={quotLines}
          quotMerges={quotMerges}
          hoardings={hoardings}
          customers={customers}
          siteMap={siteMap}
          outsideSiteMap={outsideSiteMap}
          paymentFreqs={paymentFreqs}
          onClose={() => { setShowContractModal(false); setContractQuot(null); }}
          onCreated={() => {
            setContractedQuotIds(prev => new Set([...prev, contractQuot.quotationID]));
          }}
          showToast={showToast}
        />
      )}

      {showDeleteConfirm && (
        <QuotationDeleteConfirmModal
          count={selectedQuotIds.size}
          onConfirm={handleDeleteConfirm}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}

      {rowToDelete && (
        <RowDeleteConfirmModal
          row={rowToDelete}
          onConfirm={() => {
            deleteRow(rowToDelete._id);
            setRowToDelete(null);
          }}
          onClose={() => setRowToDelete(null)}
        />
      )}

      {proformaConfirmTarget && (
        <ProformaConfirmModal
          target={proformaConfirmTarget}
          onConfirm={async () => {
            const target = proformaConfirmTarget;
            setProformaConfirmTarget(null);
            await proceedWithProforma(target, true);
          }}
          onCancel={async () => {
            const target = proformaConfirmTarget;
            setProformaConfirmTarget(null);
            await proceedWithProforma(target, false);
          }}
          onClose={() => setProformaConfirmTarget(null)}
        />
      )}

      {seriesAlertModal && (
        <SeriesAlertModal
          seriesType={seriesAlertModal.type}
          reason={seriesAlertModal.reason}
          onClose={() => setSeriesAlertModal(null)}
        />
      )}
    </>
  );
}